<template>
  <div class="reports-container">
    <div :class="['mt-6 flex justify-between items-center m-2 mx-4', isRTL != false ? 'flex-row' : '']">
      <div :class="[isRTL != false ? 'text-right' : 'text-left']">
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
          {{ isCampaignsReportMode === true ? t('reports.campaignsReport.title', 'Campaign reports') : t('reports.title') }}
        </h1>
        <h1 class="text text-gray-900 dark:text-gray-200">
          {{ isCampaignsReportMode === true ? '' : t('reports.subtitle') }}
        </h1>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {{ reportsIntroText }}
        </p>
      </div>

      <div class="flex">
        <button
          v-if="isCampaignsReportMode !== true && (selectedReport === 'clickingReport' || selectedReport === 'clickingDashboard')"
          type="button"
          class="btn mr-3"
          @click="openCustomButtonsPage"
        >
          {{ t('customReportButtons.openButton', 'Custom Buttons') }}
        </button>
        <button 
          class="btn disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
          @click="handleGenerateReportClick" 
          :disabled="isGenerateReportDisabled === true || isGeneratingReport === true">
          <svg v-if="isGeneratingReport === true" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 animate-spin" :class="[isRTL === true ? 'ml-2' : 'mr-2']" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3" class="opacity-25" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="3" stroke-linecap="round" class="opacity-90" />
          </svg>
          <Icon v-else icon="tabler:chart-line" :class="[isRTL != false ? 'ml-2' : 'mr-2']" width="20" />
          {{ t('reports.generateReportButton') }}
        </button>
      </div>
    </div>

    <div class="filters-card ml-3 mt-6 card shadow bg-white dark:bg-gray-800 w-full rounded-md p-4 border dark:border-gray-700 flex flex-col" style="border-radius: 10px; width:98%;">
      <div v-if="isCampaignsReportMode === true" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 w-full search-fields-grid">
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.campaignsReport.reportTypeLabel', 'Campaign report') }}</span>
          </label>
          <select v-model="selectedCampaignReport" class="select select-bordered w-full">
            <option v-for="report in campaignReportOptions" :key="report.id" :value="report.id">
              {{ report.name }}
            </option>
          </select>
          <p v-if="selectedCampaignReportOption !== null" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {{ selectedCampaignReportOption.description }}
          </p>
        </div>
        <div class="form-control">
          <MultiSelect
            v-model="selectedCampaignIds"
            :options="campaignOptions"
            :label="t('reports.campaignsReport.campaignsLabel', 'Campaigns')"
            :placeholder="t('reports.campaignsReport.selectCampaignsPlaceholder', 'Select campaigns')"
            id-field="campaignId"
            label-field="name"
            :is-loading="isLoadingCampaigns === true"
          />
        </div>
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.dateRangeLabel') }}</span>
          </label>
          <div class="relative z-50 report-date-range-control">
            <CustomDateRangePicker
              v-model="dateRange"
              :clearable="false"
              :placeholder="t('reports.selectDateRangePlaceholder')"
              :auto-apply="true"
              :multi-calendars="true"
            />
          </div>
        </div>
        <div class="form-control">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.quickSelectLabel') }}</span>
          </label>
          <select v-model="selectedPreset" class="select select-bordered w-full" @change="applyPreset">
            <option value="">{{ t('reports.customRangeOption') }}</option>
            <option v-for="(label, key) in translatedPresets" :key="key" :value="key">
              {{ label }}
            </option>
          </select>
        </div>
      </div>
      <template v-if="isCampaignsReportMode !== true">
      <div class="report-source-switch-row mb-4" :class="isRTL === true ? 'text-right' : 'text-left'">
        <label class="label p-0 mb-2">
          <span class="label-text font-medium">{{ t('reports.sourceModeLabel', 'Report source') }}</span>
        </label>
        <div class="report-source-switch inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-1">
          <button
            type="button"
            :class="['report-source-switch-button', reportSourceMode === 'agents' ? 'report-source-switch-button-active' : '']"
            @click="setReportSourceMode('agents')"
          >
            <span class="report-source-switch-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" width="18" height="18" viewBox="0 0 24 24" class="report-source-switch-icon">
                <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                  <path d="M12 8V4H8" />
                  <rect width="16" height="12" x="4" y="8" rx="2" />
                  <path d="M2 14h2m16 0h2m-7-1v2m-6-2v2" />
                </g>
              </svg>
            </span>
            {{ t('reports.aiAgentsSourceLabel', 'AI agents') }}
          </button>
          <button
            type="button"
            :class="['report-source-switch-button', reportSourceMode === 'entities' ? 'report-source-switch-button-active' : '']"
            @click="setReportSourceMode('entities')"
          >
            <span class="report-source-switch-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" width="18" height="18" viewBox="0 0 24 24" class="report-source-switch-icon">
                <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
                  <path d="M12 11a4 4 0 1 0-4-4a4 4 0 0 0 4 4" />
                  <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
                </g>
              </svg>
            </span>
            {{ t('reports.entitiesSourceLabel', 'Entities') }}
          </button>
        </div>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 w-full search-fields-grid">
        <div v-if="reportSourceMode === 'agents'" class="form-control" id="ai-chat-agent-filter-div">
          <MultiSelect
            v-model="selectedAgents"
            :options="aiAgents"
            :label="t('reports.aiChatAgentLabel')"
            :placeholder="t('reports.selectAgentPlaceholder')"
            id-field="agentid"
            label-field="agentname"
            :is-loading="aiAgents.length === 0"
          />
        </div>

        <div v-if="reportSourceMode === 'entities' && selectedReportSupportsEntitySource === true" class="form-control" id="entity-filter-div">
          <MultiSelect
            v-model="selectedEntityCollectionIds"
            :options="entityCollectionOptions"
            :label="t('reports.entityCollectionsLabel', 'Entity collections')"
            :placeholder="t('reports.selectEntityCollectionsPlaceholder', 'Select entity collections')"
            id-field="collectionId"
            label-field="displayName"
            :is-loading="isLoadingEntityCollections === true"
          />
        </div>

        <div v-if="reportSourceMode === 'entities' && selectedReportSupportsEntitySource === true" class="form-control" id="entity-direct-filter-div">
          <MultiSelect
            v-model="selectedEntityIds"
            :options="entityOptions"
            :label="t('reports.entityLabel', 'Entity')"
            :placeholder="t('reports.selectEntityPlaceholder', 'Select Entity')"
            id-field="entityId"
            label-field="displayName"
            :is-loading="isLoadingEntities === true"
          />
        </div>

        <div class="form-control" id="report-type-filter-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.reportTypeLabel') }}</span>
          </label>
          <div ref="reportDropdownRef" class="relative">
            <button type="button" class="select select-bordered w-full report-type-trigger" @click="toggleReportDropdown">
              <div class="flex items-center min-w-0">
                <div class="report-type-icon shrink-0">
                  <Icon :icon="selectedReportOption !== null ? selectedReportOption.icon : 'tabler:report'" width="18" />
                </div>
                <div class="report-type-text min-w-0 flex-1 truncate">
                  <div class="font-medium text-sm truncate">
                    {{ selectedReportOption !== null ? selectedReportOption.name : t('reports.selectReportPlaceholder') }}
                  </div>
                  <div v-if="selectedReportOption !== null" class="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                    {{ selectedReportOption.description }}
                  </div>
                </div>
              </div>
              <Icon icon="tabler:chevron-down" width="18" class="text-gray-400" />
            </button>
            <transition name="report-dropdown">
              <div v-if="isReportDropdownOpen === true" class="report-type-dropdown">
                <div class="p-2 border-b border-base-300 dark:border-gray-700 report-type-dropdown-search">
                  <input
                    v-model="reportTypeSearchValue"
                    type="text"
                    inputmode="search"
                    autocomplete="off"
                    spellcheck="false"
                    maxlength="120"
                    class="input input-bordered input-sm w-full"
                    :placeholder="t('reports.searchReportTypesPlaceholder', 'Search report names')"
                    @click.stop
                  />
                </div>
                <div class="report-type-dropdown-list">
                  <button
                    v-for="report in filteredBuiltInReportOptions"
                    :key="report.id"
                    type="button"
                    class="report-type-option"
                    @click="selectReportType(report)"
                  >
                    <div class="report-type-icon shrink-0">
                      <Icon :icon="report.icon" width="18" />
                    </div>
                    <div class="report-type-text min-w-0 flex-1">
                      <div class="font-medium text-sm truncate">{{ report.name }}</div>
                      <div class="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{{ report.description }}</div>
                    </div>
                  </button>
                  <div v-if="isLoadingCustomReports === true" class="border-t border-base-300 dark:border-gray-700">
                    <div
                      v-for="skeletonIndex in 3"
                      :key="`custom-report-skeleton-${skeletonIndex}`"
                      class="report-type-option report-type-option-skeleton"
                    >
                      <div class="report-type-icon shrink-0 report-type-icon-skeleton"></div>
                      <div class="report-type-text min-w-0 flex-1">
                        <div class="report-type-skeleton-line report-type-skeleton-line-title"></div>
                        <div class="report-type-skeleton-line report-type-skeleton-line-description"></div>
                      </div>
                    </div>
                  </div>
                  <button
                    v-for="report in filteredCustomReportOptions"
                    :key="report.id"
                    type="button"
                    class="report-type-option"
                    @click="selectReportType(report)"
                  >
                    <div class="report-type-icon shrink-0">
                      <Icon :icon="report.icon" width="18" />
                    </div>
                    <div class="report-type-text min-w-0 flex-1">
                      <div class="font-medium text-sm truncate">{{ report.name }}</div>
                      <div class="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{{ report.description }}</div>
                    </div>
                  </button>
                  <div v-if="filteredBuiltInReportOptions.length === 0 && filteredCustomReportOptions.length === 0 && isLoadingCustomReports === false" class="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {{ t('reports.noMatchingReportTypes', 'No matching reports found.') }}
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div v-if="isConversationIdSearchAvailable === true" class="form-control" id="conversation-id-filter-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.conversationIdSearchLabel', 'Conversation ID') }}</span>
          </label>
          <input
            v-model="conversationIdSearchValue"
            type="text"
            inputmode="search"
            autocomplete="off"
            spellcheck="false"
            :maxlength="conversationIdSearchMaxLength"
            class="input input-bordered w-full"
            :placeholder="t('reports.conversationIdSearchPlaceholder', 'Search conversation ID')"
          />
        </div>

        <div class="form-control" id="channel-type-filter-div" v-if="selectedReport === 'chatSessions'">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.communicationChannelLabel') }}</span>
          </label>
          <div ref="channelDropdownRef" class="relative">
            <button type="button" class="select select-bordered w-full report-type-trigger" @click="toggleChannelDropdown">
              <div class="flex items-center min-w-0">
                <div class="report-type-icon shrink-0">
                  <Icon :icon="selectedChannelOption !== null ? selectedChannelOption.icon : 'tabler:adjustments-horizontal'" width="18" />
                </div>
                <div class="report-type-text min-w-0 flex-1 truncate">
                  <div class="font-medium text-sm truncate">
                    {{ selectedChannelOption !== null ? selectedChannelOption.name : t('reports.communicationChannelPlaceholder') }}
                  </div>
                  <div v-if="selectedChannelOption !== null && selectedChannelOption.description !== ''" class="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                    {{ selectedChannelOption.description }}
                  </div>
                </div>
              </div>
              <Icon icon="tabler:chevron-down" width="18" class="text-gray-400" />
            </button>
            <transition name="report-dropdown">
              <div v-if="isChannelDropdownOpen === true" class="report-type-dropdown">
                <div class="p-2 border-b border-base-300 dark:border-gray-700 report-type-dropdown-search">
                  <input
                    v-model="channelTypeSearchValue"
                    type="text"
                    inputmode="search"
                    autocomplete="off"
                    spellcheck="false"
                    maxlength="120"
                    class="input input-bordered input-sm w-full"
                    :placeholder="t('reports.searchChannelsPlaceholder', 'Search channels')"
                    @click.stop
                  />
                </div>
                <div class="report-type-dropdown-list">
                  <button
                    v-for="channel in filteredTranslatedChannelOptions"
                    :key="channel.id"
                    type="button"
                    class="report-type-option"
                    @click="selectChannelType(channel)"
                  >
                    <div class="report-type-icon shrink-0">
                      <Icon :icon="channel.icon" width="18" />
                    </div>
                    <div class="report-type-text min-w-0 flex-1">
                      <div class="font-medium text-sm truncate">{{ channel.name }}</div>
                      <div v-if="channel.description !== ''" class="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{{ channel.description }}</div>
                    </div>
                  </button>
                  <div v-if="filteredTranslatedChannelOptions.length === 0" class="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {{ t('reports.noMatchingChannels', 'No matching channels found.') }}
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div class="form-control" id="sub-agent-conversation-filter-div" v-if="selectedReport === 'chatSessions'">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.subAgentConversationFilterLabel', 'Sub-agent conversations') }}</span>
          </label>
          <select v-model="selectedSubAgentConversationFilter" class="select select-bordered w-full">
            <option value="exclude">{{ t('reports.subAgentConversationFilterOptions.exclude', 'Hide sub-agent conversations') }}</option>
            <option value="include">{{ t('reports.subAgentConversationFilterOptions.include', 'Include sub-agent conversations') }}</option>
            <option value="only">{{ t('reports.subAgentConversationFilterOptions.only', 'Show only sub-agent conversations') }}</option>
          </select>
        </div>

        <div v-if="selectedReport === 'usageDashboard'" class="form-control" id="usage-scope-filter-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.usageDashboard.scopeLabel') }}</span>
          </label>
          <select v-model="selectedUsageScope" class="select select-bordered w-full">
            <option v-for="scope in usageScopeOptions" :key="scope.id" :value="scope.id">
              {{ scope.name }}
            </option>
          </select>
        </div>

        <div v-if="selectedReportSupportsAnalysisFilter === true" class="form-control" id="analysis-filter-field-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.analysisFilterLabel') }}</span>
          </label>
          <select v-model="selectedAnalysisFilterKey" class="select select-bordered w-full">
            <option value="">{{ t('reports.analysisFilterAllOption') }}</option>
            <option v-for="option in analysisFilterFieldOptions" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </div>

        <div v-if="selectedReportSupportsAnalysisFilter === true && selectedAnalysisFilterKey !== ''" class="form-control" id="analysis-filter-value-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.analysisFilterValueLabel') }}</span>
          </label>
          <select v-model="selectedAnalysisFilterValue" class="select select-bordered w-full" :disabled="isLoadingAnalysisFilterOptions === true">
            <option value="">{{ isLoadingAnalysisFilterOptions === true ? t('reports.loading') : t('reports.analysisFilterValuePlaceholder') }}</option>
            <option v-for="option in selectedAnalysisFilterValueOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div v-if="isCustomReportSelected === false && selectedReportSupportsTags === true" class="form-control" id="tag-collection-filter-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.tagCollectionsLabel') }}</span>
          </label>
          <select v-model="selectedTagCollectionId" class="select select-bordered w-full" :disabled="isLoadingTagCollections === true">
            <option value="">{{ t('reports.selectTagCollectionPlaceholder') }}</option>
            <option v-for="collection in tagCollections" :key="collection.collectionId" :value="String(collection.collectionId)">
              {{ collection.collectionName }}
            </option>
          </select>
        </div>

        <div v-if="isCustomReportSelected === false && selectedReportSupportsTags === true" class="form-control" id="tags-filter-div">
          <MultiSelect 
            v-model="selectedTagIds"
            :options="availableTags"
            :label="t('reports.tagsLabel')"
            :placeholder="t('reports.selectTagsPlaceholder')"
            id-field="tagId"
            label-field="tagName"
            :is-loading="isLoadingTags === true"
            :max-displayed-items="2"
          />
        </div>

        <div v-if="selectedReportAllowsDateRange === true" class="form-control" id="date-range-filter-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.dateRangeLabel') }}</span>
          </label>
          <div class="relative z-50 report-date-range-control">
            <CustomDateRangePicker 
              v-model="dateRange"
              :clearable="false" 
              :placeholder="t('reports.selectDateRangePlaceholder')" 
              :auto-apply="true"
              :multi-calendars="true"
            />
          </div>
        </div>

        <div v-if="selectedReportAllowsDateRange === true" class="form-control" id="quick-select-filter-div">
          <label class="label">
            <span class="label-text font-medium">{{ t('reports.quickSelectLabel') }}</span>
          </label>
          <select v-model="selectedPreset" class="select select-bordered w-full" @change="applyPreset">
            <option value="">{{ t('reports.customRangeOption') }}</option>
            <option v-for="(label, key) in translatedPresets" :key="key" :value="key">
              {{ label }}
            </option>
          </select>
        </div>
      </div>
      </template>
    </div>

    <div class="results-card ml-3 mt-6 card shadow bg-white dark:bg-gray-800 w-full rounded-md p-4 border dark:border-gray-700 flex flex-col" style="border-radius: 10px; width:98%;height: auto; min-height: 50vh;">
      <div v-if="isDashboardReport === true" class="mb-6">
        <div class="flex justify-end mb-2">
          <button class="btn btn-outline btn-info btn-sm" @click="fetchDashboardSummary" :disabled="isLoadingDashboard === true">
            <span v-if="isLoadingDashboard === true" class="loading loading-spinner loading-xs mr-2"></span>
            {{ t('reports.refreshDashboardsButton') }}
          </button>
        </div>
        <div v-if="isGeneratingReport === true || isLoadingDashboard === true" class="flex min-h-[18rem] items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-14 w-14 animate-spin text-indigo-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" class="opacity-25" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="opacity-90" />
          </svg>
        </div>
        <div v-else-if="dashboardSummary !== null">
          <div v-if="selectedReport === 'executiveDashboard' || selectedReport === 'adminDashboard'">
            <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6">
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiTotalSessions') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.totalSessions }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiTotalQueries') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.totalQueries }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiAvgMessagesToResolution') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.avgMessagesToResolution }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiAbandonedRate') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.isChatAbandonedRate }}%</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiFollowUpRate') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.followUpRate }}%</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiAvgNps') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.averageNps }}</div>
              </div>
            </div>
            <div class="grid grid-cols-1 lg:grid-cols-3 mt-3">
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.topTagsTitle') }}</h3>
                  <span class="text-xs text-gray-500">{{ dashboardSummary.topTags.length }}</span>
                </div>
                <div class="mt-3">
                  <div v-for="tag in dashboardSummary.topTags" :key="tag.name" class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ tag.name }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ tag.count }}</span>
                  </div>
                </div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.languageBreakdownTitle') }}</h3>
                </div>
                <div class="mt-3">
                  <div v-for="item in dashboardSummary.languages" :key="item.label" class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ item.label }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ item.count }}</span>
                  </div>
                </div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mb-2">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.typeBreakdownTitle') }}</h3>
                </div>
                <div class="mt-3">
                  <div v-for="item in dashboardSummary.types" :key="item.label" class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ item.label }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ item.count }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="dashboardCustomAnalysisItems.length > 0" class="grid grid-cols-1 lg:grid-cols-2 mt-3">
              <div v-for="item in dashboardCustomAnalysisItems" :key="item.key" class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ item.name }}</h3>
                  <span class="text-xs text-gray-500">{{ item.total }}</span>
                </div>
                <p v-if="item.prompt !== ''" class="text-xs text-gray-500 mt-1">{{ item.prompt }}</p>
                <div class="mt-3">
                  <div v-for="valueItem in item.valueBreakdown" :key="`${item.key}-${valueItem.value}`" class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ valueItem.value }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ valueItem.count }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="selectedReport === 'tagsDashboard'">
            <div class="grid grid-cols-1 lg:grid-cols-2">
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.topTagsTitle') }}</h3>
                  <span class="text-xs text-gray-500">{{ dashboardSummary.topTags.length }}</span>
                </div>
                <div class="mt-3">
                  <div v-for="tag in dashboardSummary.topTags" :key="tag.name" class="mb-3">
                    <div class="flex items-center justify-between">
                      <span class="text-sm text-gray-700 dark:text-gray-200">{{ tag.name }}</span>
                      <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ tag.count }}</span>
                    </div>
                    <div class="h-2 rounded bg-gray-200 dark:bg-gray-700 mt-1">
                      <div class="h-2 rounded bg-indigo-500" :style="{ width: getTagSharePercent(tag.count, dashboardSummary.topTagsTotal) + '%' }"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mb-2">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.tagConcentrationTitle') }}</h3>
                <div class="mt-3">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.topTagShareLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ dashboardSummary.topTagShare }}%</span>
                  </div>
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.top3TagShareLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ dashboardSummary.top3TagShare }}%</span>
                  </div>
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.top5TagShareLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ dashboardSummary.top5TagShare }}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="selectedReport === 'usageDashboard'">
            <div class="usage-dashboard-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.totalConversationsLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(usageDashboardView.totalSessions) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ usageDashboardScopeLabel }}</div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.totalInteractionsLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(usageDashboardView.totalMessages) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.messagesPerConversationLabel') }}: {{ formatMetricNumber(usageDashboardView.messagesPerConversation, 1) }}</div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.avgConversationTimeLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatUsageMinutes(usageDashboardView.avgDurationMinutes) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.activeHoursLabel') }}: {{ formatMetricNumber(usageDashboardView.activeHours) }}</div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.peakHourLoadLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(usageDashboardView.peakHour !== null ? usageDashboardView.peakHour.sessionCount : 0) }}</div>
                <div class="usage-time-text text-xs text-gray-500 mt-1">{{ usageDashboardView.peakHour !== null ? formatHourRange(usageDashboardView.peakHour.hour) : t('reports.notAvailable') }}</div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.sessionsPerActiveHourLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(usageDashboardView.sessionsPerActiveHour, 1) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.messagesPerActiveHourLabel') }}: {{ formatMetricNumber(usageDashboardView.messagesPerActiveHour, 1) }}</div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.resolutionRateLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ usageDashboardView.resolutionRate }}%</div>
                <div class="text-xs text-gray-500 mt-1">{{ formatMetricNumber(usageDashboardView.resolvedSessions) }} {{ t('reports.usageDashboard.resolvedConversationsLabel') }}</div>
                </div>
              </div>
            </div>
            <div class="usage-dashboard-grid grid grid-cols-1 lg:grid-cols-3 mt-3">
              <div class="usage-dashboard-cell lg:col-span-2">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="flex items-start justify-between">
                  <div>
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.usageDashboard.peakHoursTitle') }}</h3>
                    <p class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.peakHoursSubtitle') }}</p>
                  </div>
                  <div class="text-right usage-chart-summary">
                    <div class="text-xs text-gray-500">{{ t('reports.usageDashboard.peakShareLabel') }}</div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboardView.peakShare }}%</div>
                    <div class="usage-time-text text-xs text-gray-500 mt-1">{{ usageDashboardView.peakHour !== null ? formatHourShort(usageDashboardView.peakHour.hour) : t('reports.notAvailable') }}</div>
                  </div>
                </div>
                <div class="usage-legend-row mt-4">
                  <div v-if="selectedUsageScope === 'all' || selectedUsageScope === 'chat'" class="usage-legend-item">
                    <span class="usage-legend-swatch usage-legend-swatch-chat"></span>
                    <span class="text-xs text-gray-600 dark:text-gray-300">{{ t('reports.usageDashboard.chatConversationsLabel') }}</span>
                  </div>
                  <div v-if="selectedUsageScope === 'all' || selectedUsageScope === 'call'" class="usage-legend-item">
                    <span class="usage-legend-swatch usage-legend-swatch-call"></span>
                    <span class="text-xs text-gray-600 dark:text-gray-300">{{ t('reports.usageDashboard.callConversationsLabel') }}</span>
                  </div>
                </div>
                <div class="usage-hour-chart mt-4">
                  <div v-for="item in usageDashboardView.hourlyBreakdown" :key="`usage-hour-${item.hour}`" class="usage-hour-chart-item">
                    <div class="usage-hour-chart-value">{{ item.sessionCount > 0 ? formatMetricNumber(item.sessionCount) : '' }}</div>
                    <div class="usage-hour-chart-column-shell">
                      <div class="usage-hour-chart-column" :style="{ height: getUsageBarHeight(item.sessionCount, usageDashboardView.hourlyMax) }">
                        <div v-if="selectedUsageScope === 'all'" class="usage-hour-chart-stack">
                          <div class="usage-hour-chart-segment usage-hour-chart-segment-call" :style="{ height: getUsageCompositionHeight(item.callSessions, item.sessionCount) }"></div>
                          <div class="usage-hour-chart-segment usage-hour-chart-segment-chat" :style="{ height: getUsageCompositionHeight(item.chatSessions, item.sessionCount) }"></div>
                        </div>
                        <div v-else :class="['usage-hour-chart-segment', selectedUsageScope === 'call' ? 'usage-hour-chart-segment-call' : 'usage-hour-chart-segment-chat']" style="height:100%;"></div>
                      </div>
                    </div>
                    <div class="usage-hour-chart-label">{{ formatHourShort(item.hour) }}</div>
                  </div>
                </div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.usageDashboard.busiestHoursTitle') }}</h3>
                <p class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.busiestHoursSubtitle') }}</p>
                <div v-if="usageDashboardView.busiestHours.length > 0" class="mt-3">
                  <div v-for="(item, index) in usageDashboardView.busiestHours" :key="`busiest-hour-${item.hour}`" class="usage-ranked-row">
                    <div class="usage-ranked-index">{{ index + 1 }}</div>
                    <div class="flex-1 min-w-0">
                      <div class="usage-time-text text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ formatHourRange(item.hour) }}</div>
                      <div class="text-xs text-gray-500 mt-1">{{ formatMetricNumber(item.messageCount) }} {{ t('reports.usageDashboard.interactionsLabel') }}</div>
                    </div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 usage-ranked-total">{{ formatMetricNumber(item.sessionCount) }}</div>
                  </div>
                </div>
                <div v-else class="mt-4 text-sm text-gray-500">
                  {{ t('reports.noDashboardData') }}
                </div>
                </div>
              </div>
            </div>
            <div class="usage-dashboard-grid grid grid-cols-1 lg:grid-cols-2 mt-3">
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.usageDashboard.mixTitle') }}</h3>
                <p class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.mixSubtitle') }}</p>
                <div v-if="usageDashboardView.mixItems.length > 0" class="mt-3">
                  <div v-for="item in usageDashboardView.mixItems" :key="item.id" class="mb-3">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center min-w-0">
                        <Icon :icon="item.icon" class="mr-2 shrink-0 text-gray-500" width="16" />
                        <span class="text-sm text-gray-700 dark:text-gray-200 truncate">{{ item.label }}</span>
                      </div>
                      <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 ml-3">{{ formatMetricNumber(item.value) }}</div>
                    </div>
                    <div class="h-2 rounded bg-gray-200 dark:bg-gray-700 mt-2">
                      <div :class="['h-2 rounded', item.colorClass]" :style="{ width: item.percent + '%' }"></div>
                    </div>
                    <div class="text-xs text-gray-500 mt-1">{{ item.percent }}%</div>
                  </div>
                </div>
                <div v-else class="mt-4 text-sm text-gray-500">
                  {{ t('reports.noDashboardData') }}
                </div>
                </div>
              </div>
              <div class="usage-dashboard-cell">
                <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.usageDashboard.roiTitle') }}</h3>
                <p class="text-xs text-gray-500 mt-1">{{ t('reports.usageDashboard.roiSubtitle') }}</p>
                <div class="mt-3">
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.usageDashboard.messagesPerConversationLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ formatMetricNumber(usageDashboardView.messagesPerConversation, 1) }}</span>
                  </div>
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.usageDashboard.followUpRateLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboardView.followUpRate }}%</span>
                  </div>
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.usageDashboard.abandonedRateLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboardView.abandonedRate }}%</span>
                  </div>
                  <div class="flex items-center justify-between mb-3">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.usageDashboard.messagesPerActiveHourLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ formatMetricNumber(usageDashboardView.messagesPerActiveHour, 1) }}</span>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-700 dark:text-gray-200">{{ t('reports.usageDashboard.peakShareLabel') }}</span>
                    <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ usageDashboardView.peakShare }}%</span>
                  </div>
                </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="selectedReport === 'qualityDashboard'">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.resolutionRateLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.resolutions.rate }}%</div>
                <div class="text-xs text-gray-500 mt-1">{{ dashboardSummary.resolutions.solved }} / {{ dashboardSummary.resolutions.notSolved }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiFollowUpRate') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.followUpRate }}%</div>
                <div class="text-xs text-gray-500 mt-1">{{ dashboardSummary.followUps['true'] }} / {{ dashboardSummary.followUps['false'] }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.kpiAbandonedRate') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.isChatAbandonedRate }}%</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.curseRateLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.curses.rate }}%</div>
                <div class="text-xs text-gray-500 mt-1">{{ dashboardSummary.curses.detected }} / {{ dashboardSummary.curses.clean }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mr-2 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.solvedCountLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.resolutions.solved }}</div>
              </div>
              <div class="card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4 mb-2">
                <div class="text-xs text-gray-500">{{ t('reports.notSolvedCountLabel') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ dashboardSummary.resolutions.notSolved }}</div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mt-4 text-sm text-gray-500">
          {{ t('reports.noDashboardData') }}
        </div>
      </div>
      <div v-if="isClickingDashboardReport === true" class="mb-6">
        <div v-if="isGeneratingReport === true" class="flex min-h-[18rem] items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-14 w-14 animate-spin text-indigo-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" class="opacity-25" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="opacity-90" />
          </svg>
        </div>
        <div v-else-if="clickingDashboardView.hasData === true">
          <div class="usage-dashboard-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.totalClicksLabel', 'Total clicks') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(clickingDashboardView.totalClicks) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.activeHoursLabel', 'Active hours') }}: {{ formatMetricNumber(clickingDashboardView.activeHours) }}</div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.uniqueVisitorsLabel', 'Unique visitors') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(clickingDashboardView.totalUniqueVisitors) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.clicksPerVisitorLabel', 'Clicks per visitor') }}: {{ formatMetricNumber(clickingDashboardView.clicksPerVisitor, 1) }}</div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.returningVisitorsLabel', 'Returning visitors') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(clickingDashboardView.totalReturningVisitors) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ clickingDashboardView.totalReturnRatePercentage }}% {{ t('reports.clickingDashboard.returnRateLabel', 'return rate') }}</div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.repeatClicksLabel', 'Repeat clicks') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(clickingDashboardView.totalRepeatClicks) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ clickingDashboardView.repeatClickRate }}% {{ t('reports.clickingDashboard.ofClicksLabel', 'of clicks') }}</div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.peakHourLabel', 'Peak hour') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ clickingDashboardView.peakHour !== null ? formatHourShort(clickingDashboardView.peakHour.hour) : t('reports.notAvailable') }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ formatMetricNumber(clickingDashboardView.peakHour !== null ? clickingDashboardView.peakHour.clicks : 0) }} {{ t('reports.clickingDashboard.clicksLabel', 'clicks') }}</div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.sessionsLabel', 'Sessions') }}</div>
                <div class="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-2">{{ formatMetricNumber(clickingDashboardView.totalSessions) }}</div>
                <div class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.sessionsPerActiveHourLabel', 'Sessions per active hour') }}: {{ formatMetricNumber(clickingDashboardView.sessionsPerActiveHour, 1) }}</div>
              </div>
            </div>
          </div>
          <div class="usage-dashboard-grid grid grid-cols-1 lg:grid-cols-3 mt-3">
            <div class="usage-dashboard-cell lg:col-span-2">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <div class="flex items-start justify-between">
                  <div>
                    <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.clickingDashboard.hourlyClicksTitle', 'Clicks by hour') }}</h3>
                    <p class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.hourlyClicksSubtitle', 'Shows when visitors clicked the menu buttons during the selected range.') }}</p>
                  </div>
                  <div class="text-right usage-chart-summary">
                    <div class="text-xs text-gray-500">{{ t('reports.clickingDashboard.peakShareLabel', 'Peak share') }}</div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ clickingDashboardView.peakShare }}%</div>
                  </div>
                </div>
                <div class="usage-legend-row mt-4">
                  <div class="usage-legend-item">
                    <span class="usage-legend-swatch clicking-legend-swatch-unique"></span>
                    <span class="text-xs text-gray-600 dark:text-gray-300">{{ t('reports.clickingDashboard.uniqueClicksLabel', 'First visitor clicks') }}</span>
                  </div>
                  <div class="usage-legend-item">
                    <span class="usage-legend-swatch clicking-legend-swatch-repeat"></span>
                    <span class="text-xs text-gray-600 dark:text-gray-300">{{ t('reports.clickingDashboard.repeatClicksLabel', 'Repeat clicks') }}</span>
                  </div>
                </div>
                <div class="usage-hour-chart mt-4">
                  <div v-for="item in clickingDashboardView.hourlyBreakdown" :key="`clicking-hour-${item.hour}`" class="usage-hour-chart-item">
                    <div class="usage-hour-chart-value">{{ item.clicks > 0 ? formatMetricNumber(item.clicks) : '' }}</div>
                    <div class="usage-hour-chart-column-shell">
                      <div class="usage-hour-chart-column" :style="{ height: getUsageBarHeight(item.clicks, clickingDashboardView.hourlyMax) }">
                        <div class="usage-hour-chart-stack">
                          <div class="usage-hour-chart-segment clicking-hour-chart-segment-repeat" :style="{ height: getUsageCompositionHeight(item.repeatClicks, item.clicks) }"></div>
                          <div class="usage-hour-chart-segment clicking-hour-chart-segment-unique" :style="{ height: getUsageCompositionHeight(item.uniqueVisitors, item.clicks) }"></div>
                        </div>
                      </div>
                    </div>
                    <div class="usage-hour-chart-label">{{ formatHourShort(item.hour) }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.clickingDashboard.busiestHoursTitle', 'Busiest hours') }}</h3>
                <p class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.busiestHoursSubtitle', 'Top hours by button clicks.') }}</p>
                <div v-if="clickingDashboardView.busiestHours.length > 0" class="mt-3">
                  <div v-for="(item, index) in clickingDashboardView.busiestHours" :key="`clicking-busiest-hour-${item.hour}`" class="usage-ranked-row">
                    <div class="usage-ranked-index">{{ index + 1 }}</div>
                    <div class="flex-1 min-w-0">
                      <div class="usage-time-text text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ formatHourRange(item.hour) }}</div>
                      <div class="text-xs text-gray-500 mt-1">{{ item.returnRatePercentage }}% {{ t('reports.clickingDashboard.returnRateLabel', 'return rate') }}</div>
                    </div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 usage-ranked-total">{{ formatMetricNumber(item.clicks) }}</div>
                  </div>
                </div>
                <div v-else class="mt-4 text-sm text-gray-500">
                  {{ t('reports.noDashboardData') }}
                </div>
              </div>
            </div>
          </div>
          <div class="usage-dashboard-grid grid grid-cols-1 lg:grid-cols-2 mt-3">
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.clickingDashboard.returnTrendTitle', 'Return rate by hour') }}</h3>
                <p class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.returnTrendSubtitle', 'The hourly share of unique visitors that came back more than once.') }}</p>
                <div class="clicking-return-chart mt-4">
                  <svg class="clicking-return-chart-svg" viewBox="0 0 100 42" preserveAspectRatio="none" aria-hidden="true">
                    <line x1="0" y1="38" x2="100" y2="38" class="clicking-return-chart-axis" />
                    <line x1="0" y1="20" x2="100" y2="20" class="clicking-return-chart-grid" />
                    <polyline :points="clickingDashboardView.returnRateLinePoints" class="clicking-return-chart-line" />
                    <circle v-for="point in clickingDashboardView.returnRateLineDots" :key="`return-dot-${point.hour}`" :cx="point.x" :cy="point.y" r="1.4" class="clicking-return-chart-dot" />
                  </svg>
                </div>
                <div class="clicking-return-chart-labels mt-2">
                  <span>{{ formatHourShort(0) }}</span>
                  <span>{{ formatHourShort(6) }}</span>
                  <span>{{ formatHourShort(12) }}</span>
                  <span>{{ formatHourShort(18) }}</span>
                  <span>{{ formatHourShort(23) }}</span>
                </div>
              </div>
            </div>
            <div class="usage-dashboard-cell">
              <div class="card usage-dashboard-card shadow-sm bg-base-100 dark:bg-gray-900 border dark:border-gray-700 rounded-md p-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.clickingDashboard.visitorBehaviorTitle', 'Visitor behavior') }}</h3>
                <p class="text-xs text-gray-500 mt-1">{{ t('reports.clickingDashboard.visitorBehaviorSubtitle', 'Quick read on new, returning, repeat, and session activity.') }}</p>
                <div class="mt-3">
                  <div v-for="item in clickingDashboardView.behaviorItems" :key="item.id" class="mb-3">
                    <div class="flex items-center justify-between">
                      <span class="text-sm text-gray-700 dark:text-gray-200">{{ item.label }}</span>
                      <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ formatMetricNumber(item.value) }}</span>
                    </div>
                    <div class="h-2 rounded bg-gray-200 dark:bg-gray-700 mt-2">
                      <div :class="['h-2 rounded', item.colorClass]" :style="{ width: item.percent + '%' }"></div>
                    </div>
                    <div class="text-xs text-gray-500 mt-1">{{ item.percent }}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mt-4 text-sm text-gray-500">
          {{ t('reports.noDashboardData') }}
        </div>
      </div>
      <div v-if="isDashboardReport === false && isClickingDashboardReport === false" class="flex justify-between items-center mb-6 w-full" :class="{ 'flex-row-reverse': isRTL === true }">
        <div class="flex items-center table-buttons-row">
          <div class="form-control" id="report-search-input-div">
            <input 
              type="text" 
              v-model="searchValue" 
              inputmode="search"
              autocomplete="off"
              spellcheck="false"
              :maxlength="searchMaxLength"
              :placeholder="t('reports.searchPlaceholder')" 
              class="input input-bordered w-full max-w-xs"
            />
          </div>
          <div class="form-control md:col-start-4">
          <button id="export-excel-btn" class="btn btn-outline btn-info" @click="exportToExcel" :disabled="reportData === null || reportData.length === 0">
            <Icon icon="tabler:file-spreadsheet" class="mr-2" />
            {{ t('reports.exportToExcelButton') }}
          </button>
        </div>
          <div class="form-control">
            <button id="report-fullscreen-btn" class="btn btn-outline btn-neutral" @click="openReportTableFullscreen" :disabled="reportData === null || reportData.length === 0">
              <Icon icon="tabler:maximize" class="mr-2" />
              {{ t('reports.fullScreenButton', isRTL === true ? 'מסך מלא' : 'Full Screen') }}
            </button>
          </div>
          <div v-if="translatedReportHeaders.length > 0" class="form-control">
            <details class="report-field-picker">
              <summary class="btn btn-outline btn-neutral">
                <Icon icon="tabler:columns" class="mr-2" />
                {{ t('reports.fieldsButton') }}
              </summary>
              <div class="report-field-picker-menu bg-base-100 dark:bg-gray-900 border border-base-300 dark:border-gray-700 rounded-md shadow-lg p-3">
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.visibleFieldsLabel') }}</span>
                  <button type="button" class="btn btn-xs btn-ghost" @click="showAllReportFields">{{ t('reports.showAllFieldsButton') }}</button>
                </div>
                <label v-for="header in translatedReportHeaders" :key="header.value" class="report-field-picker-item cursor-pointer">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-xs"
                    :checked="isReportFieldVisible(header.value) === true"
                    @change="toggleReportField(header.value)"
                  />
                  <span class="text-sm">{{ header.text }}</span>
                </label>
              </div>
            </details>
          </div>
        </div>
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.reportResultsTitle') }}</h2>
      </div>

      <div v-if="isDashboardReport === false && isClickingDashboardReport === false && isGeneratingReport === true" class="flex min-h-[18rem] items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-14 w-14 animate-spin text-indigo-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" class="opacity-25" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="opacity-90" />
        </svg>
      </div>
      <Teleport v-else-if="isDashboardReport === false && isClickingDashboardReport === false" to="body" :disabled="isReportTableFullscreenOpen !== true">
      <div :class="['report-table-shell', isReportTableFullscreenOpen === true ? 'report-table-shell-fullscreen' : '']">
        <div v-if="isReportTableFullscreenOpen === true" class="report-table-fullscreen-header">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">{{ t('reports.reportResultsTitle') }}</h2>
          <button type="button" class="btn btn-sm btn-outline btn-neutral" @click="closeReportTableFullscreen">
            <Icon icon="tabler:minimize" class="mr-1" />
            {{ t('common.close', 'Close') }}
          </button>
        </div>
        <div :class="['report-table-scroll', isReportTableFullscreenOpen === true ? 'report-table-scroll-fullscreen' : '', isScorecardReport === true ? 'report-table-scroll-scorecard' : '']">
          <ThemeOnly theme="modern" :invert="true">
          <EasyDataTable
            :headers="visibleReportHeaders"
          :items="filteredReportData"
          buttons-pagination
          :search-value="searchValue"
          :search-field="visibleSearchFields"
          :table-class-name="isScorecardReport === true ? 'enterprise-table enterprise-scorecard-table' : 'enterprise-table'"
          :rows-items="[10, 25, 50, 100]"
          :rows-per-page="25"
          @click-row="handleRowClick"
          :body-row-class-name="isConversationDrilldownReport === true ? 'clickable-row' : ''"
        >
          <template #item-sessionstartdate="{ sessionstartdate }">
            {{ formatDateHardcoded(sessionstartdate) }}
          </template>
          <template #item-createdat="{ createdat }">
            {{ formatDateHardcoded(createdat) }}
          </template>
          <template #item-sessionid="{ sessionid }">
            <span class="compact-id-cell" :title="formatFullIdValue(sessionid)">{{ formatCompactIdValue(sessionid) }}</span>
          </template>
          <template #item-entityid="{ entityid }">
            <span class="compact-id-cell" :title="formatFullIdValue(entityid)">{{ formatCompactIdValue(entityid) }}</span>
          </template>
          <template #item-agentid="{ agentid }">
            <span class="compact-id-cell" :title="formatFullIdValue(agentid)">{{ formatCompactIdValue(agentid) }}</span>
          </template>
          <template #item-messageid="{ messageid }">
            <span class="compact-id-cell" :title="formatFullIdValue(messageid)">{{ formatCompactIdValue(messageid) }}</span>
          </template>
          <template #item-communicationchanneltype="{ communicationchanneltype }">
            <div class="flex items-center">
              <Icon :icon="getChannelIcon(communicationchanneltype)" :class="[isRTL === true ? 'ml-2' : 'mr-2']" width="16" />
              <span>{{ getChannelLabel(communicationchanneltype) }}</span>
            </div>
          </template>
          <template #item-channel="{ channel }">
            <div class="flex items-center">
              <Icon :icon="getChannelIcon(channel)" :class="[isRTL === true ? 'ml-2' : 'mr-2']" width="16" />
              <span>{{ getChannelLabel(channel) }}</span>
            </div>
          </template>
          <template #item-recordingurl="slotProps">
            <div v-if="getReportRecordingAudioUrl(slotProps) !== ''">
              <audio controls :src="getReportRecordingAudioUrl(slotProps)" class="h-8 w-44"></audio>
            </div>
            <div v-else-if="isReportRecordingLoading(slotProps) === true" class="text-xs opacity-70">
              <span class="loading loading-spinner loading-xs"></span>
            </div>
            <span v-else>-</span>
          </template>
          <template #item-date="{ date }">
            {{ formatDateHardcoded(date) }}
          </template>
          <template #item-first_interaction="{ firstInteraction }">
            {{ formatDateHardcoded(firstInteraction) }}
          </template>
           <template #item-last_interaction="{ lastInteraction }">
            {{ formatDateHardcoded(lastInteraction) }}
          </template>
          <template #item-totalconversations="{ totalconversations }">
            <span class="font-medium">{{ totalconversations }}</span>
          </template>
          <template #item-uniquesessions="{ uniquesessions }">
            <span class="font-medium">{{ uniquesessions }}</span>
          </template>
          <template #item-uniquequeries="{ uniquequeries }">
            <span class="font-medium">{{ uniquequeries }}</span>
          </template>
          <template #item-avgresponselength="{ avgresponselength }">
            <span class="font-medium">{{ Math.round(avgresponselength) }} {{ t('reports.charsSuffix') }}</span>
          </template>
          <template #item-userquery="{ userquery }">
            <div class="query-text">{{ userquery }}</div>
          </template>
          <template #item-response="{ response }">
            <div class="ai-response" v-html="sanitizeHtml(response)"></div>
          </template>
          <template #item-conversationopen="{ conversationopen, sessionid, agentid }">
            <div class="flex items-center">
              <span :class="['badge', conversationopen === true ? 'badge-success' : 'badge-error']">
                {{ conversationopen === true ? t('reports.statusOpen') : t('reports.statusClosed') }}
              </span>
              <button
                v-if="conversationopen === true"
                class="btn btn-xs btn-outline btn-error ml-2"
                @click.stop="closeConversationFromReport(sessionid, agentid)"
                :disabled="isReportSessionClosing(sessionid) === true"
              >
                <span v-if="isReportSessionClosing(sessionid) === true" class="loading loading-spinner loading-xs"></span>
                <span v-else>Close</span>
              </button>
            </div>
          </template>
          <template #item-hashumanintheloop="{ hashumanintheloop }">
            <span :class="['badge', hashumanintheloop === true ? 'badge-warning' : 'badge-ghost']">
              {{ hashumanintheloop === true ? 'Human Loop' : '-' }}
            </span>
          </template>
          <template #item-tags="slotProps">
            <span class="report-result-with-explanation">
              <div class="flex flex-wrap">
                <span v-for="tag in slotProps.tags" :key="tag" class="badge badge-outline badge-primary badge-sm m-0.5">
                  {{ tag }}
                </span>
              </div>
              <ToolTip v-if="Array.isArray(slotProps.tags) === true && slotProps.tags.length > 0 && getReportCellExplanation(slotProps, 'tags') !== ''" class="report-result-explanation-tooltip" position="left">
                <h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2>
                <p class="text-xs whitespace-pre-wrap">{{ getReportCellExplanation(slotProps, 'tags') }}</p>
              </ToolTip>
            </span>
          </template>
          <template v-for="header in reportExplanationHeaders" :key="`report-explanation-${header.value}`" #[header.slotName]="slotProps">
            <span :class="['report-result-with-explanation', isScorecardAnswerField(header.value) === true || header.value === 'totalScore' ? 'report-scorecard-cell' : '']">
              <span>{{ formatScorecardCellValue(slotProps[header.value]) }}</span>
              <ToolTip v-if="getReportCellExplanation(slotProps, header.value) !== ''" class="report-result-explanation-tooltip" position="left">
                <h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2>
                <p class="text-xs whitespace-pre-wrap">{{ getReportCellExplanation(slotProps, header.value) }}</p>
              </ToolTip>
            </span>
          </template>
          <template #loading>
            <div class="flex justify-center items-center p-8">
              <ModernLoader />
            </div>
          </template>
        </EasyDataTable>
          </ThemeOnly>
        </div>
      </div>
      </Teleport>
    </div>

    <AppModal :visible="isConversationModalOpen === true" :title="t('reports.conversationModal.title')" boxClass="max-w-7xl w-11/12" @close="closeConversationModal">
      <template #left>
        <div v-if="shouldAllowConversationExports === true" class="flex items-center table-buttons-row overflow-hidden">
          <button id="export-pdf-btn" class="btn btn-sm btn-outline btn-info" @click="exportConversationToPdf" :disabled="conversationHistory.length === 0">
            <Icon icon="tabler:file-type-pdf" class="mr-1" /> {{ t('reports.conversationModal.exportPdfButton') }}
          </button>
          <button id="export-txt-btn" class="btn btn-sm btn-outline btn-neutral" @click="exportConversationToTxt" :disabled="conversationHistory.length === 0">
            <Icon icon="tabler:file-type-txt" class="mr-1" /> {{ t('reports.conversationModal.exportTxtButton') }}
          </button>
        </div>
      </template>
      <template #header>
        <h3 class="font-bold text-lg">
          {{ t('reports.conversationModal.title') }}
          <template v-if="conversationAnalysisData !== null && conversationAnalysisData !== undefined">
            <span v-if="conversationAnalysisData.conversationsubject !== undefined && conversationAnalysisData.conversationsubject !== null && conversationAnalysisData.conversationsubject !== ''">
              {{ conversationAnalysisData.conversationsubject }} ({{ selectedSessionId }})
            </span>
            <span v-else>
               {{ selectedSessionId }}
            </span>
          </template>
          <template v-else>
             {{ selectedSessionId }}
          </template>
        </h3>
      </template>

      <div v-if="isAgentsToAgentsConversation === true && (isLoadingConversation === true || agentToAgentOriginalQuestion !== '')" class="mb-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div class="flex items-start">
          <div class="w-9 h-9 rounded-md bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 flex items-center justify-center shrink-0" :class="isRTL === true ? 'ml-3' : 'mr-3'">
            <Icon icon="tabler:message-question" width="20" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-gray-900 dark:text-white">{{ t('reports.conversationModal.originalUserQuestionLabel', 'Original user question') }}</div>
            <div v-if="isLoadingConversation === true" class="h-4 bg-base-300 rounded mt-3 w-3/4"></div>
            <p v-else class="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words" :dir="getTextDirection(agentToAgentOriginalQuestion)">{{ agentToAgentOriginalQuestion }}</p>
          </div>
        </div>
      </div>
      <div v-if="isAgentsToAgentsConversation === true && (isLoadingConversation === true || agentToAgentForwardedQuestion !== '')" class="mb-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-4">
        <div class="flex items-start">
          <div class="w-9 h-9 rounded-md bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 flex items-center justify-center shrink-0" :class="isRTL === true ? 'ml-3' : 'mr-3'">
            <Icon icon="tabler:arrow-right" width="20" />
          </div>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-gray-900 dark:text-white">{{ t('reports.conversationModal.forwardedQuestionLabel', 'Question sent to sub-agent') }}</div>
            <div v-if="isLoadingConversation === true" class="h-4 bg-base-300 rounded mt-3 w-3/4"></div>
            <p v-else class="mt-2 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words" :dir="getTextDirection(agentToAgentForwardedQuestion)">{{ agentToAgentForwardedQuestion }}</p>
          </div>
        </div>
      </div>

      <div :class="[shouldShowConversationTranscript === true && shouldRenderAnalysisColumn === true ? 'grid grid-cols-3' : 'block']">
        <div
          v-if="shouldRenderAnalysisColumn === true"
          id="conversation-analysis-details-div"
          :class="[shouldShowConversationTranscript === true && shouldRenderAnalysisColumn === true ? 'col-span-1 p-4 border-r dark:border-gray-700 max-h-[70vh] overflow-x-hidden overflow-y-auto bg-base-200 rounded mr-4' : 'p-4 max-h-[70vh] overflow-x-hidden overflow-y-auto bg-base-200 rounded']"
        >
          <h4 class="font-semibold text-lg mb-4 sticky top-0 bg-base-200 py-2 border-b dark:border-gray-600">{{ t('reports.conversationModal.analysisDetailsTitle') }}</h4>
          <div v-if="isLoadingConversation === true" class="text-base skeleton-shell">
            <div class="bg-base-100 border border-base-300 dark:border-gray-700 rounded-md p-3 mb-4">
              <div class="h-3 w-28 bg-base-300 rounded mb-3"></div>
              <div class="h-8 bg-base-300 rounded mb-2"></div>
              <div class="h-8 bg-base-300 rounded mb-2"></div>
              <div class="h-8 bg-base-300 rounded"></div>
            </div>
            <div class="h-4 w-40 bg-base-300 rounded mb-3"></div>
            <div class="h-16 bg-base-300 rounded mb-3"></div>
            <div class="h-10 bg-base-300 rounded mb-3"></div>
            <div class="h-10 bg-base-300 rounded mb-3"></div>
            <div class="h-24 bg-base-300 rounded"></div>
          </div>
          <div v-else-if="shouldShowAnalysisDetails === true" class="text-base">
              <div>
                <p v-if="hasConversationAnalysisSection('conversationSubject') === true" class="flex items-start mb-2"><Icon icon="tabler:message" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'mt-1 text-gray-500']"/><strong>{{ t('reports.conversationModal.subjectLabel') }}</strong><span class="ml-2 report-result-with-explanation"><span>{{ conversationAnalysisData.conversationsubject }}</span><ToolTip v-if="getStandardAnalysisExplanation('conversationSubject') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('conversationSubject') }}</p></ToolTip></span></p>
                  <p v-if="hasConversationAnalysisSection('languageDetected') === true" class="flex items-center mb-2"><Icon icon="tabler:language" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.languageLabel') }}</strong><span class="ml-2 report-result-with-explanation"><span>{{ conversationAnalysisData.languagedetected }}</span><ToolTip v-if="getStandardAnalysisExplanation('languageDetected') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('languageDetected') }}</p></ToolTip></span></p>
                  <div v-if="hasConversationAnalysisSection('summary') === true" class="bg-gray-100 dark:bg-gray-700 rounded-md p-2 mb-2">
                     <p class="flex items-start"><Icon icon="tabler:article" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'mt-1 text-gray-500 shrink-0']"/><strong>{{ t('reports.conversationModal.summaryLabel') }}</strong></p>
                     <p class="ml-7 report-result-with-explanation"><span>{{ conversationAnalysisData.summary }}</span><ToolTip v-if="getStandardAnalysisExplanation('summary') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('summary') }}</p></ToolTip></p>
                  </div>
                
                  <p v-if="hasConversationAnalysisSection('conversationType') === true" class="flex items-center mb-2"><Icon icon="tabler:tag" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.typeLabel') }}</strong><span class="ml-2 report-result-with-explanation"><span class="badge badge-ghost badge-sm">{{ conversationAnalysisData.conversationtype }}</span><ToolTip v-if="getStandardAnalysisExplanation('conversationType') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('conversationType') }}</p></ToolTip></span></p>
                  <p v-if="hasConversationAnalysisSection('crs') === true" class="flex items-center mb-2"><Icon icon="tabler:circle-check" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.crsLabel') }}</strong>
                     <span class="ml-2 report-result-with-explanation">
                       <span :class="['badge badge-sm', 
                                    conversationAnalysisData.crs === 'True' ? 'badge-success' : 
                                    conversationAnalysisData.crs === 'False' ? 'badge-error' : 'badge-neutral']">
                         {{ conversationAnalysisData.crs === 'True' ? t('reports.boolean.true') : conversationAnalysisData.crs === 'False' ? t('reports.boolean.false') : conversationAnalysisData.crs }}
                       </span>
                       <ToolTip v-if="getStandardAnalysisExplanation('crs') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('crs') }}</p></ToolTip>
                     </span>
                  </p>
                  <p v-if="hasConversationAnalysisSection('nps') === true" class="flex items-center mb-2"><Icon icon="tabler:percentage" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.predictedCsatLabel') }}</strong><span class="ml-2 report-result-with-explanation"><span>{{ conversationAnalysisData.nps }}</span><ToolTip v-if="getStandardAnalysisExplanation('nps') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('nps') }}</p></ToolTip></span></p>
                  <p v-if="hasConversationAnalysisSection('isConversationFollowUp') === true" class="flex items-center mb-2"><Icon icon="tabler:git-branch" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.followUpLabel') }}</strong>
                     <span class="ml-2 report-result-with-explanation">
                       <span :class="['badge badge-sm', conversationAnalysisData.isconversationfollowup === true ? 'badge-info' : 'badge-ghost']">
                         {{ conversationAnalysisData.isconversationfollowup === true ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                       </span>
                       <ToolTip v-if="getStandardAnalysisExplanation('isConversationFollowUp') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('isConversationFollowUp') }}</p></ToolTip>
                     </span>
                  </p>
                   <p v-if="hasConversationAnalysisSection('curses') === true" class="flex items-center mb-2"><Icon icon="tabler:mood-angry" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.cursesDetectedLabel') }}</strong>
                     <span class="ml-2 report-result-with-explanation">
                       <span :class="['badge badge-sm', conversationAnalysisData.cursesdetected === true ? 'badge-warning' : 'badge-ghost']">
                        {{ conversationAnalysisData.cursesdetected === true ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                       </span>
                       <ToolTip v-if="getStandardAnalysisExplanation('curses') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('curses') }}</p></ToolTip>
                     </span>
                  </p>
                  <p v-if="hasConversationAnalysisSection('curses') && conversationAnalysisData.cursesdetected === true" class="flex items-start pl-6 mb-2"><strong class="mr-1">{{ t('reports.conversationModal.cursesFoundLabel') }}</strong><span class="text">{{ (conversationAnalysisData.cursesfound || []).join(', ') }}</span></p>
                  <p v-if="hasConversationAnalysisSection('gender') === true" class="flex items-center mb-2"><Icon icon="tabler:gender-bigender" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.detectedGenderLabel') }}</strong><span class="ml-2 report-result-with-explanation"><span>{{ conversationAnalysisData.detectedgender }} ({{ conversationAnalysisData.detectionpercentage }})</span><ToolTip v-if="getStandardAnalysisExplanation('gender') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('gender') }}</p></ToolTip></span></p>
                  <p v-if="hasConversationAnalysisSection('isChatAbandoned') === true" class="flex items-center mb-2"><Icon icon="tabler:logout" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-gray-500']"/><strong>{{ t('reports.conversationModal.chatAbandonedLabel') }}</strong>
                    <span class="ml-2 report-result-with-explanation">
                      <span :class="['badge badge-sm', conversationAnalysisData.ischatabandoned === true ? 'badge-warning' : 'badge-ghost']">
                        {{ conversationAnalysisData.ischatabandoned === true ? t('reports.boolean.yes') : t('reports.boolean.no') }}
                      </span>
                      <ToolTip v-if="getStandardAnalysisExplanation('isChatAbandoned') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('isChatAbandoned') }}</p></ToolTip>
                    </span>
                  </p>
              </div>
              
      
              <div v-if="hasConversationAnalysisSection('overallSentiment') === true" class="mt-4 pt-4 border-t dark:border-gray-600">
                <h5 class="font-semibold text-base mb-2 flex items-center"><Icon icon="tabler:chart-pie" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-primary']"/>{{ t('reports.conversationModal.overallSentimentLabel') }} 
                   <span class="ml-2 report-result-with-explanation">
                     <span :class="['badge', getSentimentBadgeClass(conversationAnalysisData.overallsentiment)]"> 
                        {{ conversationAnalysisData.overallsentiment }}
                     </span>
                     <ToolTip v-if="getStandardAnalysisExplanation('overallSentiment') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('overallSentiment') }}</p></ToolTip>
                   </span>
                </h5>
                <div v-if="getStandardAnalysisExplanation('overallSentimentScores') !== ''" class="report-section-explanation-row">
                  <ToolTip class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('overallSentimentScores') }}</p></ToolTip>
                  <span>{{ t('reports.conversationModal.sentimentScoresExplanationLabel', 'Score explanation') }}</span>
                </div>
                <ul class="pl-6 list-none text-sm">
                  <li class="flex items-center mb-1">
                     <span class="w-2 h-2 inline-block rounded-full bg-success mr-2"></span>
                     <span>{{ t('reports.conversationModal.sentimentPositive') }} {{ getNumericScoreText(conversationAnalysisData.overallsentimentscores?.positive) }}</span>
                  </li>
                  <li class="flex items-center mb-1">
                     <span class="w-2 h-2 inline-block rounded-full bg-neutral mr-2"></span>
                     <span>{{ t('reports.conversationModal.sentimentNeutral') }} {{ getNumericScoreText(conversationAnalysisData.overallsentimentscores?.neutral) }}</span>
                  </li>
                  <li class="flex items-center mb-1">
                     <span class="w-2 h-2 inline-block rounded-full bg-error mr-2"></span>
                     <span>{{ t('reports.conversationModal.sentimentNegative') }} {{ getNumericScoreText(conversationAnalysisData.overallsentimentscores?.negative) }}</span>
                  </li>
               </ul>
              </div>

              <div v-if="hasConversationAnalysisSection('userSentiment') && conversationUserSentiment !== null" class="mt-3 pt-3 border-t dark:border-gray-600">
                 <h5 class="font-semibold mb-1 flex items-center"><Icon icon="tabler:user" :class="[isRTL === true ? 'ml-2' : 'mr-2']"/>{{ t('reports.conversationModal.userSentimentLabel') }} 
                    <span :class="['ml-2 badge badge-sm', getSentimentBadgeClass(conversationUserSentiment.sentiment)]">
                       {{ conversationUserSentiment.sentiment }}
                    </span>
                    <ToolTip v-if="getStandardAnalysisExplanation('participantsSentiments') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('participantsSentiments') }}</p></ToolTip>
                 </h5>
                 <ul class="pl-6 list-none text-sm mb-1">
                    <li class="flex items-center mb-1">
                       <span class="w-2 h-2 inline-block rounded-full bg-success mr-2"></span>
                       <span>{{ t('reports.conversationModal.sentimentPositive') }} {{ getNumericScoreText(getSentimentScores(conversationUserSentiment).positive) }}</span>
                    </li>
                    <li class="flex items-center mb-1">
                       <span class="w-2 h-2 inline-block rounded-full bg-neutral mr-2"></span>
                       <span>{{ t('reports.conversationModal.sentimentNeutral') }} {{ getNumericScoreText(getSentimentScores(conversationUserSentiment).neutral) }}</span>
                    </li>
                    <li class="flex items-center mb-1">
                       <span class="w-2 h-2 inline-block rounded-full bg-error mr-2"></span>
                       <span>{{ t('reports.conversationModal.sentimentNegative') }} {{ getNumericScoreText(getSentimentScores(conversationUserSentiment).negative) }}</span>
                    </li>
                 </ul>
                 <p class="italic text-gray-600 dark:text-gray-400 pl-6">{{ getSentimentDescription(conversationUserSentiment) }}</p>
              </div>
              
              <div v-if="hasConversationAnalysisSection('agentSentiment') && conversationAgentSentiment !== null" class="mt-3 pt-3 border-t dark:border-gray-600">
                 <h5 class="font-semibold mb-1 flex items-center"><Icon icon="tabler:headset" :class="[isRTL === true ? 'ml-2' : 'mr-2']"/>{{ t('reports.conversationModal.agentSentimentLabel') }} 
                     <span :class="['ml-2 badge badge-sm', getSentimentBadgeClass(conversationAgentSentiment.sentiment)]">
                       {{ conversationAgentSentiment.sentiment }}
                     </span>
                     <ToolTip v-if="getStandardAnalysisExplanation('participantsSentiments') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('participantsSentiments') }}</p></ToolTip>
                 </h5>
                 <ul class="pl-6 list-none text-sm mb-1">
                    <li class="flex items-center mb-1">
                       <span class="w-2 h-2 inline-block rounded-full bg-success mr-2"></span>
                       <span>{{ t('reports.conversationModal.sentimentPositive') }} {{ getNumericScoreText(getSentimentScores(conversationAgentSentiment).positive) }}</span>
                    </li>
                    <li class="flex items-center mb-1">
                       <span class="w-2 h-2 inline-block rounded-full bg-neutral mr-2"></span>
                       <span>{{ t('reports.conversationModal.sentimentNeutral') }} {{ getNumericScoreText(getSentimentScores(conversationAgentSentiment).neutral) }}</span>
                    </li>
                    <li class="flex items-center mb-1">
                       <span class="w-2 h-2 inline-block rounded-full bg-error mr-2"></span>
                       <span>{{ t('reports.conversationModal.sentimentNegative') }} {{ getNumericScoreText(getSentimentScores(conversationAgentSentiment).negative) }}</span>
                    </li>
                 </ul>
                 <p class="italic text-gray-600 dark:text-gray-400 pl-6">{{ getSentimentDescription(conversationAgentSentiment) }}</p>
              </div>

        
              <div v-if="hasConversationAnalysisSection('unansweredQuestions') && conversationAnalysisData.unansweredquestions !== null && conversationAnalysisData.unansweredquestions !== undefined && conversationAnalysisData.unansweredquestions.length > 0" class="mt-3 pt-3 border-t dark:border-gray-600">
                <h5 class="font-semibold mb-2 flex items-center"><Icon icon="tabler:help-circle" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-warning']"/>{{ t('reports.conversationModal.unansweredQuestionsLabel') }}<ToolTip v-if="getStandardAnalysisExplanation('unansweredQuestions') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('unansweredQuestions') }}</p></ToolTip></h5>
                <ul class="list-disc list-inside pl-6">
                  <li v-for="(item, idx) in conversationAnalysisData.unansweredquestions" :key="idx" class="mb-2">
                    <strong>{{ t('reports.conversationModal.unansweredQuestionPrefix') }}</strong> {{ item.question }} <br/> <span class="italic text-gray-500"><strong>{{ t('reports.conversationModal.unansweredAnswerPrefix') }}</strong> {{ item.answer }}</span>
                  </li>
                </ul>
              </div>

              <div v-if="hasConversationAnalysisSection('identifiedTags') && conversationTags.length > 0" class="mt-4 pt-4 border-t dark:border-gray-600">
                <h5 class="font-semibold text-base mb-2 flex items-center"><Icon icon="tabler:tags" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-info']"/>{{ t('reports.conversationModal.identifiedTagsLabel') }}<ToolTip v-if="getStandardAnalysisExplanation('identifiedTags') !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ getStandardAnalysisExplanation('identifiedTags') }}</p></ToolTip></h5>
                <div class="flex flex-wrap pl-6">
                    <span v-for="tag in conversationTags" :key="tag" class="badge badge-info badge-outline mr-2 mb-2">{{ tag }}</span>
                </div>
             </div>
              <div v-if="customAnalysisDisplayItems.length > 0" class="mt-4 pt-4 border-t dark:border-gray-600">
                <h5 class="font-semibold text-base mb-2 flex items-center"><Icon icon="tabler:sparkles" :class="[isRTL === true ? 'ml-2' : 'mr-2', 'text-info']"/>{{ t('reports.conversationModal.customAnalysisLabel', 'Custom analysis') }}</h5>
                <div v-for="item in customAnalysisDisplayItems" :key="item.visibilityKey" class="bg-gray-100 dark:bg-gray-700 rounded-md p-3 mb-2">
                  <div class="font-semibold text-sm">{{ item.name }}</div>
                  <div v-if="item.prompt !== ''" class="text-xs text-gray-500 dark:text-gray-300 mt-1">{{ item.prompt }}</div>
                  <div class="report-result-with-explanation report-custom-analysis-result">
                    <pre class="text-sm whitespace-pre-wrap mt-2">{{ item.value }}</pre>
                    <ToolTip v-if="item.explanation !== ''" class="report-result-explanation-tooltip" position="left"><h2 class="card-title text-sm">{{ getAnalysisExplanationTitle() }}</h2><p class="text-xs whitespace-pre-wrap">{{ item.explanation }}</p></ToolTip>
                  </div>
                </div>
              </div>
          </div>
          <div v-else class="text-sm text-gray-500 dark:text-gray-400 p-2">
            {{ t('reports.notAvailable') }}
          </div>
        </div>

        <div v-if="shouldShowConversationTranscript === true" :class="[shouldRenderAnalysisColumn === true ? 'col-span-2' : 'col-span-3']">
          
            <div ref="conversationContainerRef" class="py-4 max-h-[70vh] overflow-y-auto bg-base-200 rounded p-4">
                <div v-if="selectedConversationRecordingLoading === true || selectedConversationRecordingUrl !== '' || selectedConversationRecordingError !== ''" class="bg-base-100 border border-base-300 rounded-md p-3 mb-4">
                  <div class="font-semibold text-sm mb-2">{{ t('reports.headers.recording') }}</div>
                  <div v-if="selectedConversationRecordingLoading === true" class="text-sm opacity-70">
                    <span class="loading loading-spinner loading-xs mr-2"></span>
                    Loading recording...
                  </div>
                  <audio v-else-if="selectedConversationRecordingUrl !== ''" controls :src="selectedConversationRecordingUrl" class="w-full h-9"></audio>
                  <div v-else class="text-sm opacity-70">{{ selectedConversationRecordingError }}</div>
                </div>
                <div v-if="isLoadingConversation === true" class="p-6 skeleton-shell">
                    <div class="chat chat-start mb-4">
                      <div class="chat-header text-xs opacity-50 mb-1">
                        <div class="h-3 w-20 bg-base-300 rounded"></div>
                      </div>
                      <div class="chat-bubble">
                        <div class="h-4 w-48 bg-base-300 rounded mb-2"></div>
                        <div class="h-4 w-32 bg-base-300 rounded"></div>
                      </div>
                    </div>
                    <div class="chat chat-end mb-4">
                      <div class="chat-header text-xs opacity-50 mb-1">
                        <div class="h-3 w-16 bg-base-300 rounded"></div>
                      </div>
                      <div class="chat-bubble chat-bubble-primary">
                        <div class="h-4 w-40 bg-base-300 rounded mb-2"></div>
                        <div class="h-4 w-28 bg-base-300 rounded"></div>
                      </div>
                    </div>
                    <div class="chat chat-start mb-4">
                      <div class="chat-header text-xs opacity-50 mb-1">
                        <div class="h-3 w-20 bg-base-300 rounded"></div>
                      </div>
                      <div class="chat-bubble">
                        <div class="h-4 w-56 bg-base-300 rounded mb-2"></div>
                        <div class="h-4 w-36 bg-base-300 rounded"></div>
                      </div>
                    </div>
                    <div class="chat chat-end">
                      <div class="chat-header text-xs opacity-50 mb-1">
                        <div class="h-3 w-16 bg-base-300 rounded"></div>
                      </div>
                      <div class="chat-bubble chat-bubble-primary">
                        <div class="h-4 w-44 bg-base-300 rounded"></div>
                      </div>
                    </div>
                </div>
                <div v-else-if="conversationHistory.length > 0">
            
                    <template v-for="(turn, index) in conversationHistory" :key="index">
               
                        <div class="chat chat-start mb-4" v-if="isAgentsToAgentsConversation !== true && hasConversationText(turn.userquery) === true" :style="{ direction: getTextDirection(extractConversationText(turn.userquery)) }">
                            <div class="chat-header text-xs opacity-50 mb-1">
                               {{ t('reports.conversationModal.userChatLabel') }}
                               <time class="text-xs opacity-50 ml-1">{{ formatDateHardcoded(turn.createdat) }}</time>
                            </div>
                            <div class="chat-bubble chat-bubble-primary"
                                 v-html="escapeHtml(extractConversationText(turn.userquery))">
                            </div>
                        </div>
                   
                         <div class="chat chat-end mb-4" v-if="hasConversationText(turn.response) === true" :style="{ direction: getTextDirection(extractConversationText(turn.response)) }">
                             <div class="chat-header text-xs opacity-50 mb-1">
                                {{ t('reports.conversationModal.agentChatLabel') }}
                                <time class="text-xs opacity-50 ml-1">{{ formatDateHardcoded(turn.createdat) }}</time>
                             </div>
                             <div class="chat-bubble"
                                  v-html="sanitizeHtml(extractConversationText(turn.response))">
                             </div>
                             <details v-if="hasLatency(turn) === true" class="latency-details">
                               <summary class="latency-summary">{{ formatLatencyMs(getLatencyTotalMs(turn)) }}</summary>
                               <div class="latency-popover">
                                 <div v-for="stage in getLatencyStages(turn)" :key="stage.key" class="latency-stage">
                                   <span class="latency-stage-label">{{ stage.label }}</span>
                                   <span class="latency-stage-bar-wrap">
                                     <span class="latency-stage-bar" :style="{ width: stage.width, backgroundColor: stage.color }"></span>
                                   </span>
                                   <span class="latency-stage-value">{{ formatLatencyMs(stage.value) }}</span>
                                 </div>
                                 <div class="latency-total">
                                   <span>Total</span>
                                   <strong>{{ formatLatencyMs(getLatencyTotalMs(turn)) }}</strong>
                                 </div>
                                 <div v-if="getAudioProgressText(turn) !== ''" class="latency-audio-progress">{{ getAudioProgressText(turn) }}</div>
                               </div>
                             </details>
                             <div class="flex justify-end mt-2">
                               <button
                                 v-if="turn.messageid !== undefined && turn.messageid !== null && String(turn.messageid).trim() !== ''"
                                 class="btn btn-xs latency-action-button"
                                 @click="openSourcesModalForMessage(turn)"
                               >
                                 <Icon icon="tabler:book" class="mr-1" />
                                 {{ t('reports.sourcesModal.button') }}
                               </button>
                               <button
                                 v-if="hasToolExecutions(turn) === true"
                                 class="btn btn-xs latency-action-button"
                                 @click="openToolExecutionsModal(turn)"
                               >
                                 <Icon icon="tabler:tool" class="mr-1" />
                                 Tool used ({{ getToolExecutionsCount(turn) }})
                               </button>
                             </div>
                         </div>
                    </template>
                </div>
                <div v-else class="text-center p-6">
                    <p>{{ t('reports.conversationModal.noHistoryPlaceholder') }}</p>
                </div>
            </div>
        </div>
      </div>

      <template #actions>
        <button
          v-if="selectedConversationOpen === true"
          class="btn btn-sm btn-outline btn-error"
          @click="closeSelectedConversationFromModal"
          :disabled="isClosingSelectedConversation === true"
        >
          <span v-if="isClosingSelectedConversation === true" class="loading loading-spinner loading-xs mr-1"></span>
          <span v-else>Close Conversation</span>
        </button>
      </template>
    </AppModal>
    <SourcesModal
      :visible="isSourcesModalOpen === true"
      :session-id="sourcesSessionId"
      :message-id="sourcesMessageId"
      @close="closeSourcesModal"
    />
    <AppModal
      :visible="isToolExecutionsModalOpen === true"
      title="Tool Execution Details"
      boxClass="max-w-4xl w-11/12"
      @close="closeToolExecutionsModal"
    >
      <div class="max-h-[70vh] overflow-y-auto p-1">
        <div v-if="selectedToolExecutions.length === 0" class="text-sm text-gray-500">
          No tool execution details available for this message.
        </div>
        <div v-for="(execution, idx) in selectedToolExecutions" :key="execution.id !== undefined && execution.id !== null ? execution.id : idx" class="border border-base-300 rounded-md p-3 mb-3 bg-base-100">
          <div class="flex items-center justify-between mb-2">
            <div class="font-semibold text-sm">{{ execution.toolName }}</div>
            <span :class="['badge badge-sm', getToolExecutionStatusBadgeClass(execution.status)]">
              {{ getToolExecutionStatusText(execution.status) }} ({{ execution.code !== undefined && execution.code !== null ? execution.code : 'N/A' }})
            </span>
          </div>
          <div class="text-xs text-gray-500 mb-2" v-if="execution.createdAt !== null && execution.createdAt !== undefined && execution.createdAt !== ''">
            {{ formatDateHardcoded(execution.createdAt) }}
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2">
            <div class="mb-3 lg:mb-0 lg:mr-2">
              <div class="text-xs font-semibold mb-1">Body</div>
              <pre class="text-xs bg-base-200 rounded p-2 whitespace-pre-wrap break-words">{{ formatToolExecutionPayload(execution.requestBody) }}</pre>
            </div>
            <div class="lg:ml-2">
              <div class="text-xs font-semibold mb-1">Response</div>
              <pre class="text-xs bg-base-200 rounded p-2 whitespace-pre-wrap break-words">{{ formatToolExecutionPayload(execution.responseBody) }}</pre>
            </div>
          </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import axios from '@/api/axiosInstance';
import { useToast } from 'vue-toastification';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import router from '@/router';
import { useRoute } from 'vue-router';
import { globalState } from '@/globalState';
import { Icon } from '@iconify/vue/dist/offline';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas-pro';
import CustomDateRangePicker from '@/components/CustomDateRangePicker.vue';
import ModernLoader from '@/components/ModernLoader.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import AppModal from '@/components/AppModal.vue';
import SourcesModal from '@/components/SourcesModal.vue';
import ToolTip from '@/components/Tooltip.vue';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import { formatDateTimeForViewer } from '@/shared/utils/dateTime';
import { buildCustomReportDescription } from '@/shared/utils/customReports';

const { t, currentLanguage } = useI18n();
const route = useRoute();

const isRTL = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

const isCampaignsReportMode = computed(() => {
  const reportType = typeof route.query.type === 'string' ? route.query.type.trim().toLowerCase() : '';
  return reportType === 'campaigns';
});

const reportsIntroText = computed(() => {
  if (isCampaignsReportMode.value === true) {
    return t('reports.campaignsReport.subtitle', 'Select campaigns and a date range to review call attempts and responses.');
  }
  if (isRTL.value === true) {
    return 'בחרו סוכן, דוח וטווח תאריכים ואז לחצו על יצירת דוח.';
  }
  return 'Select an agent, report type, and date range, then click Generate Report.';
});

const formatDateHardcoded = (dateValue) => {
  return formatDateTimeForViewer(dateValue);
};

var callAddFont = function () {
this.addFileToVFS('Heebo-VariableFont_wght-normal.ttf', font);
this.addFont('Heebo-VariableFont_wght-normal.ttf', 'Heebo-VariableFont_wght', 'normal');
};
jsPDF.API.events.push(['addFonts', callAddFont]);

const toast = useToast();
const selectedAgents = ref([]);
const selectedCampaignIds = ref([]);
const selectedCampaignReport = ref('performanceSummary');
const campaignOptions = ref([]);
const isLoadingCampaigns = ref(false);
const selectedEntityIds = ref([]);
const selectedEntityCollectionIds = ref([]);
const reportSourceMode = ref('agents');
const selectedReport = ref('');
const dateRange = ref([]);
const reportTypeSearchValue = ref('');
const channelTypeSearchValue = ref('');
const searchValue = ref('');
const conversationIdSearchValue = ref('');
const searchMaxLength = 160;
const conversationIdSearchMaxLength = 32;
const fileNameMaxLength = 120;
const searchFields = ref([]);
const reportData = ref([]);
const reportHeaders = ref([]);
const reportFieldVisibility = ref({});
const aiAgents = ref([]);
const analysisEntities = ref([]);
const entityCollections = ref([]);
const customReportOptions = ref([]);
const isLoadingCustomReports = ref(false);
const isLoadingEntities = ref(false);
const isLoadingEntityCollections = ref(false);
const isGeneratingReport = ref(false);
let reportGenerateSocket = null;
const isReportDropdownOpen = ref(false);
const reportDropdownRef = ref(null);
const isChannelDropdownOpen = ref(false);
const channelDropdownRef = ref(null);
const isReportTableFullscreenOpen = ref(false);
const reportTableFullscreenBodyClass = 'report-table-fullscreen-active';


const isConversationModalOpen = ref(false);
const selectedSessionId = ref(null);
const selectedConversationAgentId = ref(null);
const selectedConversationOpen = ref(false);
const selectedConversationChannelType = ref('');
const conversationHistory = ref([]);
const isLoadingConversation = ref(false);
const selectedConversationRecordingUrl = ref('');
const selectedConversationRecordingLoading = ref(false);
const selectedConversationRecordingError = ref('');
const reportRecordingUrls = ref({});
const reportRecordingLoadingSessionIds = ref([]);
const conversationContainerRef = ref(null);
const conversationAnalysisData = ref(null);
const conversationTags = ref([]);
const availableTags = ref([]);
const tagCollections = ref([]);
const selectedTagIds = ref([]);
const selectedTagCollectionId = ref('');
const selectedChannelType = ref('');
const selectedSubAgentConversationFilter = ref('exclude');
const selectedUsageScope = ref('all');
const selectedAnalysisFilterKey = ref('');
const selectedAnalysisFilterValue = ref('');
const analysisFilterOptions = ref([]);
const isLoadingAnalysisFilterOptions = ref(false);
const isLoadingTags = ref(false);
const isLoadingTagCollections = ref(false);
const dashboardSummary = ref(null);
const isLoadingDashboard = ref(false);
const isSourcesModalOpen = ref(false);
const sourcesSessionId = ref('');
const sourcesMessageId = ref('');
const isToolExecutionsModalOpen = ref(false);
const selectedToolExecutions = ref([]);
const closingReportSessionIds = ref([]);
const isStandardSubscription = computed(() => {
  const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : '';
  return subscription === 'standard';
});
const isEnterpriseSubscription = computed(() => {
  const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : '';
  return subscription === 'enterprise';
});
const shouldShowRecordingColumn = computed(() => isEnterpriseSubscription.value === true);
const shouldShowConversationTranscript = computed(() => isStandardSubscription.value === false);
const shouldAllowConversationExports = computed(() => shouldShowConversationTranscript.value === true);
const isAgentsToAgentsConversation = computed(() => selectedConversationChannelType.value === 'agentstoagents');
const agentToAgentOriginalQuestion = computed(() => {
  for (let historyIndex = 0; historyIndex < conversationHistory.value.length; historyIndex = historyIndex + 1) {
    const question = extractConversationText(conversationHistory.value[historyIndex]?.userquery);
    if (question !== '') {
      return question;
    }
  }
  return '';
});
const agentToAgentForwardedQuestion = computed(() => {
  for (let historyIndex = 0; historyIndex < conversationHistory.value.length; historyIndex = historyIndex + 1) {
    const question = extractConversationText(conversationHistory.value[historyIndex]?.forwardedQuestion);
    if (question !== '') {
      return question;
    }
  }
  return '';
});
const shouldShowAnalysisDetails = computed(() => {
  const data = conversationAnalysisData.value;
  if (data === null || data === undefined) {
    return false;
  }
  const candidates = [
    data.conversationsubject,
    data.languagedetected,
    data.summary,
    data.conversationtype,
    data.crs,
    data.nps,
    data.detectedgender,
    data.overallsentiment,
    data.overallsentimentscores,
    data.participantsentiments,
    data.unansweredquestions,
    data.identifiedtags,
    data.analysisengine
  ];
  for (let i = 0; i < candidates.length; i = i + 1) {
    if (isDefinedAndNotEmpty(candidates[i]) !== false) {
      return true;
    }
  }
  return false;
});
const shouldRenderAnalysisColumn = computed(() => isLoadingConversation.value === true || shouldShowAnalysisDetails.value === true);

function isPlainReportObject(value) {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value) === true) {
    return false;
  }
  return true;
}

function getConversationAnalysisEngineData() {
  const data = conversationAnalysisData.value;
  if (data === null || data === undefined) {
    return {};
  }
  const engine = data.analysisengine;
  if (isPlainReportObject(engine) === true) {
    return engine;
  }
  if (typeof engine === 'string' && engine.trim() !== '') {
    try {
      const parsed = JSON.parse(engine);
      if (isPlainReportObject(parsed) === true) {
        return parsed;
      }
    } catch (error) {
      return {};
    }
  }
  return {};
}

function getStandardAnalysisExplanation(key) {
  const engine = getConversationAnalysisEngineData();
  const source = isPlainReportObject(engine.standardAnalysisExplanations) === true ? engine.standardAnalysisExplanations : {};
  if (typeof key !== 'string') {
    return '';
  }
  const explanation = source[key];
  if (typeof explanation === 'string' && explanation.trim() !== '') {
    return explanation.trim();
  }
  return '';
}

function getAnalysisExplanationTitle() {
  return t('reports.conversationModal.analysisExplanationTitle', 'Why this result');
}

function hasAnalysisValue(value) {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (Array.isArray(value) === true) {
    return value.length > 0;
  }
  if (isPlainReportObject(value) === true) {
    return Object.keys(value).length > 0;
  }
  return true;
}

function getUserSentimentData() {
  const data = conversationAnalysisData.value;
  if (data === null || data === undefined || isPlainReportObject(data.participantsentiments) !== true) {
    return null;
  }
  if (isPlainReportObject(data.participantsentiments.userSentiment) === true) {
    return data.participantsentiments.userSentiment;
  }
  if (isPlainReportObject(data.participantsentiments.usersentiment) === true) {
    return data.participantsentiments.usersentiment;
  }
  return null;
}

function getAgentSentimentData() {
  const data = conversationAnalysisData.value;
  if (data === null || data === undefined || isPlainReportObject(data.participantsentiments) !== true) {
    return null;
  }
  if (isPlainReportObject(data.participantsentiments.agentSentiment) === true) {
    return data.participantsentiments.agentSentiment;
  }
  if (isPlainReportObject(data.participantsentiments.agentsentiment) === true) {
    return data.participantsentiments.agentsentiment;
  }
  return null;
}

const conversationUserSentiment = computed(() => getUserSentimentData());
const conversationAgentSentiment = computed(() => getAgentSentimentData());

function getSentimentScores(sentimentData) {
  if (isPlainReportObject(sentimentData) !== true) {
    return {};
  }
  if (isPlainReportObject(sentimentData.sentimentScores) === true) {
    return sentimentData.sentimentScores;
  }
  if (isPlainReportObject(sentimentData.sentimentscores) === true) {
    return sentimentData.sentimentscores;
  }
  return {};
}

function getSentimentDescription(sentimentData) {
  if (isPlainReportObject(sentimentData) !== true) {
    return '';
  }
  if (typeof sentimentData.sentimentDescription === 'string' && sentimentData.sentimentDescription.trim() !== '') {
    return sentimentData.sentimentDescription;
  }
  if (typeof sentimentData.sentimentdescription === 'string' && sentimentData.sentimentdescription.trim() !== '') {
    return sentimentData.sentimentdescription;
  }
  return '';
}

function getNumericScoreText(value) {
  if (typeof value === 'number' && isNaN(value) === false) {
    return value.toFixed(2);
  }
  const parsed = parseFloat(value);
  if (isNaN(parsed) === false) {
    return parsed.toFixed(2);
  }
  return t('reports.notAvailable');
}

function getAnalysisResultValue(result) {
  if (isPlainReportObject(result) === true) {
    if (result.value !== undefined && result.value !== null) {
      return result.value;
    }
    if (result.result !== undefined && result.result !== null) {
      return result.result;
    }
    if (result.answer !== undefined && result.answer !== null) {
      return result.answer;
    }
  }
  return result;
}

function getAnalysisResultExplanation(result) {
  if (isPlainReportObject(result) !== true) {
    return '';
  }
  const candidates = [result.explanation, result.reason, result.reasoning, result.description];
  for (let i = 0; i < candidates.length; i = i + 1) {
    if (typeof candidates[i] === 'string' && candidates[i].trim() !== '') {
      return candidates[i].trim();
    }
  }
  return '';
}

function formatAnalysisValue(value) {
  if (value === null || value === undefined) {
    return t('reports.notAvailable');
  }
  if (typeof value === 'boolean') {
    return value === true ? t('reports.boolean.yes') : t('reports.boolean.no');
  }
  if (typeof value === 'number') {
    return isNaN(value) === false ? String(value) : t('reports.notAvailable');
  }
  if (typeof value === 'string') {
    return value.trim() !== '' ? value : t('reports.notAvailable');
  }
  if (Array.isArray(value) === true) {
    const parts = [];
    for (let i = 0; i < value.length; i = i + 1) {
      const formatted = formatAnalysisValue(value[i]);
      if (formatted !== t('reports.notAvailable')) {
        parts.push(formatted);
      }
    }
    return parts.length > 0 ? parts.join(', ') : t('reports.notAvailable');
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

const customAnalysisDisplayItems = computed(() => {
  const engine = getConversationAnalysisEngineData();
  const customItems = Array.isArray(engine.customItems) === true ? engine.customItems : [];
  const customAnalysis = isPlainReportObject(engine.customAnalysis) === true ? engine.customAnalysis : {};
  const output = [];
  const seenKeys = new Set();
  for (let i = 0; i < customItems.length; i = i + 1) {
    const item = customItems[i];
    if (isPlainReportObject(item) !== true) {
      continue;
    }
    const key = typeof item.key === 'string' && item.key.trim() !== '' ? item.key.trim() : `custom${i + 1}`;
    const name = typeof item.name === 'string' && item.name.trim() !== '' ? item.name.trim() : key;
    const result = item.result !== undefined ? item.result : customAnalysis[key];
    output.push({
      key: key,
      visibilityKey: `custom:${key}`,
      name: name,
      prompt: typeof item.prompt === 'string' ? item.prompt : '',
      value: formatAnalysisValue(getAnalysisResultValue(result)),
      explanation: getAnalysisResultExplanation(result)
    });
    seenKeys.add(key);
  }
  const customKeys = Object.keys(customAnalysis);
  for (let i = 0; i < customKeys.length; i = i + 1) {
    const key = customKeys[i];
    if (seenKeys.has(key) === true) {
      continue;
    }
    const result = customAnalysis[key];
    output.push({
      key: key,
      visibilityKey: `custom:${key}`,
      name: key,
      prompt: '',
      value: formatAnalysisValue(getAnalysisResultValue(result)),
      explanation: getAnalysisResultExplanation(result)
    });
  }
  return output;
});

function hasConversationAnalysisSection(key) {
  const data = conversationAnalysisData.value;
  if (data === null || data === undefined) {
    return false;
  }
  if (key === 'conversationSubject') {
    return hasAnalysisValue(data.conversationsubject);
  }
  if (key === 'languageDetected') {
    return hasAnalysisValue(data.languagedetected);
  }
  if (key === 'summary') {
    return hasAnalysisValue(data.summary);
  }
  if (key === 'conversationType') {
    return hasAnalysisValue(data.conversationtype);
  }
  if (key === 'crs') {
    return hasAnalysisValue(data.crs);
  }
  if (key === 'nps') {
    return hasAnalysisValue(data.nps);
  }
  if (key === 'isConversationFollowUp') {
    return data.isconversationfollowup !== null && data.isconversationfollowup !== undefined;
  }
  if (key === 'curses') {
    return data.cursesdetected !== null && data.cursesdetected !== undefined || hasAnalysisValue(data.cursesfound);
  }
  if (key === 'gender') {
    return hasAnalysisValue(data.detectedgender) === true || hasAnalysisValue(data.detectionpercentage) === true;
  }
  if (key === 'isChatAbandoned') {
    return data.ischatabandoned !== null && data.ischatabandoned !== undefined;
  }
  if (key === 'overallSentiment') {
    return hasAnalysisValue(data.overallsentiment) === true || hasAnalysisValue(data.overallsentimentscores) === true;
  }
  if (key === 'userSentiment') {
    return conversationUserSentiment.value !== null;
  }
  if (key === 'agentSentiment') {
    return conversationAgentSentiment.value !== null;
  }
  if (key === 'unansweredQuestions') {
    return Array.isArray(data.unansweredquestions) === true && data.unansweredquestions.length > 0;
  }
  if (key === 'identifiedTags') {
    return conversationTags.value.length > 0;
  }
  return false;
}

const isClosingSelectedConversation = computed(() => {
  if (typeof selectedSessionId.value !== 'string' || selectedSessionId.value.trim() === '') {
    return false;
  }
  return closingReportSessionIds.value.includes(selectedSessionId.value.trim()) === true;
});

function getChatSessionsHeaders() {
  if (isStandardSubscription.value === true) {
    return [
      { text: t('reports.headers.sessionID'), value: 'sessionid', sortable: true },
      { text: t('reports.headers.sessionStartDate'), value: 'sessionstartdate', sortable: true }
    ];
  }
  const headers = [
    { text: t('reports.headers.sessionID'), value: 'sessionid', sortable: true },
    { text: getReportTargetHeaderText(), value: 'agentname', sortable: true },
    { text: t('reports.headers.sessionStartDate'), value: 'sessionstartdate', sortable: true },
    { text: t('reports.headers.channelType'), value: 'communicationchanneltype', sortable: true },
    { text: 'Human Loop', value: 'hashumanintheloop', sortable: true },
    { text: t('reports.headers.status'), value: 'conversationopen', sortable: true },
    { text: t('reports.headers.tags'), value: 'tags', sortable: false },
    { text: t('reports.headers.reasoning'), value: 'reasoning', sortable: false }
  ];
  if (shouldShowRecordingColumn.value === true) {
    headers.splice(6, 0, { text: t('reports.headers.recording'), value: 'recordingurl', sortable: false });
  }
  return headers;
}

function stripControlChars(value) {
  const raw = typeof value === 'string' ? value : (value === null || value === undefined ? '' : String(value));
  let out = '';
  for (let i = 0; i < raw.length; i = i + 1) {
    const code = raw.charCodeAt(i);
    if (code >= 32 && code !== 127) {
      out = out + raw[i];
    }
  }
  return out;
}

function sanitizeTextValue(value, maxLen) {
  let cleaned = stripControlChars(value).trim().replace(/\s+/g, ' ');
  if (typeof maxLen === 'number' && maxLen >= 1 && cleaned.length > maxLen) {
    cleaned = cleaned.slice(0, maxLen);
  }
  return cleaned;
}

function toNumber(value) {
  if (typeof value === 'number') {
    if (isNaN(value) === true) {
      return 0;
    }
    return value;
  }
  const parsed = parseFloat(value);
  if (isNaN(parsed) === true) {
    return 0;
  }
  return parsed;
}

function createEmptyUsageInsights() {
  return {
    totalSessions: 0,
    chatSessions: 0,
    callSessions: 0,
    totalMessages: 0,
    chatMessages: 0,
    callMessages: 0,
    avgMessagesPerSession: 0,
    avgDurationMinutes: 0,
    avgChatDurationMinutes: 0,
    avgCallDurationMinutes: 0,
    resolvedSessions: 0,
    chatResolvedSessions: 0,
    callResolvedSessions: 0,
    followUpSessions: 0,
    chatFollowUpSessions: 0,
    callFollowUpSessions: 0,
    abandonedSessions: 0,
    chatAbandonedSessions: 0,
    callAbandonedSessions: 0,
    hourlyBreakdown: [],
    channelBreakdown: []
  };
}

function formatMetricNumber(value, maximumFractionDigits = 0) {
  const numericValue = toNumber(value);
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maximumFractionDigits
  });
}

function formatUsageMinutes(value) {
  const numericValue = toNumber(value);
  if (numericValue <= 0) {
    return '0m';
  }
  if (numericValue >= 60) {
    const totalMinutes = Math.round(numericValue);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h`;
  }
  if (numericValue >= 10) {
    return `${Math.round(numericValue)}m`;
  }
  return `${numericValue.toFixed(1)}m`;
}

function formatHourShort(hourValue) {
  const safeHour = Math.max(0, Math.min(23, Math.round(toNumber(hourValue))));
  return `${String(safeHour).padStart(2, '0')}:00`;
}

function formatHourRange(hourValue) {
  const safeHour = Math.max(0, Math.min(23, Math.round(toNumber(hourValue))));
  const nextHour = (safeHour + 1) % 24;
  return `${String(safeHour).padStart(2, '0')}:00 - ${String(nextHour).padStart(2, '0')}:00`;
}

function getUsageBarHeight(value, maxValue) {
  const safeValue = toNumber(value);
  const safeMax = toNumber(maxValue);
  if (safeMax <= 0 || safeValue <= 0) {
    return '4%';
  }
  const percent = Math.max((safeValue / safeMax) * 100, 8);
  return `${Math.min(percent, 100)}%`;
}

function getUsageCompositionHeight(value, totalValue) {
  const safeValue = toNumber(value);
  const safeTotal = toNumber(totalValue);
  if (safeTotal <= 0 || safeValue <= 0) {
    return '0%';
  }
  return `${(safeValue / safeTotal) * 100}%`;
}

function getTagSharePercent(count, total) {
  const safeTotal = toNumber(total);
  const safeCount = toNumber(count);
  if (safeTotal <= 0) {
    return 0;
  }
  if (safeCount < 0) {
    return 0;
  }
  return Math.round((safeCount / safeTotal) * 100);
}

function getTagCollectionField(raw, fields) {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  for (let i = 0; i < fields.length; i = i + 1) {
    const key = fields[i];
    if (raw[key] !== undefined && raw[key] !== null) {
      return raw[key];
    }
  }
  return undefined;
}

function normalizeTagCollections(rawList) {
  const list = Array.isArray(rawList) === true ? rawList : [];
  const normalized = [];
  for (let i = 0; i < list.length; i = i + 1) {
    const raw = list[i];
    const idValue = getTagCollectionField(raw, ['collectionId', 'collectionid', 'id']);
    let nameValue = getTagCollectionField(raw, ['collectionName', 'collectionname', 'name']);
    if (idValue === undefined || idValue === null) {
      continue;
    }
    let normalizedId = idValue;
    if (typeof normalizedId === 'string') {
      const trimmed = normalizedId.trim();
      if (trimmed === '') {
        continue;
      }
      const parsed = parseInt(trimmed, 10);
      if (isNaN(parsed) === false) {
        normalizedId = parsed;
      } else {
        normalizedId = trimmed;
      }
    }
    if (nameValue === undefined || nameValue === null || String(nameValue).trim() === '') {
      nameValue = String(normalizedId);
    }
    normalized.push({
      collectionId: normalizedId,
      collectionName: String(nameValue)
    });
  }
  return normalized;
}

function extractConversationText(value) {
  const raw = typeof value === 'string' ? value : (value === null || value === undefined ? '' : String(value));
  const trimmed = raw.trim();
  if (trimmed === '') {
    return '';
  }
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === 'object') {
        if (typeof parsed.default === 'string' && parsed.default.trim() !== '') {
          return parsed.default;
        }
        if (typeof parsed.text === 'string' && parsed.text.trim() !== '') {
          return parsed.text;
        }
        if (parsed.text !== null && typeof parsed.text === 'object' && typeof parsed.text.body === 'string' && parsed.text.body.trim() !== '') {
          return parsed.text.body;
        }
        if (typeof parsed.body === 'string' && parsed.body.trim() !== '') {
          return parsed.body;
        }
        if (typeof parsed.message === 'string' && parsed.message.trim() !== '') {
          return parsed.message;
        }
        if (typeof parsed.content === 'string' && parsed.content.trim() !== '') {
          return parsed.content;
        }
      }
      if (Array.isArray(parsed) === true && parsed.length > 0 && typeof parsed[0] === 'string' && parsed[0].trim() !== '') {
        return parsed[0];
      }
    } catch (error) {
      return raw;
    }
  }
  return raw;
}

function hasConversationText(value) {
  return extractConversationText(value).trim().length > 0;
}

function normalizeLatencyMs(value) {
  const parsed = Number(value);
  if (Number.isFinite(parsed) === true && parsed >= 0) {
    return Math.floor(parsed);
  }
  return 0;
}

function getLatencyObject(turn) {
  if (turn === undefined) { return null; }
  if (turn === null) { return null; }
  if (turn.latency === undefined) { return null; }
  if (turn.latency === null) { return null; }
  if (typeof turn.latency !== 'object') { return null; }
  return turn.latency;
}

function getLatencyTotalMs(turn) {
  const latency = getLatencyObject(turn);
  if (latency === null) { return 0; }
  const direct = normalizeLatencyMs(latency.totalMs);
  if (direct > 0) { return direct; }
  const stages = getLatencyStages(turn);
  let total = 0;
  for (let index = 0; index < stages.length; index = index + 1) {
    if (stages[index].key !== 'firstSentenceMs') {
      total = total + stages[index].value;
    }
  }
  return total;
}

function hasLatency(turn) {
  return getLatencyTotalMs(turn) > 0;
}

function formatLatencyMs(value) {
  const ms = normalizeLatencyMs(value);
  return `${ms}ms`;
}

function getLatencyStageDefinitions() {
  return [
    { key: 'transportInMs', label: 'Transport In', color: '#607d8b' },
    { key: 'vadDelayMs', label: 'VAD Delay', color: '#e45757' },
    { key: 'sttFinalizeMs', label: 'STT Finalize', color: '#5b9ee5' },
    { key: 'llmProcessingMs', label: 'LLM Processing', color: '#f4a23a' },
    { key: 'firstSentenceMs', label: 'First Sentence', color: '#d4c942' },
    { key: 'ttsSynthesisMs', label: 'TTS Synthesis', color: '#61b15a' },
    { key: 'transportOutMs', label: 'Transport Out', color: '#607d8b' }
  ];
}

function getLatencyStages(turn) {
  const latency = getLatencyObject(turn);
  if (latency === null) { return []; }
  const definitions = getLatencyStageDefinitions();
  const output = [];
  let maxValue = 1;
  for (let index = 0; index < definitions.length; index = index + 1) {
    const definition = definitions[index];
    const value = normalizeLatencyMs(latency[definition.key]);
    if (value <= 0) { continue; }
    if (value > maxValue) { maxValue = value; }
    output.push({
      key: definition.key,
      label: definition.label,
      value: value,
      color: definition.color,
      width: '0%'
    });
  }
  for (let index = 0; index < output.length; index = index + 1) {
    output[index].width = `${Math.max(8, Math.round((output[index].value / maxValue) * 100))}%`;
  }
  return output;
}

function getAudioProgressText(turn) {
  const latency = getLatencyObject(turn);
  if (latency === null) { return ''; }
  const finalMs = normalizeLatencyMs(latency.finalAudioProcMs);
  const totalMs = normalizeLatencyMs(latency.totalAudioProcMs);
  if (finalMs <= 0) {
    if (totalMs <= 0) { return ''; }
  }
  return `Audio processed: final ${formatLatencyMs(finalMs)}, total ${formatLatencyMs(totalMs)}`;
}

function normalizeToolExecutionEntries(entries) {
  const list = Array.isArray(entries) === true ? entries : [];
  const normalized = [];
  for (let i = 0; i < list.length; i = i + 1) {
    const item = list[i];
    if (item === undefined || item === null || typeof item !== 'object') {
      continue;
    }
    const toolName = typeof item.toolName === 'string' && item.toolName.trim().length > 0
      ? item.toolName.trim()
      : (typeof item.name === 'string' && item.name.trim().length > 0 ? item.name.trim() : 'Unknown tool');
    const statusRaw = typeof item.status === 'string' ? item.status.trim().toLowerCase() : '';
    const status = statusRaw === 'failed' ? 'failed' : 'success';
    const code = typeof item.code === 'string' && item.code.trim().length > 0
      ? item.code.trim()
      : (status === 'success' ? 'OK' : 'ERROR');
    normalized.push({
      id: typeof item.id === 'string' && item.id.trim().length > 0 ? item.id.trim() : `${toolName}-${i + 1}`,
      toolName,
      status,
      code,
      requestBody: item.requestBody !== undefined ? item.requestBody : null,
      responseBody: item.responseBody !== undefined ? item.responseBody : null,
      createdAt: typeof item.createdAt === 'string' && item.createdAt.trim().length > 0 ? item.createdAt.trim() : ''
    });
  }
  return normalized;
}

function normalizeToolNameForQueryCheck(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function parseToolExecutionRequestBody(requestBody) {
  if (requestBody === undefined || requestBody === null) {
    return null;
  }
  if (typeof requestBody === 'object' && Array.isArray(requestBody) !== true) {
    return requestBody;
  }
  if (typeof requestBody !== 'string') {
    return null;
  }
  const trimmed = requestBody.trim();
  if (trimmed.length === 0) {
    return null;
  }
  if (trimmed.startsWith('{') === false && trimmed.startsWith('[') === false) {
    return null;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null && Array.isArray(parsed) !== true) {
      return parsed;
    }
    return null;
  } catch (parseError) {
    return null;
  }
}

function isQueryOnlyRequestBody(requestBody) {
  const parsed = parseToolExecutionRequestBody(requestBody);
  if (parsed === null) {
    return false;
  }
  const keys = Object.keys(parsed);
  if (keys.length === 0) {
    return false;
  }
  for (let i = 0; i < keys.length; i = i + 1) {
    const key = keys[i];
    if (key !== 'query' && key !== 'queries') {
      return false;
    }
  }
  if (Array.isArray(parsed.queries) === true && parsed.queries.length > 0) {
    return true;
  }
  if (typeof parsed.query === 'string' && parsed.query.trim().length > 0) {
    return true;
  }
  return false;
}

function isQueryToolExecution(execution) {
  if (execution === undefined || execution === null || typeof execution !== 'object') {
    return false;
  }
  const toolName = normalizeToolNameForQueryCheck(execution.toolName);
  if (toolName === 'agent_vector_knowledge') {
    return true;
  }
  if (toolName === 'internalknowledgesearch') {
    return true;
  }
  if (toolName === 'knowledgebasesearch') {
    return true;
  }
  if (toolName.includes('knowledge') === true || toolName.includes('vector') === true) {
    return isQueryOnlyRequestBody(execution.requestBody) === true;
  }
  return isQueryOnlyRequestBody(execution.requestBody) === true;
}

function getDisplayableToolExecutions(turn) {
  const normalized = normalizeToolExecutionEntries(turn?.toolExecutions);
  const displayable = [];
  for (let i = 0; i < normalized.length; i = i + 1) {
    if (isQueryToolExecution(normalized[i]) === false) {
      displayable.push(normalized[i]);
    }
  }
  return displayable;
}

function hasToolExecutions(turn) {
  return getDisplayableToolExecutions(turn).length === 1;
}

function getToolExecutionsCount(turn) {
  return getDisplayableToolExecutions(turn).length;
}

function getToolExecutionStatusBadgeClass(status) {
  return status === 'failed' ? 'badge-error' : 'badge-success';
}

function getToolExecutionStatusText(status) {
  return status === 'failed' ? 'Failed' : 'Success';
}

function formatToolExecutionPayload(value) {
  if (value === undefined || value === null) {
    return '(empty)';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') === true || trimmed.startsWith('[') === true) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch (e) {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

function sanitizeFileNameSegment(value, maxLen) {
  let cleaned = sanitizeTextValue(value, maxLen);
  cleaned = cleaned.replace(/[\\\/:*?"<>|]/g, '');
  cleaned = cleaned.replace(/\./g, '_');
  cleaned = cleaned.replace(/_{2,}/g, '_');
  cleaned = cleaned.trim();
  if (cleaned.length === 0) {
    return '';
  }
  if (typeof maxLen === 'number' && maxLen >= 1 && cleaned.length > maxLen) {
    cleaned = cleaned.slice(0, maxLen);
  }
  return cleaned;
}

function normalizeIdValue(value, maxLen) {
  const cleaned = sanitizeTextValue(value, maxLen);
  if (cleaned === '') {
    return '';
  }
  if (/^[A-Za-z0-9א-ת._:-]+$/.test(cleaned) === false) {
    return '';
  }
  return cleaned;
}

function normalizeIdFromUnknown(value, maxLen, fieldCandidates) {
  if (typeof value === 'string' || typeof value === 'number') {
    return normalizeIdValue(String(value), maxLen);
  }
  if (value !== null && value !== undefined && typeof value === 'object') {
    const candidates = Array.isArray(fieldCandidates) === true ? fieldCandidates : [];
    for (let i = 0; i < candidates.length; i = i + 1) {
      const key = candidates[i];
      if (typeof key === 'string' && key !== '' && value[key] !== undefined && value[key] !== null) {
        const normalized = normalizeIdValue(String(value[key]), maxLen);
        if (normalized !== '') {
          return normalized;
        }
      }
    }
  }
  return '';
}

function normalizeIdList(values, maxCount, maxLen) {
  const arr = Array.isArray(values) === true ? values : [];
  const out = [];
  for (let i = 0; i < arr.length; i = i + 1) {
    const v = normalizeIdValue(arr[i], maxLen);
    if (v !== '') {
      out.push(v);
    }
    if (out.length >= maxCount) {
      break;
    }
  }
  return out;
}

function normalizeIdListFlexible(values, maxCount, maxLen, fieldCandidates) {
  const arr = Array.isArray(values) === true ? values : [];
  const out = [];
  for (let i = 0; i < arr.length; i = i + 1) {
    const v = normalizeIdFromUnknown(arr[i], maxLen, fieldCandidates);
    if (v !== '') {
      out.push(v);
    }
    if (out.length >= maxCount) {
      break;
    }
  }
  return out;
}

function normalizeChannelTypeValue(value) {
  if (typeof value !== 'string') {
    return '';
  }
  const raw = value.trim().toLowerCase();
  if (raw === '') {
    return '';
  }
  if (raw === 'excludeagentstoagents' || raw === 'exclude_agents_to_agents' || raw === 'exclude-agents-to-agents') {
    return 'excludeAgentsToAgents';
  }
  if (['agentstoagents', 'agents_to_agents', 'agents-to-agents'].includes(raw) === true) {
    return 'agentsToAgents';
  }
  if (raw === 'web' || raw === 'website' || raw === 'widget') {
    return 'web';
  }
  if (raw === 'webvoice') {
    return 'webVoice';
  }
  if (raw === 'whatsappvoice' || raw === 'whatsapp_voice') {
    return 'whatsappVoice';
  }
  if (raw === 'whatsapp' || raw.startsWith('whatsapp') === true) {
    return 'whatsapp';
  }
  if (raw === 'phone' || raw === 'call' || raw === 'voice_phone') {
    return 'phone';
  }
  if (raw === 'email' || raw.startsWith('email') === true) {
    return 'email';
  }
  return '';
}

function getChannelOption(value) {
  const normalized = normalizeChannelTypeValue(value);
  const options = baseChannelOptions.value;
  if (Array.isArray(options) === false) {
    return null;
  }
  for (let i = 0; i < options.length; i = i + 1) {
    if (options[i].id === normalized) {
      return options[i];
    }
  }
  return null;
}

function getChannelLabel(value) {
  const normalized = normalizeChannelTypeValue(value);
  if (normalized === 'whatsapp') {
    return 'Whatsapp';
  }
  const option = getChannelOption(value);
  if (option !== null && option !== undefined) {
    return option.name;
  }
  return t('reports.channelOptions.unknown');
}

function getChannelIcon(value) {
  const option = getChannelOption(value);
  if (option !== null && option !== undefined) {
    return option.icon;
  }
  return 'tabler:help-circle';
}

function formatFullIdValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}

function getCompactIdSource(value) {
  const raw = formatFullIdValue(value).trim();
  if (raw === '') {
    return '';
  }
  const parts = raw.split(':');
  for (let index = parts.length - 1; index >= 0; index = index - 1) {
    const part = parts[index].trim();
    if (part !== '') {
      return part;
    }
  }
  return raw;
}

function formatCompactIdValue(value) {
  const raw = getCompactIdSource(value);
  if (raw === '') {
    return '-';
  }
  const withoutDashes = raw.replace(/-/g, '');
  if (withoutDashes.length <= 14) {
    return withoutDashes;
  }
  return `${withoutDashes.slice(0, 8)}..${withoutDashes.slice(-4)}`;
}

function sanitizeHtml(rawHtml) {
  const input = typeof rawHtml === 'string' ? rawHtml : (rawHtml === null || rawHtml === undefined ? '' : String(rawHtml));
  if (input.trim().length === 0) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + input + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined || container === null) {
    return '';
  }
  const allowed = new Set(['div', 'span', 'h1', 'h2', 'h3', 'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td']);
  function clean(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (allowed.has(tag) === false) {
        const parent = node.parentNode;
        while (node.firstChild !== null) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
        return;
      }
      if (tag === 'a') {
        const href = node.getAttribute('href') !== null ? node.getAttribute('href') : '';
        if (/^(https?:|mailto:|tel:|#)/i.test(href) === false) {
          node.removeAttribute('href');
        } else {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      if (node.hasAttribute('style') === true) {
        const style = node.getAttribute('style') !== null ? node.getAttribute('style') : '';
        const parts = style.split(';').map(function(p){ return p.trim(); }).filter(function(p){ return p.length > 0; });
        const safeParts = parts.filter(function(p){ const l = p.toLowerCase(); return l.indexOf('background-color') === 0 || l.indexOf('color') === 0; });
        if (safeParts.length > 0) {
          node.setAttribute('style', safeParts.join(';'));
        } else {
          node.removeAttribute('style');
        }
      }
      Array.from(node.attributes).forEach(function(attr) {
        if (/^on/i.test(attr.name) === true) {
          node.removeAttribute(attr.name);
        }
      });
    }
    let child = node.firstChild;
    while (child !== null) {
      const next = child.nextSibling;
      clean(child);
      child = next;
    }
  }
  clean(container);
  return container.innerHTML.trim();
}

watch(searchValue, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, searchMaxLength);
  if (sanitized !== nextValue) {
    searchValue.value = sanitized;
  }
});

watch(reportTypeSearchValue, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, searchMaxLength);
  if (sanitized !== nextValue) {
    reportTypeSearchValue.value = sanitized;
  }
});

watch(channelTypeSearchValue, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, searchMaxLength);
  if (sanitized !== nextValue) {
    channelTypeSearchValue.value = sanitized;
  }
});

watch(conversationIdSearchValue, (nextValue) => {
  const sanitized = sanitizeTextValue(nextValue, conversationIdSearchMaxLength);
  if (sanitized !== nextValue) {
    conversationIdSearchValue.value = sanitized;
  }
});

const builtInReportOptions = computed(() => {
  const items = [
    { id: 'chatSessions', name: t('reports.reportTypes.chatSessions'), description: t('reports.reportTypeDescriptions.chatSessions'), icon: 'tabler:message-dots', isCustom: false, allowDateRange: true },
    { id: 'rawConversationsData', name: t('reports.reportTypes.rawConversationsData'), description: t('reports.reportTypeDescriptions.rawConversationsData'), icon: 'app:rawConversationsData', isCustom: false, allowDateRange: true },
    { id: 'conversationSummary', name: t('reports.reportTypes.conversationSummary'), description: t('reports.reportTypeDescriptions.conversationSummary'), icon: 'tabler:chart-bar', isCustom: false, allowDateRange: true },
    { id: 'overallConversationSummary', name: t('reports.reportTypes.overallConversationSummary'), description: t('reports.reportTypeDescriptions.overallConversationSummary'), icon: 'app:overallConversationSummary', isCustom: false, allowDateRange: true },
    { id: 'clickingReport', name: t('reports.reportTypes.clickingReport', 'Clicking Report'), description: t('reports.reportTypeDescriptions.clickingReport', 'Daily button clicks with cookie-based unique and repeat visitor counts.'), icon: 'app:clickingReport', isCustom: false, allowDateRange: true },
    { id: 'clickingDashboard', name: t('reports.reportTypes.clickingDashboard', 'Clicking Dashboard'), description: t('reports.reportTypeDescriptions.clickingDashboard', 'Hourly click activity with returning visitor and repeat-click behavior.'), icon: 'app:clickingReport', isCustom: false, allowDateRange: true },
    { id: 'responseTime', name: t('reports.reportTypes.responseTime'), description: t('reports.reportTypeDescriptions.responseTime'), icon: 'tabler:clock', isCustom: false, allowDateRange: true },
    { id: 'errorRates', name: t('reports.reportTypes.errorRates'), description: t('reports.reportTypeDescriptions.errorRates'), icon: 'tabler:alert-triangle', isCustom: false, allowDateRange: true },
    { id: 'tagSummary', name: t('reports.reportTypes.tagSummary'), description: t('reports.reportTypeDescriptions.tagSummary'), icon: 'tabler:tags', isCustom: false, allowDateRange: true },
    { id: 'tagSummaryByDay', name: t('reports.reportTypes.tagSummaryByDay'), description: t('reports.reportTypeDescriptions.tagSummaryByDay'), icon: 'tabler:tags', isCustom: false, allowDateRange: true },
    { id: 'executiveDashboard', name: t('reports.reportTypes.executiveDashboard'), description: t('reports.reportTypeDescriptions.executiveDashboard'), icon: 'tabler:dashboard', isCustom: false, allowDateRange: true },
    { id: 'adminDashboard', name: t('reports.reportTypes.adminDashboard'), description: t('reports.reportTypeDescriptions.adminDashboard'), icon: 'tabler:layout-dashboard', isCustom: false, allowDateRange: true },
    { id: 'usageDashboard', name: t('reports.reportTypes.usageDashboard'), description: t('reports.reportTypeDescriptions.usageDashboard'), icon: 'tabler:chart-line', isCustom: false, allowDateRange: true },
    { id: 'tagsDashboard', name: t('reports.reportTypes.tagsDashboard'), description: t('reports.reportTypeDescriptions.tagsDashboard'), icon: 'tabler:tags', isCustom: false, allowDateRange: true },
    { id: 'qualityDashboard', name: t('reports.reportTypes.qualityDashboard'), description: t('reports.reportTypeDescriptions.qualityDashboard'), icon: 'tabler:shield-check', isCustom: false, allowDateRange: true },
    { id: 'salesConversationScorecard', name: t('reports.reportTypes.salesConversationScorecard'), description: t('reports.reportTypeDescriptions.salesConversationScorecard'), icon: 'tabler:report', isCustom: false, allowDateRange: true },
    { id: 'serviceConversationScorecard', name: t('reports.reportTypes.serviceConversationScorecard'), description: t('reports.reportTypeDescriptions.serviceConversationScorecard'), icon: 'tabler:report', isCustom: false, allowDateRange: true }
  ];
  if (isStandardSubscription.value === true) {
    return items.filter(item => item.id !== 'rawConversationsData');
  }
  return items;
});

const campaignReportOptions = computed(() => [
  { id: 'performanceSummary', name: t('reports.campaignsReport.reportTypes.performanceSummary', 'Performance summary'), description: t('reports.campaignsReport.reportTypeDescriptions.performanceSummary', 'Campaign totals, completion rates, failures, responses, and average call duration.') },
  { id: 'deliveryLog', name: t('reports.campaignsReport.reportTypes.deliveryLog', 'Delivery log'), description: t('reports.campaignsReport.reportTypeDescriptions.deliveryLog', 'Every campaign delivery attempt with provider status, duration, errors, and AI response.') },
  { id: 'callOutcomes', name: t('reports.campaignsReport.reportTypes.callOutcomes', 'Call outcomes'), description: t('reports.campaignsReport.reportTypeDescriptions.callOutcomes', 'Outcome breakdown by campaign and delivery status.') },
  { id: 'aiResponses', name: t('reports.campaignsReport.reportTypes.aiResponses', 'AI responses'), description: t('reports.campaignsReport.reportTypeDescriptions.aiResponses', 'Answered campaign calls with the stored response prompt result.') },
  { id: 'failedDeliveries', name: t('reports.campaignsReport.reportTypes.failedDeliveries', 'Failed deliveries'), description: t('reports.campaignsReport.reportTypeDescriptions.failedDeliveries', 'Failed campaign attempts with provider details and error messages.') },
  { id: 'recipientActivity', name: t('reports.campaignsReport.reportTypes.recipientActivity', 'Recipient activity'), description: t('reports.campaignsReport.reportTypeDescriptions.recipientActivity', 'Recipient-level call count, latest status, last call, duration, and AI response.') }
]);

const selectedCampaignReportOption = computed(() => {
  const options = Array.isArray(campaignReportOptions.value) === true ? campaignReportOptions.value : [];
  for (let index = 0; index < options.length; index = index + 1) {
    if (options[index].id === selectedCampaignReport.value) {
      return options[index];
    }
  }
  return null;
});

const translatedAvailableReports = computed(() => {
  const customItems = Array.isArray(customReportOptions.value) === true
    ? customReportOptions.value.map((report, index) => ({
        id: `customReport${index + 1}`,
        name: typeof report.name === 'string' && report.name.trim() !== '' ? report.name.trim() : 'Custom Report',
        description: typeof report.description === 'string' && report.description.trim() !== '' ? report.description.trim() : buildCustomReportDescription(report),
        icon: 'tabler:settings',
        isCustom: true,
        reportId: report.reportId,
        allowDateRange: report.allowDateRange === true
      }))
    : [];
  return [...builtInReportOptions.value, ...customItems];
});

const filteredBuiltInReportOptions = computed(() => {
  const searchTerm = typeof reportTypeSearchValue.value === 'string' ? reportTypeSearchValue.value.trim().toLowerCase() : '';
  if (searchTerm === '') {
    return builtInReportOptions.value;
  }
  return builtInReportOptions.value.filter(report => {
    const reportName = typeof report.name === 'string' ? report.name.toLowerCase() : '';
    const reportDescription = typeof report.description === 'string' ? report.description.toLowerCase() : '';
    return reportName.includes(searchTerm) === true || reportDescription.includes(searchTerm) === true;
  });
});

const selectedReportOption = computed(() => {
  const items = translatedAvailableReports.value;
  if (Array.isArray(items) === false) {
    return null;
  }
  for (let i = 0; i < items.length; i = i + 1) {
    if (items[i].id === selectedReport.value) {
      return items[i];
    }
  }
  return null;
});

const isCustomReportSelected = computed(() => {
  return selectedReportOption.value !== null && selectedReportOption.value !== undefined && selectedReportOption.value.isCustom === true;
});

const selectedCustomReportId = computed(() => {
  if (isCustomReportSelected.value === false || selectedReportOption.value === null || selectedReportOption.value === undefined) {
    return '';
  }
  return typeof selectedReportOption.value.reportId === 'string' ? selectedReportOption.value.reportId : '';
});

const selectedReportAllowsDateRange = computed(() => {
  const option = selectedReportOption.value;
  if (option === null || option === undefined) {
    return true;
  }
  return option.allowDateRange !== false;
});

const selectedReportSupportsAnalysisFilter = computed(() => {
  return selectedReport.value !== '' && selectedReport.value !== 'clickingReport' && selectedReport.value !== 'clickingDashboard';
});

const selectedReportSupportsTags = computed(() => {
  return selectedReport.value !== 'clickingReport' && selectedReport.value !== 'clickingDashboard';
});

const selectedReportSupportsEntitySource = computed(() => {
  return selectedReport.value !== 'clickingReport' && selectedReport.value !== 'clickingDashboard';
});

function getReportLanguageCode() {
  if (currentLanguage.value === 'he') {
    return 'he';
  }
  if (currentLanguage.value === 'ar') {
    return 'ar';
  }
  return 'en';
}

function getLocalizedEntityLabel(localizedMap) {
  if (localizedMap === null || localizedMap === undefined || typeof localizedMap !== 'object' || Array.isArray(localizedMap) === true) {
    return '';
  }
  const languageCode = getReportLanguageCode();
  if (typeof localizedMap[languageCode] === 'string' && localizedMap[languageCode].trim() !== '') {
    return localizedMap[languageCode].trim();
  }
  if (typeof localizedMap.en === 'string' && localizedMap.en.trim() !== '') {
    return localizedMap.en.trim();
  }
  const keys = Object.keys(localizedMap);
  for (let index = 0; index < keys.length; index = index + 1) {
    if (typeof localizedMap[keys[index]] === 'string' && localizedMap[keys[index]].trim() !== '') {
      return localizedMap[keys[index]].trim();
    }
  }
  return '';
}

function getEntityDisplayLabel(entity) {
  if (entity === null || entity === undefined || typeof entity !== 'object') {
    return '';
  }
  const nameLabel = getLocalizedEntityLabel(entity.name);
  if (nameLabel !== '') {
    return nameLabel;
  }
  if (typeof entity.entityType === 'string' && entity.entityType.trim() !== '') {
    return entity.entityType.trim();
  }
  if (typeof entity.entityId === 'string') {
    return entity.entityId;
  }
  return '';
}

function normalizeEntityCollectionIdList(values) {
  const list = Array.isArray(values) === true ? values : [];
  const normalized = [];
  for (let index = 0; index < list.length; index = index + 1) {
    const collectionId = normalizeIdValue(list[index], 32);
    if (collectionId === '') {
      continue;
    }
    if (normalized.includes(collectionId) === true) {
      continue;
    }
    normalized.push(collectionId);
    if (normalized.length >= 100) {
      break;
    }
  }
  return normalized;
}

function normalizeEntityCollectionRow(rawCollection) {
  const collection = rawCollection !== null && rawCollection !== undefined && typeof rawCollection === 'object' ? rawCollection : {};
  const collectionId = normalizeIdValue(collection.collectionId !== undefined ? collection.collectionId : (collection.collectionid !== undefined ? collection.collectionid : collection.id), 32);
  if (collectionId === '') {
    return null;
  }
  const collectionNameRaw = collection.collectionName !== undefined ? collection.collectionName : (collection.collectionname !== undefined ? collection.collectionname : collection.name);
  const collectionName = typeof collectionNameRaw === 'string' && collectionNameRaw.trim() !== '' ? collectionNameRaw.trim() : collectionId;
  const entityIds = normalizeIdListFlexible(collection.entityIds !== undefined ? collection.entityIds : collection.entityids, 500, 128, ['entityId', 'entityid', 'id']);
  return {
    collectionId: collectionId,
    collectionName: collectionName,
    entityIds: entityIds
  };
}

const entityOptions = computed(() => {
  const list = Array.isArray(analysisEntities.value) === true ? analysisEntities.value : [];
  const options = [];
  for (let index = 0; index < list.length; index = index + 1) {
    const entity = list[index];
    const entityId = typeof entity.entityId === 'string' ? entity.entityId : '';
    if (entityId === '') {
      continue;
    }
    options.push({
      ...entity,
      entityId: entityId,
      displayName: getEntityDisplayLabel(entity)
    });
  }
  return options;
});

const entityCollectionOptions = computed(() => {
  const list = Array.isArray(entityCollections.value) === true ? entityCollections.value : [];
  const options = [];
  for (let index = 0; index < list.length; index = index + 1) {
    const collection = list[index];
    const entityCount = Array.isArray(collection.entityIds) === true ? collection.entityIds.length : 0;
    options.push({
      ...collection,
      collectionId: String(collection.collectionId),
      displayName: `${collection.collectionName} (${entityCount})`
    });
  }
  return options;
});

const selectedEntityCollectionIdList = computed(() => normalizeEntityCollectionIdList(selectedEntityCollectionIds.value));
const selectedEntityId = computed(() => {
  if (selectedReportEntityIds.value.length === 0) {
    return '';
  }
  return selectedReportEntityIds.value[0];
});

const selectedEntityIdList = computed(() => normalizeIdListFlexible(selectedEntityIds.value, 200, 128, ['entityId', 'entityid', 'id']));
const selectedCollectionEntityIdList = computed(() => {
  const collectionIds = selectedEntityCollectionIdList.value;
  const options = Array.isArray(entityCollectionOptions.value) === true ? entityCollectionOptions.value : [];
  const entityIds = [];
  for (let collectionIndex = 0; collectionIndex < collectionIds.length; collectionIndex = collectionIndex + 1) {
    const collectionId = collectionIds[collectionIndex];
    for (let optionIndex = 0; optionIndex < options.length; optionIndex = optionIndex + 1) {
      if (String(options[optionIndex].collectionId) !== String(collectionId)) {
        continue;
      }
      const collectionEntityIds = Array.isArray(options[optionIndex].entityIds) === true ? options[optionIndex].entityIds : [];
      for (let entityIndex = 0; entityIndex < collectionEntityIds.length; entityIndex = entityIndex + 1) {
        const entityId = normalizeIdValue(collectionEntityIds[entityIndex], 128);
        if (entityId === '') {
          continue;
        }
        if (entityIds.includes(entityId) === true) {
          continue;
        }
        entityIds.push(entityId);
      }
      break;
    }
  }
  return entityIds;
});
const selectedReportEntityIds = computed(() => {
  const directEntityIds = selectedEntityIdList.value;
  const collectionEntityIds = selectedCollectionEntityIdList.value;
  const entityIds = [];
  for (let index = 0; index < directEntityIds.length; index = index + 1) {
    if (entityIds.includes(directEntityIds[index]) === false) {
      entityIds.push(directEntityIds[index]);
    }
  }
  for (let index = 0; index < collectionEntityIds.length; index = index + 1) {
    if (entityIds.includes(collectionEntityIds[index]) === false) {
      entityIds.push(collectionEntityIds[index]);
    }
  }
  return entityIds;
});

const selectedEntityOption = computed(() => {
  if (selectedEntityId.value === '') {
    return null;
  }
  const options = Array.isArray(entityOptions.value) === true ? entityOptions.value : [];
  for (let index = 0; index < options.length; index = index + 1) {
    if (options[index].entityId === selectedEntityId.value) {
      return options[index];
    }
  }
  return null;
});

function getSelectedEntityDisplayName() {
  const option = selectedEntityOption.value;
  if (option !== null && option !== undefined && typeof option.displayName === 'string' && option.displayName.trim() !== '') {
    return option.displayName.trim();
  }
  if (option !== null && option !== undefined) {
    const label = getEntityDisplayLabel(option);
    if (label !== '') {
      return label;
    }
  }
  return selectedEntityId.value;
}

function getReportTargetHeaderText() {
  if (reportSourceMode.value === 'entities') {
    return t('reports.entityNameHeader', 'Entity name');
  }
  return t('reports.headers.agentName');
}

function formatReportHeaderText(value) {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmedValue = value.trim();
  const match = trimmedValue.match(/^(.*\S)\s+(1-\d+)$/);
  if (match === null) {
    return value;
  }
  return `${match[1]}\n${match[2]}`;
}

function formatScorecardCellValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'number' && Number.isFinite(value) === true) {
    return String(value);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return String(value);
}

function getScorecardHeaderWidth(fieldValue) {
  if (fieldValue === 'rowindex') {
    return 56;
  }
  if (fieldValue === 'totalScore') {
    return 72;
  }
  if (fieldValue === 'date') {
    return 108;
  }
  if (fieldValue === 'sessionid') {
    return 120;
  }
  if (fieldValue === 'channel') {
    return 96;
  }
  if (isScorecardAnswerField(fieldValue) === true) {
    return 76;
  }
  return undefined;
}

function isCompactIdField(fieldValue) {
  if (typeof fieldValue !== 'string') {
    return false;
  }
  const normalizedFieldValue = fieldValue.trim().toLowerCase();
  if (normalizedFieldValue === 'sessionid') {
    return true;
  }
  if (normalizedFieldValue === 'entityid') {
    return true;
  }
  if (normalizedFieldValue === 'agentid') {
    return true;
  }
  if (normalizedFieldValue === 'messageid') {
    return true;
  }
  if (normalizedFieldValue === 'conversationid') {
    return true;
  }
  return false;
}

function getReportHeaderWidth(fieldValue) {
  if (isCompactIdField(fieldValue) === true) {
    return 82;
  }
  return undefined;
}

function enhanceReportHeaders(headers) {
  if (Array.isArray(headers) === false) {
    return [];
  }
  const output = [];
  for (let index = 0; index < headers.length; index = index + 1) {
    const header = headers[index];
    if (header === null || header === undefined || typeof header !== 'object') {
      continue;
    }
    const fieldValue = typeof header.value === 'string' ? header.value : '';
    const width = getReportHeaderWidth(fieldValue);
    if (width !== undefined) {
      output.push({
        ...header,
        width
      });
      continue;
    }
    output.push(header);
  }
  return output;
}

function enhanceScorecardHeaders(headers) {
  if (Array.isArray(headers) === false) {
    return [];
  }
  const output = [];
  for (let index = 0; index < headers.length; index = index + 1) {
    const header = headers[index];
    if (header === null || header === undefined || typeof header !== 'object') {
      continue;
    }
    const fieldValue = typeof header.value === 'string' ? header.value : '';
    const width = getScorecardHeaderWidth(fieldValue);
    if (width !== undefined) {
      output.push({
        ...header,
        width
      });
      continue;
    }
    output.push(header);
  }
  return output;
}

function isAgentNameField(value) {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized === 'agentname' || normalized === 'agent_name';
}

function normalizeReportHeadersForSource(headers) {
  if (Array.isArray(headers) === false) {
    return [];
  }
  if (reportSourceMode.value !== 'entities') {
    return headers.map(header => {
      if (header !== null && header !== undefined && typeof header === 'object') {
        return {
          ...header,
          text: formatReportHeaderText(header.text)
        };
      }
      return header;
    });
  }
  return headers.map(header => {
    if (header !== null && header !== undefined && typeof header === 'object' && isAgentNameField(header.value) === true) {
      return {
        ...header,
        text: formatReportHeaderText(getReportTargetHeaderText())
      };
    }
    if (header !== null && header !== undefined && typeof header === 'object') {
      return {
        ...header,
        text: formatReportHeaderText(header.text)
      };
    }
    return header;
  });
}

function normalizeReportRowsForSource(rows, headers) {
  if (Array.isArray(rows) === false) {
    return [];
  }
  if (reportSourceMode.value !== 'entities') {
    return rows;
  }
  if (selectedReportEntityIds.value.length !== 1) {
    return rows;
  }
  const entityName = getSelectedEntityDisplayName();
  if (entityName === '') {
    return rows;
  }
  const targetFields = [];
  const headerList = Array.isArray(headers) === true ? headers : [];
  for (let index = 0; index < headerList.length; index = index + 1) {
    const header = headerList[index];
    if (header !== null && header !== undefined && typeof header === 'object' && isAgentNameField(header.value) === true) {
      targetFields.push(header.value);
    }
  }
  if (targetFields.length === 0) {
    targetFields.push('agentname');
  }
  return rows.map(row => {
    if (row === null || row === undefined || typeof row !== 'object' || Array.isArray(row) === true) {
      return row;
    }
    const nextRow = {
      ...row
    };
    for (let index = 0; index < targetFields.length; index = index + 1) {
      nextRow[targetFields[index]] = entityName;
    }
    return nextRow;
  });
}

const baseChannelOptions = computed(() => [
  { id: 'web', name: t('reports.channelOptions.web'), description: t('reports.channelDescriptions.web'), icon: 'heroicons:globe-alt' },
  { id: 'webVoice', name: t('reports.channelOptions.webVoice'), description: t('reports.channelDescriptions.webVoice'), icon: 'tabler:microphone' },
  { id: 'whatsapp', name: 'Whatsapp', description: t('reports.channelDescriptions.whatsapp'), icon: 'tabler:brand-whatsapp' },
  { id: 'whatsappVoice', name: t('reports.channelOptions.whatsappVoice'), description: t('reports.channelDescriptions.whatsappVoice'), icon: 'tabler:brand-whatsapp' },
  { id: 'phone', name: t('reports.channelOptions.phone'), description: t('reports.channelDescriptions.phone'), icon: 'tabler:phone' },
  { id: 'email', name: t('reports.channelOptions.email'), description: t('reports.channelDescriptions.email'), icon: 'tabler:mail' },
  { id: 'agentsToAgents', name: t('reports.channelOptions.agentsToAgents'), description: t('reports.channelDescriptions.agentsToAgents'), icon: 'tabler:hierarchy-2' }
]);

const translatedChannelOptions = computed(() => [
  { id: '', name: t('reports.channelOptions.all'), description: t('reports.channelDescriptions.all'), icon: 'tabler:adjustments-horizontal' },
  ...baseChannelOptions.value.filter(option => option.id !== 'agentsToAgents')
]);

const usageScopeOptions = computed(() => [
  { id: 'all', name: t('reports.usageDashboard.scopeOptions.all') },
  { id: 'chat', name: t('reports.usageDashboard.scopeOptions.chat') },
  { id: 'call', name: t('reports.usageDashboard.scopeOptions.call') }
]);

const analysisFilterKeys = [
  'languageDetected',
  'conversationType',
  'overallSentiment',
  'crs',
  'isConversationFollowUp',
  'cursesDetected',
  'detectedGender',
  'isChatAbandoned'
];

function getAnalysisFilterFieldLabel(key) {
  if (key === 'overallSentiment') {
    return t(`reports.analysisFilterFields.${key}`, 'Sentiment');
  }
  return t(`reports.analysisFilterFields.${key}`);
}

function getAnalysisFilterValueLabel(key, value) {
  if (value === 'true') {
    return t('reports.boolean.yes');
  }
  if (value === 'false') {
    return t('reports.boolean.no');
  }
  if (key === 'crs') {
    if (value === 'True') {
      return t('reports.analysisFilterValues.crs.true');
    }
    if (value === 'False') {
      return t('reports.analysisFilterValues.crs.false');
    }
    if (value === 'No Issue Found') {
      return t('reports.analysisFilterValues.crs.noIssueFound');
    }
  }
  return value;
}

const analysisFilterFieldOptions = computed(() => {
  const options = [];
  for (let i = 0; i < analysisFilterKeys.length; i = i + 1) {
    const key = analysisFilterKeys[i];
    options.push({
      id: key,
      name: getAnalysisFilterFieldLabel(key)
    });
  }
  return options;
});

const selectedAnalysisFilterValueOptions = computed(() => {
  const selectedKey = selectedAnalysisFilterKey.value;
  const rows = Array.isArray(analysisFilterOptions.value) === true ? analysisFilterOptions.value : [];
  const options = [];
  for (let i = 0; i < rows.length; i = i + 1) {
    const row = rows[i];
    if (row.filterKey === selectedKey && row.value !== '') {
      options.push({
        value: row.value,
        label: `${getAnalysisFilterValueLabel(selectedKey, row.value)} (${formatMetricNumber(row.count)})`
      });
    }
  }
  return options;
});

const filteredCustomReportOptions = computed(() => {
  const items = translatedAvailableReports.value.filter(report => report.isCustom === true);
  const searchTerm = typeof reportTypeSearchValue.value === 'string' ? reportTypeSearchValue.value.trim().toLowerCase() : '';
  if (searchTerm === '') {
    return items;
  }
  return items.filter(report => {
    const reportName = typeof report.name === 'string' ? report.name.toLowerCase() : '';
    const reportDescription = typeof report.description === 'string' ? report.description.toLowerCase() : '';
    return reportName.includes(searchTerm) === true || reportDescription.includes(searchTerm) === true;
  });
});

const filteredTranslatedChannelOptions = computed(() => {
  const searchTerm = typeof channelTypeSearchValue.value === 'string' ? channelTypeSearchValue.value.trim().toLowerCase() : '';
  if (searchTerm === '') {
    return translatedChannelOptions.value;
  }
  return translatedChannelOptions.value.filter(channel => {
    const channelName = typeof channel.name === 'string' ? channel.name.toLowerCase() : '';
    const channelDescription = typeof channel.description === 'string' ? channel.description.toLowerCase() : '';
    return channelName.includes(searchTerm) === true || channelDescription.includes(searchTerm) === true;
  });
});

const selectedChannelOption = computed(() => {
  const items = translatedChannelOptions.value;
  if (Array.isArray(items) === false) {
    return null;
  }
  for (let i = 0; i < items.length; i = i + 1) {
    if (items[i].id === selectedChannelType.value) {
      return items[i];
    }
  }
  if (items.length > 0) {
    return items[0];
  }
  return null;
});

const usageDashboardScopeLabel = computed(() => {
  const items = usageScopeOptions.value;
  for (let i = 0; i < items.length; i = i + 1) {
    if (items[i].id === selectedUsageScope.value) {
      return items[i].name;
    }
  }
  return t('reports.usageDashboard.scopeOptions.all');
});

const dashboardReportIds = ['executiveDashboard', 'adminDashboard', 'usageDashboard', 'tagsDashboard', 'qualityDashboard'];
const scorecardReportIds = ['salesConversationScorecard', 'serviceConversationScorecard'];
const isDashboardReport = computed(() => dashboardReportIds.includes(selectedReport.value));
const isClickingDashboardReport = computed(() => selectedReport.value === 'clickingDashboard');
const isScorecardReport = computed(() => scorecardReportIds.includes(selectedReport.value));
const conversationDrilldownReportIds = ['chatSessions', 'rawConversationsData', ...scorecardReportIds];
const isConversationDrilldownReport = computed(() => conversationDrilldownReportIds.includes(selectedReport.value));
const isConversationIdSearchAvailable = computed(() => isConversationDrilldownReport.value === true);

function getClickingDashboardHour(value) {
  if (typeof value === 'number') {
    return Math.max(0, Math.min(23, Math.round(value)));
  }
  if (typeof value !== 'string') {
    return 0;
  }
  const hourPart = value.trim().split(':')[0];
  const parsedHour = parseInt(hourPart, 10);
  if (isNaN(parsedHour) === true) {
    return 0;
  }
  return Math.max(0, Math.min(23, parsedHour));
}

function getClickingPercent(value, totalValue) {
  const safeValue = toNumber(value);
  const safeTotal = toNumber(totalValue);
  if (safeTotal <= 0 || safeValue <= 0) {
    return 0;
  }
  return Math.min(100, Math.round((safeValue / safeTotal) * 100));
}

const clickingDashboardView = computed(() => {
  const rows = Array.isArray(reportData.value) === true ? reportData.value : [];
  const hourlyBreakdown = [];
  for (let hour = 0; hour < 24; hour = hour + 1) {
    hourlyBreakdown.push({
      hour,
      clicks: 0,
      uniqueVisitors: 0,
      returningVisitors: 0,
      returnRatePercentage: 0,
      repeatClicks: 0,
      sessions: 0
    });
  }
  for (let index = 0; index < rows.length; index = index + 1) {
    const row = rows[index];
    const hour = getClickingDashboardHour(row.hour);
    hourlyBreakdown[hour] = {
      hour,
      clicks: toNumber(row.clicks),
      uniqueVisitors: toNumber(row.uniquevisitors),
      returningVisitors: toNumber(row.returningvisitors),
      returnRatePercentage: toNumber(row.returnratepercentage),
      repeatClicks: toNumber(row.repeatclicks),
      sessions: toNumber(row.sessions)
    };
  }
  let totalClicksFallback = 0;
  let totalUniqueVisitorsFallback = 0;
  let totalReturningVisitorsFallback = 0;
  let totalRepeatClicksFallback = 0;
  let totalSessionsFallback = 0;
  let hourlyMax = 0;
  let activeHours = 0;
  for (let index = 0; index < hourlyBreakdown.length; index = index + 1) {
    const row = hourlyBreakdown[index];
    totalClicksFallback = totalClicksFallback + row.clicks;
    totalUniqueVisitorsFallback = totalUniqueVisitorsFallback + row.uniqueVisitors;
    totalReturningVisitorsFallback = totalReturningVisitorsFallback + row.returningVisitors;
    totalRepeatClicksFallback = totalRepeatClicksFallback + row.repeatClicks;
    totalSessionsFallback = totalSessionsFallback + row.sessions;
    if (row.clicks > hourlyMax) {
      hourlyMax = row.clicks;
    }
    if (row.clicks > 0) {
      activeHours = activeHours + 1;
    }
  }
  const firstRow = rows.length > 0 && rows[0] !== undefined && rows[0] !== null ? rows[0] : {};
  const totalClicksFromQuery = toNumber(firstRow.totalclicks);
  const totalUniqueVisitorsFromQuery = toNumber(firstRow.totaluniquevisitors);
  const totalReturningVisitorsFromQuery = toNumber(firstRow.totalreturningvisitors);
  const totalRepeatClicksFromQuery = toNumber(firstRow.totalrepeatclicks);
  const totalSessionsFromQuery = toNumber(firstRow.totalsessions);
  const totalClicks = totalClicksFromQuery > 0 ? totalClicksFromQuery : totalClicksFallback;
  const totalUniqueVisitors = totalUniqueVisitorsFromQuery > 0 ? totalUniqueVisitorsFromQuery : totalUniqueVisitorsFallback;
  const totalReturningVisitors = totalReturningVisitorsFromQuery > 0 ? totalReturningVisitorsFromQuery : totalReturningVisitorsFallback;
  const totalRepeatClicks = totalRepeatClicksFromQuery > 0 ? totalRepeatClicksFromQuery : totalRepeatClicksFallback;
  const totalSessions = totalSessionsFromQuery > 0 ? totalSessionsFromQuery : totalSessionsFallback;
  const totalReturnRateFromQuery = toNumber(firstRow.totalreturnratepercentage);
  const totalReturnRatePercentage = totalReturnRateFromQuery > 0 ? Number(totalReturnRateFromQuery.toFixed(1)) : (totalUniqueVisitors > 0 ? Number(((totalReturningVisitors / totalUniqueVisitors) * 100).toFixed(1)) : 0);
  const repeatClickRate = totalClicks > 0 ? Math.round((totalRepeatClicks / totalClicks) * 100) : 0;
  const clicksPerVisitor = totalUniqueVisitors > 0 ? Number((totalClicks / totalUniqueVisitors).toFixed(1)) : 0;
  const sessionsPerActiveHour = activeHours > 0 ? Number((totalSessions / activeHours).toFixed(1)) : 0;
  const busiestHours = [...hourlyBreakdown]
    .filter(item => item.clicks > 0)
    .sort((a, b) => {
      if (b.clicks !== a.clicks) {
        return b.clicks - a.clicks;
      }
      return a.hour - b.hour;
    })
    .slice(0, 5);
  const peakHour = busiestHours.length > 0 ? busiestHours[0] : null;
  const peakShare = peakHour !== null && totalClicks > 0 ? Math.round((peakHour.clicks / totalClicks) * 100) : 0;
  const returnRateLineDots = [];
  const returnRateLineValues = [];
  for (let index = 0; index < hourlyBreakdown.length; index = index + 1) {
    const row = hourlyBreakdown[index];
    const x = hourlyBreakdown.length > 1 ? Number(((index / (hourlyBreakdown.length - 1)) * 100).toFixed(2)) : 0;
    const y = Number((38 - ((Math.max(0, Math.min(100, row.returnRatePercentage)) / 100) * 36)).toFixed(2));
    returnRateLineDots.push({
      hour: row.hour,
      x,
      y,
      value: row.returnRatePercentage
    });
    returnRateLineValues.push(`${x},${y}`);
  }
  const newVisitors = Math.max(totalUniqueVisitors - totalReturningVisitors, 0);
  const behaviorItems = [
    {
      id: 'newVisitors',
      label: t('reports.clickingDashboard.newVisitorsLabel', 'New visitors'),
      value: newVisitors,
      percent: getClickingPercent(newVisitors, totalUniqueVisitors),
      colorClass: 'clicking-behavior-bar-new'
    },
    {
      id: 'returningVisitors',
      label: t('reports.clickingDashboard.returningVisitorsLabel', 'Returning visitors'),
      value: totalReturningVisitors,
      percent: getClickingPercent(totalReturningVisitors, totalUniqueVisitors),
      colorClass: 'clicking-behavior-bar-returning'
    },
    {
      id: 'repeatClicks',
      label: t('reports.clickingDashboard.repeatClicksLabel', 'Repeat clicks'),
      value: totalRepeatClicks,
      percent: getClickingPercent(totalRepeatClicks, totalClicks),
      colorClass: 'clicking-behavior-bar-repeat'
    },
    {
      id: 'sessions',
      label: t('reports.clickingDashboard.sessionsLabel', 'Sessions'),
      value: totalSessions,
      percent: getClickingPercent(totalSessions, totalClicks),
      colorClass: 'clicking-behavior-bar-sessions'
    }
  ];
  return {
    hasData: rows.length > 0 && totalClicks > 0,
    totalClicks,
    totalUniqueVisitors,
    totalReturningVisitors,
    totalReturnRatePercentage,
    totalRepeatClicks,
    totalSessions,
    repeatClickRate,
    clicksPerVisitor,
    sessionsPerActiveHour,
    activeHours,
    hourlyBreakdown,
    hourlyMax,
    busiestHours,
    peakHour,
    peakShare,
    returnRateLinePoints: returnRateLineValues.join(' '),
    returnRateLineDots,
    behaviorItems
  };
});

const usageDashboardView = computed(() => {
  const usageSource = dashboardSummary.value !== null && dashboardSummary.value !== undefined && dashboardSummary.value.usageInsights !== undefined && dashboardSummary.value.usageInsights !== null
    ? dashboardSummary.value.usageInsights
    : createEmptyUsageInsights();
  const scope = selectedUsageScope.value;
  let totalSessions = toNumber(usageSource.totalSessions);
  let totalMessages = toNumber(usageSource.totalMessages);
  let avgDurationMinutes = toNumber(usageSource.avgDurationMinutes);
  let resolvedSessions = toNumber(usageSource.resolvedSessions);
  let followUpSessions = toNumber(usageSource.followUpSessions);
  let abandonedSessions = toNumber(usageSource.abandonedSessions);
  if (scope === 'chat') {
    totalSessions = toNumber(usageSource.chatSessions);
    totalMessages = toNumber(usageSource.chatMessages);
    avgDurationMinutes = toNumber(usageSource.avgChatDurationMinutes);
    resolvedSessions = toNumber(usageSource.chatResolvedSessions);
    followUpSessions = toNumber(usageSource.chatFollowUpSessions);
    abandonedSessions = toNumber(usageSource.chatAbandonedSessions);
  } else if (scope === 'call') {
    totalSessions = toNumber(usageSource.callSessions);
    totalMessages = toNumber(usageSource.callMessages);
    avgDurationMinutes = toNumber(usageSource.avgCallDurationMinutes);
    resolvedSessions = toNumber(usageSource.callResolvedSessions);
    followUpSessions = toNumber(usageSource.callFollowUpSessions);
    abandonedSessions = toNumber(usageSource.callAbandonedSessions);
  }
  const hourlySource = Array.isArray(usageSource.hourlyBreakdown) === true ? usageSource.hourlyBreakdown : [];
  const hourlyBreakdown = [];
  for (let i = 0; i < hourlySource.length; i = i + 1) {
    const row = hourlySource[i];
    const totalSessionValue = toNumber(row.totalSessions);
    const chatSessionValue = toNumber(row.chatSessions);
    const callSessionValue = toNumber(row.callSessions);
    const totalMessageValue = toNumber(row.totalMessages);
    const chatMessageValue = toNumber(row.chatMessages);
    const callMessageValue = toNumber(row.callMessages);
    hourlyBreakdown.push({
      hour: toNumber(row.hour),
      totalSessions: totalSessionValue,
      chatSessions: chatSessionValue,
      callSessions: callSessionValue,
      totalMessages: totalMessageValue,
      chatMessages: chatMessageValue,
      callMessages: callMessageValue,
      sessionCount: scope === 'chat' ? chatSessionValue : (scope === 'call' ? callSessionValue : totalSessionValue),
      messageCount: scope === 'chat' ? chatMessageValue : (scope === 'call' ? callMessageValue : totalMessageValue)
    });
  }
  let hourlyMax = 0;
  let activeHours = 0;
  for (let i = 0; i < hourlyBreakdown.length; i = i + 1) {
    const row = hourlyBreakdown[i];
    if (row.sessionCount > hourlyMax) {
      hourlyMax = row.sessionCount;
    }
    if (row.sessionCount > 0) {
      activeHours = activeHours + 1;
    }
  }
  const busiestHours = [...hourlyBreakdown]
    .filter(item => item.sessionCount > 0)
    .sort((a, b) => {
      if (b.sessionCount !== a.sessionCount) {
        return b.sessionCount - a.sessionCount;
      }
      return a.hour - b.hour;
    })
    .slice(0, 5);
  const peakHour = busiestHours.length > 0 ? busiestHours[0] : null;
  const mixItems = [];
  if (scope === 'all') {
    const chatValue = toNumber(usageSource.chatSessions);
    const callValue = toNumber(usageSource.callSessions);
    mixItems.push({
      id: 'chat',
      label: t('reports.usageDashboard.chatConversationsLabel'),
      icon: 'tabler:message-dots',
      value: chatValue,
      percent: totalSessions > 0 ? Math.round((chatValue / totalSessions) * 100) : 0,
      colorClass: 'usage-mix-bar-chat'
    });
    mixItems.push({
      id: 'call',
      label: t('reports.usageDashboard.callConversationsLabel'),
      icon: 'tabler:phone-call',
      value: callValue,
      percent: totalSessions > 0 ? Math.round((callValue / totalSessions) * 100) : 0,
      colorClass: 'usage-mix-bar-call'
    });
  } else {
    const channelRows = Array.isArray(usageSource.channelBreakdown) === true ? usageSource.channelBreakdown.filter(item => item.usageScope === scope) : [];
    let channelTotal = 0;
    for (let i = 0; i < channelRows.length; i = i + 1) {
      channelTotal = channelTotal + toNumber(channelRows[i].totalSessions);
    }
    for (let i = 0; i < channelRows.length; i = i + 1) {
      const item = channelRows[i];
      const itemValue = toNumber(item.totalSessions);
      mixItems.push({
        id: `${scope}-${item.channelType}-${i}`,
        label: getChannelLabel(item.channelType),
        icon: getChannelIcon(item.channelType),
        value: itemValue,
        percent: channelTotal > 0 ? Math.round((itemValue / channelTotal) * 100) : 0,
        colorClass: scope === 'call' ? 'usage-mix-bar-call' : 'usage-mix-bar-chat'
      });
    }
  }
  const resolutionRate = totalSessions > 0 ? Math.round((resolvedSessions / totalSessions) * 100) : 0;
  const followUpRate = totalSessions > 0 ? Math.round((followUpSessions / totalSessions) * 100) : 0;
  const abandonedRate = totalSessions > 0 ? Math.round((abandonedSessions / totalSessions) * 100) : 0;
  const sessionsPerActiveHour = activeHours > 0 ? Number((totalSessions / activeHours).toFixed(1)) : 0;
  const messagesPerActiveHour = activeHours > 0 ? Number((totalMessages / activeHours).toFixed(1)) : 0;
  const messagesPerConversation = totalSessions > 0 ? Number((totalMessages / totalSessions).toFixed(1)) : 0;
  const peakShare = peakHour !== null && totalSessions > 0 ? Math.round((peakHour.sessionCount / totalSessions) * 100) : 0;
  return {
    totalSessions,
    totalMessages,
    avgDurationMinutes,
    resolvedSessions,
    activeHours,
    hourlyBreakdown,
    hourlyMax,
    busiestHours,
    peakHour,
    mixItems,
    resolutionRate,
    followUpRate,
    abandonedRate,
    sessionsPerActiveHour,
    messagesPerActiveHour,
    messagesPerConversation,
    peakShare
  };
});

const dashboardCustomAnalysisItems = computed(() => {
  const summary = dashboardSummary.value;
  if (summary === null || summary === undefined) {
    return [];
  }
  if (summary.analysisEngine === null || summary.analysisEngine === undefined) {
    return [];
  }
  if (Array.isArray(summary.analysisEngine.customItems) !== true) {
    return [];
  }
  return summary.analysisEngine.customItems;
});

const translatedPresets = computed(() => ({
  today: t('reports.presets.today'),
  thisWeek: t('reports.presets.thisWeek'),
  lastWeek: t('reports.presets.lastWeek'),
  thisMonth: t('reports.presets.thisMonth'),
  lastMonth: t('reports.presets.lastMonth')
}));

const availableReports = [
  { id: 'chatSessions', name: 'Chat Sessions Overview' },
  { id: 'rawConversationsData', name: 'Raw conversations data report' },
  { id: 'conversationSummary', name: 'Conversation Summary' },
  { id: 'overallConversationSummary', name: 'Overall conversation summary' },
  { id: 'responseTime', name: 'Response Time Analysis' },
  { id: 'errorRates', name: 'Error Rate Analysis' },
  { id: 'tagSummary', name: 'Tag Summary' },
  { id: 'tagSummaryByDay', name: 'Tag Summary By Day' },
  { id: 'executiveDashboard', name: 'Executive Dashboard' },
  { id: 'usageDashboard', name: 'Usage Dashboard' },
  { id: 'tagsDashboard', name: 'Tags Dashboard' },
  { id: 'qualityDashboard', name: 'Quality Dashboard' }
];

const presets = {
  today: 'Today',
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month'
};

const presetRanges = {
  today: () => {
    const today = new Date();
    return [today, today];
  },
  thisWeek: () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    return [startOfWeek, today];
  },
  lastWeek: () => {
    const today = new Date();
    const startOfLastWeek = new Date(today);
    startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
    const endOfLastWeek = new Date(today);
    endOfLastWeek.setDate(today.getDate() - today.getDay() - 1);
    return [startOfLastWeek, endOfLastWeek];
  },
  thisMonth: () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    return [startOfMonth, today];
  },
  lastMonth: () => {
    const today = new Date();
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    return [startOfLastMonth, endOfLastMonth];
  }
};

const isFormValid = computed(() => {
  const hasSelectedAgents = reportSourceMode.value === 'agents' && selectedAgents.value.length > 0;
  const hasSelectedEntity = reportSourceMode.value === 'entities' && selectedReportSupportsEntitySource.value === true && selectedReportEntityIds.value.length > 0;
  const hasReportTarget = hasSelectedAgents === true || hasSelectedEntity === true;
  const hasSelectedReport = isDefinedAndNotEmpty(selectedReport.value) != false;
  if (selectedReportAllowsDateRange.value === false) {
    return hasReportTarget === true && hasSelectedReport === true;
  }
  return hasReportTarget === true &&
         hasSelectedReport === true &&
         dateRange.value.length === 2 &&
         dateRange.value[0] <= dateRange.value[1];
});

const isCampaignsReportFormValid = computed(() => {
  if (typeof selectedCampaignReport.value !== 'string' || selectedCampaignReport.value.trim().length === 0) {
    return false;
  }
  if (Array.isArray(selectedCampaignIds.value) !== true || selectedCampaignIds.value.length === 0) {
    return false;
  }
  if (Array.isArray(dateRange.value) !== true || dateRange.value.length !== 2) {
    return false;
  }
  const startDate = new Date(dateRange.value[0]);
  const endDate = new Date(dateRange.value[1]);
  if (isNaN(startDate.getTime()) === true || isNaN(endDate.getTime()) === true) {
    return false;
  }
  return startDate.getTime() <= endDate.getTime();
});

const isGenerateReportDisabled = computed(() => {
  if (isCampaignsReportMode.value === true) {
    return isCampaignsReportFormValid.value !== true;
  }
  return isFormValid.value !== true;
});

const selectedPreset = ref('');

const setReportSourceMode = (mode) => {
  if (mode !== 'agents' && mode !== 'entities') {
    return;
  }
  if (mode === 'entities' && selectedReportSupportsEntitySource.value !== true) {
    return;
  }
  if (reportSourceMode.value === mode) {
    return;
  }
  reportSourceMode.value = mode;
};

function openCustomButtonsPage() {
  router.push('/customReportButtons');
}

const applyPreset = () => {
  if (selectedPreset.value != '' && presetRanges[selectedPreset.value] != undefined) {
    dateRange.value = presetRanges[selectedPreset.value]();
  }
}

const toggleReportDropdown = () => {
  isReportDropdownOpen.value = isReportDropdownOpen.value === false;
  if (isReportDropdownOpen.value === false) {
    reportTypeSearchValue.value = '';
  }
};

const selectReportType = (report) => {
  if (report !== null && report !== undefined && typeof report.id === 'string') {
    selectedReport.value = report.id;
    reportFieldVisibility.value = {};
    if (report.id === 'clickingReport' || report.id === 'clickingDashboard') {
      reportSourceMode.value = 'agents';
      selectedAnalysisFilterKey.value = '';
      selectedAnalysisFilterValue.value = '';
      selectedTagIds.value = [];
      selectedTagCollectionId.value = '';
    }
  }
  isReportDropdownOpen.value = false;
  reportTypeSearchValue.value = '';
};

const handleReportDropdownClickOutside = (event) => {
  if (isReportDropdownOpen.value === false) {
    return;
  }
  const dropdownEl = reportDropdownRef.value;
  if (dropdownEl !== null && dropdownEl !== undefined) {
    if (dropdownEl.contains(event.target) === false) {
      isReportDropdownOpen.value = false;
      reportTypeSearchValue.value = '';
    }
  }
};

const toggleChannelDropdown = () => {
  isChannelDropdownOpen.value = isChannelDropdownOpen.value === false;
  if (isChannelDropdownOpen.value === false) {
    channelTypeSearchValue.value = '';
  }
};

const selectChannelType = (channel) => {
  if (channel !== null && channel !== undefined && typeof channel.id === 'string') {
    selectedChannelType.value = channel.id;
  } else {
    selectedChannelType.value = '';
  }
  isChannelDropdownOpen.value = false;
  channelTypeSearchValue.value = '';
};

const handleChannelDropdownClickOutside = (event) => {
  if (isChannelDropdownOpen.value === false) {
    return;
  }
  const dropdownEl = channelDropdownRef.value;
  if (dropdownEl !== null && dropdownEl !== undefined) {
    if (dropdownEl.contains(event.target) === false) {
      isChannelDropdownOpen.value = false;
      channelTypeSearchValue.value = '';
    }
  }
};

const getAuthToken = () => {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') != false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') != false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const getReportGenerateSocketUrl = () => {
  let endpoint = typeof globalState.apiEndpoint === 'string' ? globalState.apiEndpoint.trim() : '';
  if (endpoint.length === 0 && typeof window !== 'undefined' && window.location !== undefined && window.location !== null) {
    endpoint = window.location.origin;
  }
  if (endpoint.length === 0) {
    return '';
  }
  const sanitizedEndpoint = endpoint.endsWith('/') === true ? endpoint.slice(0, -1) : endpoint;
  const socketBase = sanitizedEndpoint.startsWith('https://') === true
    ? `wss://${sanitizedEndpoint.slice('https://'.length)}`
    : sanitizedEndpoint.startsWith('http://') === true
      ? `ws://${sanitizedEndpoint.slice('http://'.length)}`
      : sanitizedEndpoint;
  return `${socketBase}/api/reports/generate/ws`;
};

const createReportSocketError = (message, statusCode, requestSent) => {
  const error = new Error(message);
  error.statusCode = statusCode;
  error.requestSent = requestSent;
  return error;
};

const generateReportOverSocket = (requestBody) => {
  return new Promise((resolve, reject) => {
    const socketUrl = getReportGenerateSocketUrl();
    if (socketUrl === '' || typeof WebSocket === 'undefined') {
      reject(createReportSocketError('Report WebSocket is unavailable.', 0, false));
      return;
    }
    const requestId = `report-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const token = getAuthToken();
    let settled = false;
    let requestSent = false;
    let connectionTimer = null;
    let ws = null;
    const finish = (error, result) => {
      if (settled === true) {
        return;
      }
      settled = true;
      if (connectionTimer !== null) {
        window.clearTimeout(connectionTimer);
        connectionTimer = null;
      }
      if (reportGenerateSocket === ws) {
        reportGenerateSocket = null;
      }
      if (ws !== null) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        if (ws.readyState === 0 || ws.readyState === 1) {
          ws.close();
        }
      }
      if (error !== null && error !== undefined) {
        reject(error);
        return;
      }
      resolve(result);
    };
    const sendRequest = () => {
      if (requestSent === true || ws === null || ws.readyState !== 1) {
        return;
      }
      requestSent = true;
      if (connectionTimer !== null) {
        window.clearTimeout(connectionTimer);
        connectionTimer = null;
      }
      ws.send(JSON.stringify({
        type: 'generateReport',
        requestId,
        ...requestBody
      }));
    };
    try {
      ws = new WebSocket(socketUrl);
    } catch (error) {
      reject(createReportSocketError('Failed to open report WebSocket.', 0, false));
      return;
    }
    reportGenerateSocket = ws;
    connectionTimer = window.setTimeout(() => {
      finish(createReportSocketError('Report WebSocket connection timed out.', 0, false), null);
    }, 15000);
    ws.onopen = () => {
      if (token !== '') {
        ws.send(JSON.stringify({ type: 'authenticate', token }));
      }
    };
    ws.onmessage = (event) => {
      let payload = null;
      try {
        payload = JSON.parse(String(event.data));
      } catch (error) {
        payload = null;
      }
      if (payload === null || typeof payload !== 'object') {
        return;
      }
      const type = typeof payload.type === 'string' ? payload.type : '';
      if (type === 'ready') {
        sendRequest();
        return;
      }
      if (payload.requestId !== requestId) {
        return;
      }
      if (type === 'reportResult') {
        finish(null, {
          data: Array.isArray(payload.data) === true ? payload.data : [],
          headers: Array.isArray(payload.headers) === true ? payload.headers : []
        });
        return;
      }
      if (type === 'reportError' || type === 'error') {
        const statusCode = Number(payload.statusCode);
        const safeStatusCode = Number.isInteger(statusCode) === true ? statusCode : 500;
        const message = typeof payload.message === 'string' && payload.message !== '' ? payload.message : 'Failed to generate report.';
        finish(createReportSocketError(message, safeStatusCode, requestSent), null);
      }
    };
    ws.onerror = () => {
      finish(createReportSocketError('Report WebSocket connection failed.', 0, requestSent), null);
    };
    ws.onclose = () => {
      finish(createReportSocketError('Report WebSocket connection closed before completion.', 0, requestSent), null);
    };
  });
};

const clearSelectedConversationRecording = () => {
  if (selectedConversationRecordingUrl.value !== '') {
    try { URL.revokeObjectURL(selectedConversationRecordingUrl.value); } catch (error) {}
  }
  selectedConversationRecordingUrl.value = '';
  selectedConversationRecordingLoading.value = false;
  selectedConversationRecordingError.value = '';
};

const getBlobAuthHeaders = () => {
  const headers = getAuthHeaders();
  if (headers['Content-Type'] !== undefined) {
    delete headers['Content-Type'];
  }
  return headers;
};

const clearReportRecordingUrls = () => {
  const values = Object.values(reportRecordingUrls.value);
  for (let index = 0; index < values.length; index = index + 1) {
    const url = typeof values[index] === 'string' ? values[index] : '';
    if (url !== '') {
      try { URL.revokeObjectURL(url); } catch (error) {}
    }
  }
  reportRecordingUrls.value = {};
  reportRecordingLoadingSessionIds.value = [];
};

const getReportRecordingAudioUrl = (row) => {
  const safeSessionId = normalizeIdValue(row?.sessionid, 180);
  if (safeSessionId === '') {
    return '';
  }
  const url = reportRecordingUrls.value[safeSessionId];
  return typeof url === 'string' ? url : '';
};

const isReportRecordingLoading = (row) => {
  const safeSessionId = normalizeIdValue(row?.sessionid, 180);
  if (safeSessionId === '') {
    return false;
  }
  return reportRecordingLoadingSessionIds.value.includes(safeSessionId) === true;
};

const loadReportRecordingAudioUrl = async (row) => {
  if (shouldShowRecordingColumn.value !== true) {
    return;
  }
  const safeSessionId = normalizeIdValue(row?.sessionid, 180);
  const recordingValue = typeof row?.recordingurl === 'string' ? row.recordingurl.trim() : '';
  if (safeSessionId === '' || recordingValue === '') {
    return;
  }
  if (reportRecordingUrls.value[safeSessionId] !== undefined) {
    return;
  }
  if (reportRecordingLoadingSessionIds.value.includes(safeSessionId) === true) {
    return;
  }
  reportRecordingLoadingSessionIds.value = [...reportRecordingLoadingSessionIds.value, safeSessionId];
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/reports/conversation/${encodeURIComponent(safeSessionId)}/recording`, {
      headers: getBlobAuthHeaders(),
      responseType: 'blob'
    });
    const contentType = typeof response.headers?.['content-type'] === 'string' && response.headers['content-type'].trim() !== '' ? response.headers['content-type'].trim() : 'audio/wav';
    const blob = new Blob([response.data], { type: contentType });
    const objectUrl = URL.createObjectURL(blob);
    reportRecordingUrls.value = { ...reportRecordingUrls.value, [safeSessionId]: objectUrl };
  } catch (error) {
    reportRecordingUrls.value = { ...reportRecordingUrls.value, [safeSessionId]: '' };
  } finally {
    reportRecordingLoadingSessionIds.value = reportRecordingLoadingSessionIds.value.filter(id => id !== safeSessionId);
  }
};

const hydrateReportRecordingAudioUrls = async (rows) => {
  if (shouldShowRecordingColumn.value !== true) {
    return;
  }
  const list = Array.isArray(rows) === true ? rows : [];
  const loaders = [];
  for (let index = 0; index < list.length; index = index + 1) {
    if (typeof list[index]?.recordingurl === 'string' && list[index].recordingurl.trim() !== '') {
      loaders.push(loadReportRecordingAudioUrl(list[index]));
    }
  }
  await Promise.all(loaders);
};

const handleAuthError = (error) => {
  const message = typeof error?.response?.data?.message === 'string' ? error.response.data.message : (typeof error?.response?.data?.error === 'string' ? error.response.data.error : '');
  const expired = typeof message === 'string' ? message.toLowerCase().includes('jwt expired') : false;
  const statusCode = Number(error?.response?.status !== undefined ? error.response.status : error?.statusCode);
  if (statusCode === 401 || statusCode === 403 || expired === true) {
    router.push('/login');
  }
};

const fetchAvailableTags = async () => {
  isLoadingTags.value = true;
  try {
    const collectionRaw = selectedTagCollectionId.value;
    const params = {};
    if (collectionRaw !== '' && collectionRaw !== null && collectionRaw !== undefined) {
      const parsedId = parseInt(collectionRaw, 10);
      if (isNaN(parsedId) === false) {
        params.collectionId = parsedId;
      }
    }
    const response = await axios.get(`${globalState.apiEndpoint}/api/tags/`, {
      params,
      headers: getAuthHeaders()
    });
    availableTags.value = response.data;
  } catch (error) {
    toast.error(t('reports.toasts.failedToLoadTags'));
    console.error('Error loading tags:', error);
    handleAuthError(error);
    availableTags.value = [];
  } finally {
    isLoadingTags.value = false;
  }
};

const fetchAvailableTagCollections = async () => {
  isLoadingTagCollections.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections`, {
      headers: getAuthHeaders()
    });
    tagCollections.value = normalizeTagCollections(response.data);
  } catch (error) {
    toast.error(t('reports.toasts.failedToLoadTags'));
    console.error('Error loading tag collections:', error);
    handleAuthError(error);
    tagCollections.value = [];
  } finally {
    isLoadingTagCollections.value = false;
  }
};

const fetchAvailableCustomReports = async () => {
  isLoadingCustomReports.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/customReports/available`, {
      headers: getAuthHeaders()
    });
    customReportOptions.value = Array.isArray(response.data) === true ? response.data : [];
  } catch (error) {
    console.error('Error loading custom reports:', error);
    handleAuthError(error);
    customReportOptions.value = [];
  } finally {
    isLoadingCustomReports.value = false;
  }
};

const fetchAvailableEntities = async () => {
  isLoadingEntities.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/entitiesAnalysis/entities`, {
      headers: getAuthHeaders()
    });
    const list = response.data !== undefined && response.data !== null && Array.isArray(response.data.entities) === true ? response.data.entities : [];
    analysisEntities.value = list;
    if (selectedEntityIdList.value.length > 0) {
      const validEntityIds = [];
      for (let index = 0; index < list.length; index = index + 1) {
        const entityId = typeof list[index].entityId === 'string' ? list[index].entityId : '';
        if (entityId !== '') {
          validEntityIds.push(entityId);
        }
      }
      selectedEntityIds.value = selectedEntityIdList.value.filter(entityId => validEntityIds.includes(entityId) === true);
    }
  } catch (error) {
    console.error('Error loading entities:', error);
    handleAuthError(error);
    analysisEntities.value = [];
  } finally {
    isLoadingEntities.value = false;
  }
};

const fetchAvailableEntityCollections = async () => {
  isLoadingEntityCollections.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/entitiesAnalysis/entityCollections`, {
      headers: getAuthHeaders()
    });
    const rows = response.data !== undefined && response.data !== null && Array.isArray(response.data.collections) === true ? response.data.collections : [];
    const normalizedCollections = [];
    for (let index = 0; index < rows.length; index = index + 1) {
      const normalizedCollection = normalizeEntityCollectionRow(rows[index]);
      if (normalizedCollection === null) {
        continue;
      }
      normalizedCollections.push(normalizedCollection);
    }
    entityCollections.value = normalizedCollections;
    if (selectedEntityCollectionIdList.value.length > 0) {
      const validCollectionIds = [];
      for (let index = 0; index < normalizedCollections.length; index = index + 1) {
        validCollectionIds.push(String(normalizedCollections[index].collectionId));
      }
      selectedEntityCollectionIds.value = selectedEntityCollectionIdList.value.filter(collectionId => validCollectionIds.includes(String(collectionId)) === true);
    }
  } catch (error) {
    console.error('Error loading entity collections:', error);
    handleAuthError(error);
    entityCollections.value = [];
  } finally {
    isLoadingEntityCollections.value = false;
  }
};

const fetchAnalysisFilterOptions = async () => {
  isLoadingAnalysisFilterOptions.value = true;
  try {
    if (selectedReport.value === '' || selectedReportSupportsAnalysisFilter.value !== true) {
      analysisFilterOptions.value = [];
      return;
    }
    const startCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[0] : null;
    const endCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[1] : null;
    const startDate = startCandidate !== null ? new Date(startCandidate) : null;
    const endDate = endCandidate !== null ? new Date(endCandidate) : null;
    let hasInvalidDateRange = false;
    if (startDate === null) {
      hasInvalidDateRange = true;
    }
    if (endDate === null) {
      hasInvalidDateRange = true;
    }
    if (startDate !== null && isNaN(startDate.getTime()) === true) {
      hasInvalidDateRange = true;
    }
    if (endDate !== null && isNaN(endDate.getTime()) === true) {
      hasInvalidDateRange = true;
    }
    if (hasInvalidDateRange === true) {
      analysisFilterOptions.value = [];
      return;
    }
    const safeAgentIds = reportSourceMode.value === 'agents' ? normalizeIdListFlexible(selectedAgents.value, 50, 128, ['agentid', 'agentId', 'id']) : [];
    const safeEntityIds = reportSourceMode.value === 'entities' ? selectedReportEntityIds.value : [];
    if (safeAgentIds.length < 1 && safeEntityIds.length === 0) {
      analysisFilterOptions.value = [];
      return;
    }
    const params = {
      agentIds: safeAgentIds,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    if (safeEntityIds.length > 0) {
      params.entityIds = safeEntityIds.join(',');
    }
    const response = await axios.get(`${globalState.apiEndpoint}/api/reports/analysisFilterOptions`, {
      headers: getAuthHeaders(),
      params: params
    });
    const rows = response.data !== undefined && response.data !== null && Array.isArray(response.data.options) === true ? response.data.options : [];
    const normalized = [];
    for (let i = 0; i < rows.length; i = i + 1) {
      const row = rows[i];
      const filterKey = typeof row.filterKey === 'string' ? row.filterKey.trim() : '';
      const value = row.value !== undefined && row.value !== null ? sanitizeTextValue(row.value, 160) : '';
      const count = toNumber(row.count);
      if (analysisFilterKeys.includes(filterKey) === true && value !== '') {
        normalized.push({
          filterKey: filterKey,
          value: value,
          count: count
        });
      }
    }
    analysisFilterOptions.value = normalized;
    if (selectedAnalysisFilterValue.value !== '') {
      let hasSelectedValue = false;
      for (let i = 0; i < normalized.length; i = i + 1) {
        if (normalized[i].filterKey === selectedAnalysisFilterKey.value && normalized[i].value === selectedAnalysisFilterValue.value) {
          hasSelectedValue = true;
        }
      }
      if (hasSelectedValue === false) {
        selectedAnalysisFilterValue.value = '';
      }
    }
  } catch (error) {
    console.error('Error loading analysis filter options:', error);
    handleAuthError(error);
    analysisFilterOptions.value = [];
  } finally {
    isLoadingAnalysisFilterOptions.value = false;
  }
};

const fetchDashboardSummary = async () => {
  isLoadingDashboard.value = true;
  try {
    const startCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[0] : null;
    const endCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[1] : null;
    const startDate = startCandidate !== null ? new Date(startCandidate) : null;
    const endDate = endCandidate !== null ? new Date(endCandidate) : null;
    if (startDate === null || endDate === null || isNaN(startDate.getTime()) === true || isNaN(endDate.getTime()) === true) {
      dashboardSummary.value = null;
      return;
    }
    const safeAgentIds = reportSourceMode.value === 'agents' ? normalizeIdListFlexible(selectedAgents.value, 50, 128, ['agentid', 'agentId', 'id']) : [];
    const safeEntityIds = reportSourceMode.value === 'entities' ? selectedReportEntityIds.value : [];
    const params = {
      agentIds: safeAgentIds,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    if (safeEntityIds.length > 0) {
      params.entityIds = safeEntityIds.join(',');
    }
    if (selectedAnalysisFilterKey.value !== '' && selectedAnalysisFilterValue.value !== '') {
      params.analysisFilterKey = selectedAnalysisFilterKey.value;
      params.analysisFilterValue = selectedAnalysisFilterValue.value;
    }
    const response = await axios.get(`${globalState.apiEndpoint}/api/reports/conversationsAnalysis`, {
      headers: getAuthHeaders(),
      params: params
    });
    const data = response.data;
    if (data === null || data === undefined) {
      dashboardSummary.value = null;
      return;
    }
    const followUps = data.followUps !== undefined && data.followUps !== null ? data.followUps : {};
    const followUpTrue = toNumber(followUps['true']);
    const followUpFalse = toNumber(followUps['false']);
    const followUpTotal = followUpTrue + followUpFalse;
    const followUpRate = followUpTotal > 0 ? Math.round((followUpTrue / followUpTotal) * 100) : 0;
    const resolutions = data.resolutions !== undefined && data.resolutions !== null ? data.resolutions : {};
    const solvedCount = toNumber(resolutions.solved);
    const notSolvedCount = toNumber(resolutions.notSolved);
    const resolutionTotal = solvedCount + notSolvedCount;
    const resolutionRate = resolutionTotal > 0 ? Math.round((solvedCount / resolutionTotal) * 100) : 0;
    const curses = data.curses !== undefined && data.curses !== null ? data.curses : {};
    const curseDetected = toNumber(curses['true']);
    const curseClean = toNumber(curses['false']);
    const curseTotal = curseDetected + curseClean;
    const curseRate = curseTotal > 0 ? Math.round((curseDetected / curseTotal) * 100) : 0;
    const avgNps = data.averageNps !== null && data.averageNps !== undefined ? toNumber(data.averageNps) : 0;
    const languages = data.languages !== undefined && data.languages !== null ? data.languages : {};
    const types = data.types !== undefined && data.types !== null ? data.types : {};
    const rawTopTags = Array.isArray(data.topTags) === true ? data.topTags : [];
    const topTags = rawTopTags.map((tag) => ({ name: tag.name, count: toNumber(tag.count) }));
    topTags.sort((a, b) => b.count - a.count);
    const topTagsTotal = topTags.reduce((sum, tag) => sum + toNumber(tag.count), 0);
    const topTagCount = topTags.length > 0 ? toNumber(topTags[0].count) : 0;
    const top3Count = topTags.slice(0, 3).reduce((sum, tag) => sum + toNumber(tag.count), 0);
    const top5Count = topTags.slice(0, 5).reduce((sum, tag) => sum + toNumber(tag.count), 0);
    const topTagShare = topTagsTotal > 0 ? Math.round((topTagCount / topTagsTotal) * 100) : 0;
    const top3TagShare = topTagsTotal > 0 ? Math.round((top3Count / topTagsTotal) * 100) : 0;
    const top5TagShare = topTagsTotal > 0 ? Math.round((top5Count / topTagsTotal) * 100) : 0;
    const usageInsightsRaw = data.usageInsights !== undefined && data.usageInsights !== null ? data.usageInsights : {};
    const usageHourlyBreakdown = Array.isArray(usageInsightsRaw.hourlyBreakdown) === true ? usageInsightsRaw.hourlyBreakdown.map(item => ({
      hour: toNumber(item.hour),
      totalSessions: toNumber(item.totalSessions),
      chatSessions: toNumber(item.chatSessions),
      callSessions: toNumber(item.callSessions),
      totalMessages: toNumber(item.totalMessages),
      chatMessages: toNumber(item.chatMessages),
      callMessages: toNumber(item.callMessages),
      avgDurationMinutes: toNumber(item.avgDurationMinutes)
    })) : [];
    const usageChannelBreakdown = Array.isArray(usageInsightsRaw.channelBreakdown) === true ? usageInsightsRaw.channelBreakdown.map(item => ({
      channelType: typeof item.channelType === 'string' && item.channelType.trim() !== '' ? item.channelType.trim() : 'unknown',
      usageScope: typeof item.usageScope === 'string' && item.usageScope.trim() !== '' ? item.usageScope.trim() : 'chat',
      totalSessions: toNumber(item.totalSessions),
      totalMessages: toNumber(item.totalMessages),
      avgMessagesPerSession: toNumber(item.avgMessagesPerSession),
      avgDurationMinutes: toNumber(item.avgDurationMinutes)
    })) : [];
    const analysisEngineRaw = data.analysisEngine !== undefined && data.analysisEngine !== null ? data.analysisEngine : {};
    const customAnalysisItems = Array.isArray(analysisEngineRaw.customItems) === true ? analysisEngineRaw.customItems.map(item => ({
      key: typeof item.key === 'string' ? item.key : '',
      name: typeof item.name === 'string' && item.name.trim() !== '' ? item.name.trim() : (typeof item.key === 'string' ? item.key : 'Custom analysis'),
      prompt: typeof item.prompt === 'string' ? item.prompt : '',
      total: toNumber(item.total),
      valueBreakdown: Array.isArray(item.valueBreakdown) === true ? item.valueBreakdown.map(valueItem => ({
        value: formatAnalysisValue(valueItem.value),
        count: toNumber(valueItem.count)
      })) : [],
      samples: Array.isArray(item.samples) === true ? item.samples.map(sample => ({
        value: formatAnalysisValue(sample.value),
        explanation: typeof sample.explanation === 'string' ? sample.explanation : ''
      })) : []
    })) : [];
    dashboardSummary.value = {
      totalSessions: toNumber(data.totalSessions),
      totalQueries: toNumber(data.totalQueries),
      avgMessagesToResolution: toNumber(data.avgMessagesToResolution),
      isChatAbandonedRate: toNumber(data.isChatAbandonedRate),
      followUpRate: followUpRate,
      averageNps: avgNps,
      topTags: topTags,
      topTagsTotal: topTagsTotal,
      topTagShare: topTagShare,
      top3TagShare: top3TagShare,
      top5TagShare: top5TagShare,
      resolutions: { solved: solvedCount, notSolved: notSolvedCount, rate: resolutionRate },
      curses: { detected: curseDetected, clean: curseClean, rate: curseRate },
      followUps: { true: followUpTrue, false: followUpFalse },
      languages: Object.keys(languages).map((key) => ({ label: key, count: languages[key] })),
      types: Object.keys(types).map((key) => ({ label: key, count: types[key] })),
      analysisEngine: {
        selectedStandardItems: Array.isArray(analysisEngineRaw.selectedStandardItems) === true ? analysisEngineRaw.selectedStandardItems : [],
        customItems: customAnalysisItems
      },
      usageInsights: {
        totalSessions: toNumber(usageInsightsRaw.totalSessions),
        chatSessions: toNumber(usageInsightsRaw.chatSessions),
        callSessions: toNumber(usageInsightsRaw.callSessions),
        totalMessages: toNumber(usageInsightsRaw.totalMessages),
        chatMessages: toNumber(usageInsightsRaw.chatMessages),
        callMessages: toNumber(usageInsightsRaw.callMessages),
        avgMessagesPerSession: toNumber(usageInsightsRaw.avgMessagesPerSession),
        avgDurationMinutes: toNumber(usageInsightsRaw.avgDurationMinutes),
        avgChatDurationMinutes: toNumber(usageInsightsRaw.avgChatDurationMinutes),
        avgCallDurationMinutes: toNumber(usageInsightsRaw.avgCallDurationMinutes),
        resolvedSessions: toNumber(usageInsightsRaw.resolvedSessions),
        chatResolvedSessions: toNumber(usageInsightsRaw.chatResolvedSessions),
        callResolvedSessions: toNumber(usageInsightsRaw.callResolvedSessions),
        followUpSessions: toNumber(usageInsightsRaw.followUpSessions),
        chatFollowUpSessions: toNumber(usageInsightsRaw.chatFollowUpSessions),
        callFollowUpSessions: toNumber(usageInsightsRaw.callFollowUpSessions),
        abandonedSessions: toNumber(usageInsightsRaw.abandonedSessions),
        chatAbandonedSessions: toNumber(usageInsightsRaw.chatAbandonedSessions),
        callAbandonedSessions: toNumber(usageInsightsRaw.callAbandonedSessions),
        hourlyBreakdown: usageHourlyBreakdown,
        channelBreakdown: usageChannelBreakdown
      }
    };
  } catch (error) {
    console.error('Error loading dashboard summary:', error);
    handleAuthError(error);
    dashboardSummary.value = null;
  } finally {
    isLoadingDashboard.value = false;
  }
};

onMounted(async () => {
  document.addEventListener('click', handleReportDropdownClickOutside);
  document.addEventListener('click', handleChannelDropdownClickOutside);
  document.addEventListener('keydown', handleReportTableFullscreenKeydown);
  try {
    const data = await getAllAIChats({ includeStyles: false });
    aiAgents.value = Array.isArray(data) === true ? data : [];

    await fetchAvailableTagCollections();
    await fetchAvailableTags();
    await fetchAvailableCustomReports();
    await fetchAvailableEntities();
    await fetchAvailableEntityCollections();

  } catch (error) {
    toast.error(t('reports.toasts.failedToLoadInitialData'));
    console.error('Error loading initial data:', error);
    handleAuthError(error);
  }

  const endDate = new Date();
  const startDate = new Date(new Date().setDate(endDate.getDate() - 7));
  dateRange.value = [startDate, endDate];

  if (isCampaignsReportMode.value === true) {
    await fetchCampaignOptions();
  }

  loadStyle(`
  

    .dp__main input {

    width:100% !important;
  text-align:center;
  padding-right:3rem;


  `);

});

onUnmounted(() => {
  document.removeEventListener('click', handleReportDropdownClickOutside);
  document.removeEventListener('click', handleChannelDropdownClickOutside);
  document.removeEventListener('keydown', handleReportTableFullscreenKeydown);
  setReportTableFullscreenBodyState(false);
  clearReportRecordingUrls();
  clearSelectedConversationRecording();
  if (reportGenerateSocket !== null) {
    reportGenerateSocket.close();
    reportGenerateSocket = null;
  }
});

function mergeDynamicReportHeaders(baseHeaders) {
  const headers = Array.isArray(baseHeaders) === true ? [...baseHeaders] : [];
  const seenValues = new Set(headers.map(header => header.value));
  const dynamicHeaders = Array.isArray(reportHeaders.value) === true ? reportHeaders.value : [];
  for (let i = 0; i < dynamicHeaders.length; i = i + 1) {
    const header = dynamicHeaders[i];
    const value = typeof header.value === 'string' ? header.value : '';
    if (value.startsWith('analysis_') === true && seenValues.has(value) === false) {
      headers.push(header);
      seenValues.add(value);
    }
  }
  return headers;
}

const translatedReportHeaders = computed(() => {
    if (isCampaignsReportMode.value === true) {
        const campaignHeaders = Array.isArray(reportHeaders.value) === true ? reportHeaders.value : [];
        searchFields.value = campaignHeaders.map(header => header.value);
        return campaignHeaders;
    }
    if (isCustomReportSelected.value === true) {
        const customHeaders = Array.isArray(reportHeaders.value) === true ? reportHeaders.value : [];
        searchFields.value = customHeaders.map(header => header.value);
        return customHeaders;
    }
    let headers = [];
    switch (selectedReport.value) {
        case 'chatSessions':
            headers = mergeDynamicReportHeaders(getChatSessionsHeaders());
            break;
        case 'rawConversationsData':
            headers = [
                { text: t('reports.headers.conversationID'), value: 'conversationid', sortable: true },
                { text: t('reports.headers.sessionID'), value: 'sessionid', sortable: true },
                { text: t('reports.headers.messageID'), value: 'messageid', sortable: true },
                { text: getReportTargetHeaderText(), value: 'agentname', sortable: true },
                { text: t('reports.headers.createdAt'), value: 'createdat', sortable: true },
                { text: t('reports.headers.userMessage'), value: 'userquery', sortable: false },
                { text: t('reports.headers.agentResponse'), value: 'response', sortable: false }
            ];
            break;
        case 'conversationSummary':
            headers = [
                { text: t('reports.headers.date'), value: 'date', sortable: true },
                { text: t('reports.headers.totalConversations'), value: 'totalconversations', sortable: true },
                { text: t('reports.headers.totalMessages'), value: 'totalmessages', sortable: true },
                { text: t('reports.headers.avgMessagesPerConversation'), value: 'avgmessagesperconversation', sortable: true },
                { text: t('reports.headers.avgMessagesToResolution'), value: 'avgmessagestoresolution', sortable: true },
                { text: t('reports.headers.abandonedConversationsCount'), value: 'abandonedconversationscount', sortable: true },
                { text: t('reports.headers.abandonedConversationsRate'), value: 'abandonedconversationsrate', sortable: true },
                { text: t('reports.headers.uniqueSessions'), value: 'uniquesessions', sortable: true },
                { text: t('reports.headers.uniqueQueries'), value: 'uniquequeries', sortable: true },
                { text: t('reports.headers.avgResponseLength'), value: 'avgresponselength', sortable: true }
            ];
            break;
        case 'overallConversationSummary':
            headers = [
                { text: t('reports.headers.dateRange'), value: 'daterange', sortable: false },
                { text: t('reports.headers.avgConversationsPerDate'), value: 'avgconversationsperdate', sortable: true },
                { text: t('reports.headers.totalConversations'), value: 'totalconversations', sortable: true },
                { text: t('reports.headers.totalMessages'), value: 'totalmessages', sortable: true },
                { text: t('reports.headers.avgMessagesPerConversation'), value: 'avgmessagesperconversation', sortable: true },
                { text: t('reports.headers.avgMessagesToResolution'), value: 'avgmessagestoresolution', sortable: true },
                { text: t('reports.headers.abandonedConversationsCount'), value: 'abandonedconversationscount', sortable: true },
                { text: t('reports.headers.abandonedConversationsRate'), value: 'abandonedconversationsrate', sortable: true },
                { text: t('reports.headers.uniqueSessions'), value: 'uniquesessions', sortable: true },
                { text: t('reports.headers.uniqueQueries'), value: 'uniquequeries', sortable: true },
                { text: t('reports.headers.avgResponseLength'), value: 'avgresponselength', sortable: true }
            ];
            break;
        case 'clickingReport':
            headers = [
                { text: t('reports.headers.date'), value: 'date', sortable: true },
                { text: t('reports.headers.buttonName', 'Button'), value: 'buttonname', sortable: true },
                { text: t('reports.headers.clicks', 'Clicks'), value: 'clicks', sortable: true },
                { text: t('reports.headers.uniqueVisitors', 'Unique Visitors'), value: 'uniquevisitors', sortable: true },
                { text: t('reports.headers.repeatClicks', 'Repeat Clicks'), value: 'repeatclicks', sortable: true }
            ];
            break;
        case 'clickingDashboard':
            headers = [
                { text: t('reports.headers.hour', 'Hour'), value: 'hour', sortable: true },
                { text: t('reports.headers.clicks', 'Clicks'), value: 'clicks', sortable: true },
                { text: t('reports.headers.uniqueVisitors', 'Unique Visitors'), value: 'uniquevisitors', sortable: true },
                { text: t('reports.headers.returningVisitors', 'Returning Visitors'), value: 'returningvisitors', sortable: true },
                { text: t('reports.headers.returnRatePercentage', 'Return Rate (%)'), value: 'returnratepercentage', sortable: true },
                { text: t('reports.headers.repeatClicks', 'Repeat Clicks'), value: 'repeatclicks', sortable: true },
                { text: t('reports.headers.sessions', 'Sessions'), value: 'sessions', sortable: true }
            ];
            break;
        case 'responseTime':
            headers = [
                { text: t('reports.headers.date'), value: 'date', sortable: true },
                { text: t('reports.headers.totalQueries'), value: 'totalqueries', sortable: true },
                { text: t('reports.headers.avgResponseLength'), value: 'avgresponselength', sortable: true },
                { text: t('reports.headers.minResponseLength'), value: 'minresponselength', sortable: true },
                { text: t('reports.headers.maxResponseLength'), value: 'maxresponselength', sortable: true }
            ];
            break;
        case 'errorRates':
            headers = [
                { text: t('reports.headers.date'), value: 'date', sortable: true },
                { text: t('reports.headers.totalQueries'), value: 'totalqueries', sortable: true },
                { text: t('reports.headers.errorCount'), value: 'errorcount', sortable: true },
                { text: t('reports.headers.errorRatePercentage'), value: 'errorratepercentage', sortable: true }
            ];
            break;
        case 'tagSummary':
            headers = [
                { text: t('reports.headers.tagName'), value: 'tagname', sortable: true },
                { text: t('reports.headers.tagCount'), value: 'tagcount', sortable: true },
                { text: t('reports.headers.uniqueSessions'), value: 'uniquesessions', sortable: true }
            ];
            break;
        case 'tagSummaryByDay':
            headers = [
                { text: t('reports.headers.date'), value: 'date', sortable: true },
                { text: t('reports.headers.tagName'), value: 'tagname', sortable: true },
                { text: t('reports.headers.tagCount'), value: 'tagcount', sortable: true },
                { text: t('reports.headers.uniqueSessions'), value: 'uniquesessions', sortable: true }
            ];
            break;
        default:
            headers = Array.isArray(reportHeaders.value) === true ? reportHeaders.value : [];
    }
    searchFields.value = headers.map(header => header.value);
    return headers;
});

const visibleReportHeaders = computed(() => {
  const headers = Array.isArray(translatedReportHeaders.value) === true ? translatedReportHeaders.value : [];
  const visibleHeaders = headers.filter(header => reportFieldVisibility.value[header.value] !== false);
  if (isScorecardReport.value === true) {
    return enhanceScorecardHeaders(visibleHeaders);
  }
  return enhanceReportHeaders(visibleHeaders);
});

const visibleSearchFields = computed(() => visibleReportHeaders.value.map(header => header.value));

const filteredReportData = computed(() => {
  const rows = Array.isArray(reportData.value) === true ? reportData.value : [];
  const searchTerm = typeof conversationIdSearchValue.value === 'string' ? conversationIdSearchValue.value.trim().toLowerCase() : '';
  if (isConversationIdSearchAvailable.value !== true || searchTerm === '') {
    return rows;
  }
  const filteredRows = [];
  for (let index = 0; index < rows.length; index = index + 1) {
    const row = rows[index];
    const conversationId = row !== null && row !== undefined && row.conversationid !== undefined && row.conversationid !== null ? String(row.conversationid).toLowerCase() : '';
    if (conversationId.includes(searchTerm) === true) {
      filteredRows.push(row);
    }
  }
  return filteredRows;
});

function getReportCellExplanation(row, fieldValue) {
  if (row === null || row === undefined || typeof row !== 'object') {
    return '';
  }
  if (typeof fieldValue !== 'string' || fieldValue.trim() === '') {
    return '';
  }
  const explanation = row[`${fieldValue}Explanation`];
  if (typeof explanation === 'string' && explanation.trim() !== '') {
    return explanation.trim();
  }
  return '';
}

function hasReportCellExplanation(fieldValue) {
  const rows = Array.isArray(reportData.value) === true ? reportData.value : [];
  for (let index = 0; index < rows.length; index = index + 1) {
    if (getReportCellExplanation(rows[index], fieldValue) !== '') {
      return true;
    }
  }
  return false;
}

function isScorecardAnswerField(fieldValue) {
  if (isScorecardReport.value !== true) {
    return false;
  }
  if (typeof fieldValue !== 'string' || fieldValue.trim() === '') {
    return false;
  }
  const excludedFields = ['rowindex', 'date', 'subject', 'sessionid', 'channel', 'tags', 'totalScore', 'summary'];
  return excludedFields.includes(fieldValue) === false;
}

const reportExplanationHeaders = computed(() => {
  const headers = Array.isArray(visibleReportHeaders.value) === true ? visibleReportHeaders.value : [];
  const output = [];
  for (let index = 0; index < headers.length; index = index + 1) {
    const header = headers[index];
    const value = typeof header.value === 'string' ? header.value : '';
    if (value === '') {
      continue;
    }
    if (value === 'tags') {
      continue;
    }
    if (hasReportCellExplanation(value) === true || isScorecardAnswerField(value) === true) {
      output.push({
        ...header,
        slotName: `item-${value}`
      });
    }
  }
  return output;
});

function isReportFieldVisible(fieldValue) {
  if (typeof fieldValue !== 'string' || fieldValue.trim() === '') {
    return false;
  }
  return reportFieldVisibility.value[fieldValue] !== false;
}

function toggleReportField(fieldValue) {
  if (typeof fieldValue !== 'string' || fieldValue.trim() === '') {
    return;
  }
  const nextValue = reportFieldVisibility.value[fieldValue] === false;
  reportFieldVisibility.value = {
    ...reportFieldVisibility.value,
    [fieldValue]: nextValue
  };
}

function showAllReportFields() {
  reportFieldVisibility.value = {};
}

function setReportTableFullscreenBodyState(isActive) {
  if (typeof document === 'undefined') {
    return;
  }
  if (document.body === null) {
    return;
  }
  if (isActive === true) {
    document.body.classList.add(reportTableFullscreenBodyClass);
    return;
  }
  document.body.classList.remove(reportTableFullscreenBodyClass);
}

function handleReportTableFullscreenKeydown(event) {
  if (isReportTableFullscreenOpen.value !== true) {
    return;
  }
  if (event.key !== 'Escape') {
    return;
  }
  closeReportTableFullscreen();
}

function openReportTableFullscreen() {
  if (Array.isArray(reportData.value) === false || reportData.value.length === 0) {
    return;
  }
  isReportTableFullscreenOpen.value = true;
  setReportTableFullscreenBodyState(true);
}

function closeReportTableFullscreen() {
  isReportTableFullscreenOpen.value = false;
  setReportTableFullscreenBodyState(false);
}

const updateReportHeaders = () => {
  switch (selectedReport.value) {
    case 'chatSessions':
      reportHeaders.value = getChatSessionsHeaders();
      break;
    case 'rawConversationsData':
      reportHeaders.value = [
        { text: t('reports.headers.conversationID'), value: 'conversationid', sortable: true },
        { text: t('reports.headers.sessionID'), value: 'sessionid', sortable: true },
        { text: t('reports.headers.messageID'), value: 'messageid', sortable: true },
        { text: getReportTargetHeaderText(), value: 'agentname', sortable: true },
        { text: t('reports.headers.createdAt'), value: 'createdat', sortable: true },
        { text: t('reports.headers.userMessage'), value: 'userquery', sortable: false },
        { text: t('reports.headers.agentResponse'), value: 'response', sortable: false }
      ];
      break;
    case 'conversationSummary':
      reportHeaders.value = [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Total Conversations', value: 'totalconversations', sortable: true },
        { text: 'Unique Sessions', value: 'uniquesessions', sortable: true },
        { text: 'Unique Queries', value: 'uniquequeries', sortable: true },
        { text: 'Avg Response Length', value: 'avgresponselength', sortable: true }
      ];
      break;
    case 'overallConversationSummary':
      reportHeaders.value = [
        { text: t('reports.headers.dateRange'), value: 'daterange', sortable: false },
        { text: t('reports.headers.avgConversationsPerDate'), value: 'avgconversationsperdate', sortable: true },
        { text: t('reports.headers.totalConversations'), value: 'totalconversations', sortable: true },
        { text: t('reports.headers.totalMessages'), value: 'totalmessages', sortable: true },
        { text: t('reports.headers.avgMessagesPerConversation'), value: 'avgmessagesperconversation', sortable: true },
        { text: t('reports.headers.avgMessagesToResolution'), value: 'avgmessagestoresolution', sortable: true },
        { text: t('reports.headers.abandonedConversationsCount'), value: 'abandonedconversationscount', sortable: true },
        { text: t('reports.headers.abandonedConversationsRate'), value: 'abandonedconversationsrate', sortable: true },
        { text: t('reports.headers.uniqueSessions'), value: 'uniquesessions', sortable: true },
        { text: t('reports.headers.uniqueQueries'), value: 'uniquequeries', sortable: true },
        { text: t('reports.headers.avgResponseLength'), value: 'avgresponselength', sortable: true }
      ];
      break;
    case 'clickingReport':
      reportHeaders.value = [
        { text: t('reports.headers.date'), value: 'date', sortable: true },
        { text: t('reports.headers.buttonName', 'Button'), value: 'buttonname', sortable: true },
        { text: t('reports.headers.clicks', 'Clicks'), value: 'clicks', sortable: true },
        { text: t('reports.headers.uniqueVisitors', 'Unique Visitors'), value: 'uniquevisitors', sortable: true },
        { text: t('reports.headers.repeatClicks', 'Repeat Clicks'), value: 'repeatclicks', sortable: true }
      ];
      break;
    case 'clickingDashboard':
      reportHeaders.value = [
        { text: t('reports.headers.hour', 'Hour'), value: 'hour', sortable: true },
        { text: t('reports.headers.clicks', 'Clicks'), value: 'clicks', sortable: true },
        { text: t('reports.headers.uniqueVisitors', 'Unique Visitors'), value: 'uniquevisitors', sortable: true },
        { text: t('reports.headers.returningVisitors', 'Returning Visitors'), value: 'returningvisitors', sortable: true },
        { text: t('reports.headers.returnRatePercentage', 'Return Rate (%)'), value: 'returnratepercentage', sortable: true },
        { text: t('reports.headers.repeatClicks', 'Repeat Clicks'), value: 'repeatclicks', sortable: true },
        { text: t('reports.headers.sessions', 'Sessions'), value: 'sessions', sortable: true }
      ];
      break;
    case 'responseTime':
      reportHeaders.value = [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Total Queries', value: 'total_queries', sortable: true },
        { text: 'Avg Response Length', value: 'avg_response_length', sortable: true },
        { text: 'Min Response Length', value: 'min_response_length', sortable: true },
        { text: 'Max Response Length', value: 'max_response_length', sortable: true }
      ];
      break;
    case 'errorRates':
      reportHeaders.value = [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Total Queries', value: 'total_queries', sortable: true },
        { text: 'Error Count', value: 'error_count', sortable: true },
        { text: 'Error Rate (%)', value: 'error_rate_percentage', sortable: true }
      ];
      break;
    case 'tagSummary':
      reportHeaders.value = [
        { text: 'Tag', value: 'tagname', sortable: true },
        { text: 'Tag Count', value: 'tagcount', sortable: true },
        { text: 'Unique Sessions', value: 'uniquesessions', sortable: true }
      ];
      break;
    case 'tagSummaryByDay':
      reportHeaders.value = [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Tag', value: 'tagname', sortable: true },
        { text: 'Tag Count', value: 'tagcount', sortable: true },
        { text: 'Unique Sessions', value: 'uniquesessions', sortable: true }
      ];
      break;
    default:
      reportHeaders.value = [];
  }
};

watch(selectedReport, updateReportHeaders);

watch(isStandardSubscription, (nextValue) => {
  if (nextValue === true && selectedReport.value === 'rawConversationsData') {
    selectedReport.value = 'chatSessions';
  }
}, { immediate: true });

watch(isEnterpriseSubscription, () => {
  clearReportRecordingUrls();
  if (selectedReport.value === 'chatSessions') {
    updateReportHeaders();
  }
}, { immediate: true });

watch(selectedReport, () => {
  if (selectedReport.value === 'clickingReport' || selectedReport.value === 'clickingDashboard') {
    reportSourceMode.value = 'agents';
    selectedAnalysisFilterKey.value = '';
    selectedAnalysisFilterValue.value = '';
    analysisFilterOptions.value = [];
    selectedTagIds.value = [];
    selectedTagCollectionId.value = '';
  }
  clearReportRecordingUrls();
  reportData.value = [];
  reportHeaders.value = [];
  searchFields.value = [];
  closeReportTableFullscreen();
  conversationAnalysisData.value = null;
  conversationHistory.value = [];
  dashboardSummary.value = null;
  selectedUsageScope.value = 'all';
  selectedAnalysisFilterKey.value = '';
  selectedAnalysisFilterValue.value = '';
  conversationIdSearchValue.value = '';
  analysisFilterOptions.value = [];
  isSourcesModalOpen.value = false;
  sourcesSessionId.value = '';
  sourcesMessageId.value = '';
  selectedChannelType.value = '';
  selectedSubAgentConversationFilter.value = 'exclude';
  isChannelDropdownOpen.value = false;
  reportTypeSearchValue.value = '';
  if (isCustomReportSelected.value === true) {
    selectedTagCollectionId.value = '';
    selectedTagIds.value = [];
  }
  if (selectedReportAllowsDateRange.value === false) {
    selectedPreset.value = '';
  }
});

watch(selectedCampaignReport, () => {
  reportData.value = [];
  reportHeaders.value = [];
  searchFields.value = [];
  reportFieldVisibility.value = {};
  dashboardSummary.value = null;
});

watch(selectedTagCollectionId, async (nextValue, prevValue) => {
  if (nextValue !== prevValue) {
    selectedTagIds.value = [];
    await fetchAvailableTags();
  }
});

watch(selectedAnalysisFilterKey, async (nextValue, prevValue) => {
  if (nextValue !== prevValue) {
    selectedAnalysisFilterValue.value = '';
    if (nextValue !== '') {
      await fetchAnalysisFilterOptions();
    }
  }
});

watch(() => route.query.type, async (nextType) => {
  const reportType = typeof nextType === 'string' ? nextType.trim().toLowerCase() : '';
  if (reportType === 'campaigns') {
    await fetchCampaignOptions();
    reportData.value = [];
    reportHeaders.value = [];
    searchFields.value = [];
    dashboardSummary.value = null;
    return;
  }
  selectedCampaignIds.value = [];
});

watch(reportSourceMode, (nextValue) => {
  if (nextValue === 'agents') {
    selectedEntityIds.value = [];
    selectedEntityCollectionIds.value = [];
  }
  if (nextValue === 'entities') {
    selectedAgents.value = [];
  }
  clearReportRecordingUrls();
  reportData.value = [];
  reportHeaders.value = [];
  searchFields.value = [];
  closeReportTableFullscreen();
  dashboardSummary.value = null;
  analysisFilterOptions.value = [];
  selectedAnalysisFilterValue.value = '';
});

watch([selectedAgents, selectedEntityIds, selectedEntityCollectionIds, dateRange, reportSourceMode], async () => {
  analysisFilterOptions.value = [];
  selectedAnalysisFilterValue.value = '';
  if (selectedAnalysisFilterKey.value !== '' && selectedReport.value !== '' && selectedReportSupportsAnalysisFilter.value === true) {
    await fetchAnalysisFilterOptions();
  }
}, { deep: true });

function parseCampaignReportProviderResponse(value) {
  if (value === undefined || value === null) {
    return {};
  }
  if (typeof value === 'object') {
    return value;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed !== undefined && parsed !== null && typeof parsed === 'object') {
        return parsed;
      }
    } catch (error) {
      return {};
    }
  }
  return {};
}

function readCampaignReportProviderValue(providerResponse, keys) {
  const response = providerResponse !== undefined && providerResponse !== null && typeof providerResponse === 'object' ? providerResponse : {};
  const names = Array.isArray(keys) === true ? keys : [];
  for (let index = 0; index < names.length; index = index + 1) {
    const key = names[index];
    const value = response[key];
    if (value !== undefined && value !== null && String(value).trim().length > 0) {
      return String(value).trim();
    }
  }
  return '';
}

function formatCampaignReportDuration(value) {
  const raw = String(value !== undefined && value !== null ? value : '').trim();
  if (raw.length === 0) {
    return '';
  }
  const seconds = Number(raw);
  if (Number.isFinite(seconds) === true) {
    if (seconds <= 0) {
      return '';
    }
    return t('campaigns.stats.durationSeconds', '{seconds}s', { seconds });
  }
  return raw;
}

function buildCampaignReportHeaders(reportType) {
  if (reportType === 'performanceSummary') {
    return [
      { text: t('reports.campaignsReport.headers.campaignName', 'Campaign name'), value: 'campaignName' },
      { text: t('reports.campaignsReport.headers.totalRecipients', 'Recipients'), value: 'totalRecipients' },
      { text: t('reports.campaignsReport.headers.totalAttempts', 'Attempts'), value: 'totalAttempts' },
      { text: t('reports.campaignsReport.headers.sent', 'Sent'), value: 'sent' },
      { text: t('reports.campaignsReport.headers.called', 'Called'), value: 'called' },
      { text: t('reports.campaignsReport.headers.queued', 'Queued'), value: 'queued' },
      { text: t('reports.campaignsReport.headers.failed', 'Failed'), value: 'failed' },
      { text: t('reports.campaignsReport.headers.aiResponses', 'AI responses'), value: 'aiResponses' },
      { text: t('reports.campaignsReport.headers.averageDuration', 'Avg duration'), value: 'averageDuration' },
      { text: t('reports.campaignsReport.headers.successRate', 'Success rate'), value: 'successRate' },
      { text: t('reports.campaignsReport.headers.failureRate', 'Failure rate'), value: 'failureRate' }
    ];
  }
  if (reportType === 'callOutcomes') {
    return [
      { text: t('reports.campaignsReport.headers.campaignName', 'Campaign name'), value: 'campaignName' },
      { text: t('reports.campaignsReport.headers.outcome', 'Outcome'), value: 'outcome' },
      { text: t('reports.campaignsReport.headers.totalAttempts', 'Attempts'), value: 'totalAttempts' },
      { text: t('reports.campaignsReport.headers.rate', 'Rate'), value: 'rate' },
      { text: t('reports.campaignsReport.headers.averageDuration', 'Avg duration'), value: 'averageDuration' },
      { text: t('reports.campaignsReport.headers.aiResponses', 'AI responses'), value: 'aiResponses' },
      { text: t('reports.campaignsReport.headers.errors', 'Errors'), value: 'errors' }
    ];
  }
  if (reportType === 'aiResponses') {
    return [
      { text: t('reports.campaignsReport.headers.campaignName', 'Campaign name'), value: 'campaignName' },
      { text: t('reports.campaignsReport.headers.recipient', 'Recipient'), value: 'recipient' },
      { text: t('reports.campaignsReport.headers.aiResponse', 'AI response'), value: 'aiResponse' },
      { text: t('reports.campaignsReport.headers.duration', 'Duration'), value: 'duration' },
      { text: t('reports.campaignsReport.headers.deliveryStatus', 'Delivery status'), value: 'deliveryStatus' },
      { text: t('reports.campaignsReport.headers.createdAt', 'Date'), value: 'createdAt' }
    ];
  }
  if (reportType === 'failedDeliveries') {
    return [
      { text: t('reports.campaignsReport.headers.campaignName', 'Campaign name'), value: 'campaignName' },
      { text: t('reports.campaignsReport.headers.recipient', 'Recipient'), value: 'recipient' },
      { text: t('reports.campaignsReport.headers.deliveryStatus', 'Delivery status'), value: 'deliveryStatus' },
      { text: t('reports.campaignsReport.headers.providerStatus', 'Provider status'), value: 'providerStatus' },
      { text: t('reports.campaignsReport.headers.errorMessage', 'Error'), value: 'errorMessage' },
      { text: t('reports.campaignsReport.headers.createdAt', 'Date'), value: 'createdAt' }
    ];
  }
  if (reportType === 'recipientActivity') {
    return [
      { text: t('reports.campaignsReport.headers.campaignName', 'Campaign name'), value: 'campaignName' },
      { text: t('reports.campaignsReport.headers.recipient', 'Recipient'), value: 'recipient' },
      { text: t('reports.campaignsReport.headers.totalAttempts', 'Attempts'), value: 'totalAttempts' },
      { text: t('reports.campaignsReport.headers.lastStatus', 'Last status'), value: 'lastStatus' },
      { text: t('reports.campaignsReport.headers.lastCalledAt', 'Last called'), value: 'lastCalledAt' },
      { text: t('reports.campaignsReport.headers.duration', 'Duration'), value: 'duration' },
      { text: t('reports.campaignsReport.headers.aiResponse', 'AI response'), value: 'aiResponse' },
      { text: t('reports.campaignsReport.headers.errorMessage', 'Error'), value: 'errorMessage' }
    ];
  }
  return [
    { text: t('reports.campaignsReport.headers.campaignName', 'Campaign name'), value: 'campaignName' },
    { text: t('reports.campaignsReport.headers.recipient', 'Recipient'), value: 'recipient' },
    { text: t('reports.campaignsReport.headers.channel', 'Channel'), value: 'channel' },
    { text: t('reports.campaignsReport.headers.sendMethod', 'Method'), value: 'sendMethod' },
    { text: t('reports.campaignsReport.headers.deliveryStatus', 'Delivery status'), value: 'deliveryStatus' },
    { text: t('reports.campaignsReport.headers.providerStatus', 'Provider status'), value: 'providerStatus' },
    { text: t('reports.campaignsReport.headers.duration', 'Duration'), value: 'duration' },
    { text: t('reports.campaignsReport.headers.aiResponse', 'AI response'), value: 'aiResponse' },
    { text: t('reports.campaignsReport.headers.errorMessage', 'Error'), value: 'errorMessage' },
    { text: t('reports.campaignsReport.headers.createdAt', 'Date'), value: 'createdAt' }
  ];
}

function getCampaignOptionById(campaignId) {
  const safeId = typeof campaignId === 'string' ? campaignId.trim() : String(campaignId !== undefined && campaignId !== null ? campaignId : '');
  const options = Array.isArray(campaignOptions.value) === true ? campaignOptions.value : [];
  for (let index = 0; index < options.length; index = index + 1) {
    const option = options[index];
    if (option !== null && option !== undefined && String(option.campaignId) === safeId) {
      return option;
    }
  }
  return null;
}

function isCampaignLogInDateRange(createdAt, startDate, endDate) {
  if (createdAt === undefined || createdAt === null || createdAt === '') {
    return false;
  }
  const created = new Date(createdAt);
  if (isNaN(created.getTime()) === true) {
    return false;
  }
  return created.getTime() >= startDate.getTime() && created.getTime() <= endDate.getTime();
}

function readCampaignReportDurationSeconds(providerResponse) {
  const duration = readCampaignReportProviderValue(providerResponse, ['duration', 'callDuration', 'Duration']);
  const seconds = Number(duration);
  if (Number.isFinite(seconds) !== true || seconds <= 0) {
    return 0;
  }
  return seconds;
}

function readCampaignReportAiResponse(providerResponse) {
  const answer = readCampaignReportProviderValue(providerResponse, ['aiResponse', 'responsePromptAnswer', 'response', 'extractedResponse']);
  if (answer.length > 0) {
    return answer;
  }
  const status = readCampaignReportProviderValue(providerResponse, ['responsePromptStatus']);
  if (status.length === 0) {
    return '';
  }
  const errorText = readCampaignReportProviderValue(providerResponse, ['responsePromptError']);
  if (status === 'processed') {
    return t('campaigns.responsePromptStatus.processed', 'Processed');
  }
  if (status === 'answered') {
    return t('campaigns.responsePromptStatus.answered', 'Answered');
  }
  if (status === 'noTranscript') {
    return t('campaigns.responsePromptStatus.noTranscript', 'No transcript');
  }
  if (status === 'failed') {
    if (errorText.length > 0) {
      return t('campaigns.responsePromptStatus.failedWithError', 'Failed: {error}', { error: errorText });
    }
    return t('campaigns.responsePromptStatus.failed', 'Failed');
  }
  return status;
}

function formatCampaignReportPercent(value, total) {
  const safeValue = Number(value);
  const safeTotal = Number(total);
  if (Number.isFinite(safeValue) !== true || Number.isFinite(safeTotal) !== true || safeTotal <= 0) {
    return '0%';
  }
  const percent = Math.round((safeValue / safeTotal) * 1000) / 10;
  return `${percent}%`;
}

function normalizeCampaignReportStatus(value) {
  const raw = String(value !== undefined && value !== null ? value : '').trim();
  if (raw.length === 0) {
    return t('reports.campaignsReport.statuses.unknown', 'Unknown');
  }
  const lower = raw.toLowerCase();
  if (lower === 'called') {
    return t('campaigns.cards.called', 'Called');
  }
  if (lower === 'sent') {
    return t('campaigns.cards.sent', 'Sent');
  }
  if (lower === 'queued' || lower === 'waiting') {
    return t('campaigns.cards.queued', 'Queued');
  }
  if (lower === 'failed') {
    return t('campaigns.cards.failed', 'Failed');
  }
  if (lower === 'skipped') {
    return t('campaigns.runtimeStatus.skipped', 'Skipped');
  }
  return raw;
}

function buildCampaignDeliveryLogRow(campaignName, log) {
  const providerResponse = parseCampaignReportProviderResponse(log !== undefined && log !== null ? log.providerresponse : {});
  const duration = readCampaignReportProviderValue(providerResponse, ['duration', 'callDuration', 'Duration']);
  return {
    campaignName: campaignName,
    recipient: log !== undefined && log !== null ? String(log.recipient !== undefined && log.recipient !== null ? log.recipient : '') : '',
    channel: log !== undefined && log !== null ? String(log.channel !== undefined && log.channel !== null ? log.channel : '') : '',
    sendMethod: log !== undefined && log !== null ? String(log.sendmethod !== undefined && log.sendmethod !== null ? log.sendmethod : '') : '',
    deliveryStatus: log !== undefined && log !== null ? String(log.deliverystatus !== undefined && log.deliverystatus !== null ? log.deliverystatus : '') : '',
    providerStatus: log !== undefined && log !== null ? String(log.providerstatus !== undefined && log.providerstatus !== null ? log.providerstatus : '') : '',
    duration: formatCampaignReportDuration(duration),
    aiResponse: readCampaignReportAiResponse(providerResponse),
    errorMessage: log !== undefined && log !== null ? String(log.errormessage !== undefined && log.errormessage !== null ? log.errormessage : '') : '',
    createdAt: formatDateTimeForViewer(log !== undefined && log !== null ? log.createdat : '')
  };
}

function buildCampaignPerformanceSummaryRows(campaignReports) {
  const reports = Array.isArray(campaignReports) === true ? campaignReports : [];
  const rows = [];
  for (let index = 0; index < reports.length; index = index + 1) {
    const report = reports[index];
    const logs = Array.isArray(report.logs) === true ? report.logs : [];
    const recipientMap = {};
    let totalDuration = 0;
    let durationCount = 0;
    let aiResponses = 0;
    let sent = 0;
    let called = 0;
    let queued = 0;
    let failed = 0;
    for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
      const log = logs[logIndex];
      const recipient = log !== undefined && log !== null ? String(log.recipient !== undefined && log.recipient !== null ? log.recipient : '').trim() : '';
      if (recipient.length > 0) {
        recipientMap[recipient] = true;
      }
      const providerResponse = parseCampaignReportProviderResponse(log !== undefined && log !== null ? log.providerresponse : {});
      const durationSeconds = readCampaignReportDurationSeconds(providerResponse);
      if (durationSeconds > 0) {
        totalDuration = totalDuration + durationSeconds;
        durationCount = durationCount + 1;
      }
      if (readCampaignReportAiResponse(providerResponse).length > 0) {
        aiResponses = aiResponses + 1;
      }
      const status = log !== undefined && log !== null ? String(log.deliverystatus !== undefined && log.deliverystatus !== null ? log.deliverystatus : '').trim().toLowerCase() : '';
      if (status === 'sent') {
        sent = sent + 1;
      }
      if (status === 'called') {
        called = called + 1;
      }
      if (status === 'queued' || status === 'waiting') {
        queued = queued + 1;
      }
      if (status === 'failed') {
        failed = failed + 1;
      }
    }
    const totalRecipients = Object.keys(recipientMap).length;
    const total = totalRecipients > 0 ? totalRecipients : logs.length;
    const averageDuration = durationCount > 0 ? formatCampaignReportDuration(Math.round(totalDuration / durationCount)) : '';
    rows.push({
      campaignName: report.campaignName,
      totalRecipients: total,
      totalAttempts: logs.length,
      sent,
      called,
      queued,
      failed,
      aiResponses,
      averageDuration,
      successRate: formatCampaignReportPercent(called + sent, total),
      failureRate: formatCampaignReportPercent(failed, total)
    });
  }
  return rows;
}

function ensureCampaignOutcomeBucket(map, campaignName, outcome) {
  const key = `${campaignName}::${outcome}`;
  if (map[key] === undefined) {
    map[key] = {
      campaignName,
      outcome,
      totalAttempts: 0,
      totalDuration: 0,
      durationCount: 0,
      aiResponses: 0,
      errors: 0,
      campaignAttempts: 0
    };
  }
  return map[key];
}

function buildCampaignCallOutcomeRows(campaignReports) {
  const reports = Array.isArray(campaignReports) === true ? campaignReports : [];
  const rows = [];
  for (let reportIndex = 0; reportIndex < reports.length; reportIndex = reportIndex + 1) {
    const report = reports[reportIndex];
    const logs = Array.isArray(report.logs) === true ? report.logs : [];
    const map = {};
    for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
      const log = logs[logIndex];
      const outcome = normalizeCampaignReportStatus(log !== undefined && log !== null ? log.deliverystatus : '');
      const bucket = ensureCampaignOutcomeBucket(map, report.campaignName, outcome);
      const providerResponse = parseCampaignReportProviderResponse(log !== undefined && log !== null ? log.providerresponse : {});
      const durationSeconds = readCampaignReportDurationSeconds(providerResponse);
      const errorMessage = log !== undefined && log !== null ? String(log.errormessage !== undefined && log.errormessage !== null ? log.errormessage : '').trim() : '';
      bucket.totalAttempts = bucket.totalAttempts + 1;
      bucket.campaignAttempts = logs.length;
      if (durationSeconds > 0) {
        bucket.totalDuration = bucket.totalDuration + durationSeconds;
        bucket.durationCount = bucket.durationCount + 1;
      }
      if (readCampaignReportAiResponse(providerResponse).length > 0) {
        bucket.aiResponses = bucket.aiResponses + 1;
      }
      if (errorMessage.length > 0) {
        bucket.errors = bucket.errors + 1;
      }
    }
    const keys = Object.keys(map);
    for (let index = 0; index < keys.length; index = index + 1) {
      const bucket = map[keys[index]];
      rows.push({
        campaignName: bucket.campaignName,
        outcome: bucket.outcome,
        totalAttempts: bucket.totalAttempts,
        rate: formatCampaignReportPercent(bucket.totalAttempts, bucket.campaignAttempts),
        averageDuration: bucket.durationCount > 0 ? formatCampaignReportDuration(Math.round(bucket.totalDuration / bucket.durationCount)) : '',
        aiResponses: bucket.aiResponses,
        errors: bucket.errors
      });
    }
  }
  return rows;
}

function isCampaignReportFailedLog(log, providerResponse) {
  const deliveryStatus = log !== undefined && log !== null ? String(log.deliverystatus !== undefined && log.deliverystatus !== null ? log.deliverystatus : '').trim().toLowerCase() : '';
  if (deliveryStatus === 'failed') {
    return true;
  }
  const providerStatus = log !== undefined && log !== null ? Number(log.providerstatus) : 0;
  if (Number.isFinite(providerStatus) === true && providerStatus >= 400) {
    return true;
  }
  const errorMessage = log !== undefined && log !== null ? String(log.errormessage !== undefined && log.errormessage !== null ? log.errormessage : '').trim() : '';
  if (errorMessage.length > 0) {
    return true;
  }
  const providerError = readCampaignReportProviderValue(providerResponse, ['error', 'errorMessage', 'message']);
  return providerError.length > 0;
}

function buildCampaignAiResponseRows(campaignReports) {
  const reports = Array.isArray(campaignReports) === true ? campaignReports : [];
  const rows = [];
  for (let reportIndex = 0; reportIndex < reports.length; reportIndex = reportIndex + 1) {
    const report = reports[reportIndex];
    const logs = Array.isArray(report.logs) === true ? report.logs : [];
    for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
      const log = logs[logIndex];
      const row = buildCampaignDeliveryLogRow(report.campaignName, log);
      if (String(row.aiResponse).trim().length === 0) {
        continue;
      }
      rows.push({
        campaignName: row.campaignName,
        recipient: row.recipient,
        aiResponse: row.aiResponse,
        duration: row.duration,
        deliveryStatus: row.deliveryStatus,
        createdAt: row.createdAt
      });
    }
  }
  return rows;
}

function buildCampaignFailedDeliveryRows(campaignReports) {
  const reports = Array.isArray(campaignReports) === true ? campaignReports : [];
  const rows = [];
  for (let reportIndex = 0; reportIndex < reports.length; reportIndex = reportIndex + 1) {
    const report = reports[reportIndex];
    const logs = Array.isArray(report.logs) === true ? report.logs : [];
    for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
      const log = logs[logIndex];
      const providerResponse = parseCampaignReportProviderResponse(log !== undefined && log !== null ? log.providerresponse : {});
      if (isCampaignReportFailedLog(log, providerResponse) !== true) {
        continue;
      }
      const row = buildCampaignDeliveryLogRow(report.campaignName, log);
      rows.push({
        campaignName: row.campaignName,
        recipient: row.recipient,
        deliveryStatus: row.deliveryStatus,
        providerStatus: row.providerStatus,
        errorMessage: row.errorMessage,
        createdAt: row.createdAt
      });
    }
  }
  return rows;
}

function buildCampaignRecipientActivityRows(campaignReports) {
  const reports = Array.isArray(campaignReports) === true ? campaignReports : [];
  const rows = [];
  for (let reportIndex = 0; reportIndex < reports.length; reportIndex = reportIndex + 1) {
    const report = reports[reportIndex];
    const logs = Array.isArray(report.logs) === true ? report.logs : [];
    const map = {};
    for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
      const log = logs[logIndex];
      const recipient = log !== undefined && log !== null ? String(log.recipient !== undefined && log.recipient !== null ? log.recipient : '').trim() : '';
      if (recipient.length === 0) {
        continue;
      }
      const key = recipient;
      if (map[key] === undefined) {
        map[key] = {
          campaignName: report.campaignName,
          recipient,
          totalAttempts: 0,
          lastStatus: '',
          lastCalledAtRaw: '',
          duration: '',
          aiResponse: '',
          errorMessage: ''
        };
      }
      const row = map[key];
      const deliveryRow = buildCampaignDeliveryLogRow(report.campaignName, log);
      const createdAt = log !== undefined && log !== null ? String(log.createdat !== undefined && log.createdat !== null ? log.createdat : '') : '';
      const currentTime = new Date(createdAt).getTime();
      const lastTime = new Date(row.lastCalledAtRaw).getTime();
      row.totalAttempts = row.totalAttempts + 1;
      if (row.duration.length === 0 && deliveryRow.duration.length > 0) {
        row.duration = deliveryRow.duration;
      }
      if (row.aiResponse.length === 0 && deliveryRow.aiResponse.length > 0) {
        row.aiResponse = deliveryRow.aiResponse;
      }
      if (row.errorMessage.length === 0 && deliveryRow.errorMessage.length > 0) {
        row.errorMessage = deliveryRow.errorMessage;
      }
      if (Number.isNaN(lastTime) === true || (Number.isNaN(currentTime) !== true && currentTime >= lastTime)) {
        row.lastStatus = deliveryRow.deliveryStatus;
        row.lastCalledAtRaw = createdAt;
      }
    }
    const keys = Object.keys(map);
    for (let index = 0; index < keys.length; index = index + 1) {
      const row = map[keys[index]];
      rows.push({
        campaignName: row.campaignName,
        recipient: row.recipient,
        totalAttempts: row.totalAttempts,
        lastStatus: row.lastStatus,
        lastCalledAt: formatDateTimeForViewer(row.lastCalledAtRaw),
        duration: row.duration,
        aiResponse: row.aiResponse,
        errorMessage: row.errorMessage
      });
    }
  }
  return rows;
}

function buildCampaignReportRows(reportType, campaignReports) {
  if (reportType === 'performanceSummary') {
    return buildCampaignPerformanceSummaryRows(campaignReports);
  }
  if (reportType === 'callOutcomes') {
    return buildCampaignCallOutcomeRows(campaignReports);
  }
  if (reportType === 'aiResponses') {
    return buildCampaignAiResponseRows(campaignReports);
  }
  if (reportType === 'failedDeliveries') {
    return buildCampaignFailedDeliveryRows(campaignReports);
  }
  if (reportType === 'recipientActivity') {
    return buildCampaignRecipientActivityRows(campaignReports);
  }
  const reports = Array.isArray(campaignReports) === true ? campaignReports : [];
  const rows = [];
  for (let reportIndex = 0; reportIndex < reports.length; reportIndex = reportIndex + 1) {
    const report = reports[reportIndex];
    const logs = Array.isArray(report.logs) === true ? report.logs : [];
    for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
      rows.push(buildCampaignDeliveryLogRow(report.campaignName, logs[logIndex]));
    }
  }
  return rows;
}

const fetchCampaignOptions = async () => {
  isLoadingCampaigns.value = true;
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/campaigns/`, {
      headers: getAuthHeaders()
    });
    const rows = Array.isArray(response.data) === true ? response.data : [];
    const options = [];
    for (let index = 0; index < rows.length; index = index + 1) {
      const row = rows[index];
      if (row === null || row === undefined || typeof row !== 'object') {
        continue;
      }
      const campaignId = row.campaignId !== undefined && row.campaignId !== null ? String(row.campaignId) : '';
      const name = typeof row.name === 'string' && row.name.trim() !== '' ? row.name.trim() : campaignId;
      if (campaignId !== '') {
        options.push({ campaignId, name });
      }
    }
    campaignOptions.value = options;
  } catch (error) {
    campaignOptions.value = [];
    handleAuthError(error);
  } finally {
    isLoadingCampaigns.value = false;
  }
};

const handleGenerateReportClick = async () => {
  if (isCampaignsReportMode.value === true) {
    await generateCampaignsReport();
    return;
  }
  await generateReport();
};

const generateCampaignsReport = async () => {
  isGeneratingReport.value = true;
  try {
    clearReportRecordingUrls();
    conversationAnalysisData.value = null;
    dashboardSummary.value = null;
    const startCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[0] : null;
    const endCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[1] : null;
    const startDate = startCandidate !== null ? new Date(startCandidate) : null;
    const endDate = endCandidate !== null ? new Date(endCandidate) : null;
    if (startDate === null || endDate === null || isNaN(startDate.getTime()) === true || isNaN(endDate.getTime()) === true) {
      toast.error(t('reports.toasts.failedToGenerateReport'));
      reportData.value = [];
      reportHeaders.value = [];
      searchFields.value = [];
      return;
    }
    if (startDate.getTime() > endDate.getTime()) {
      toast.error(t('reports.toasts.failedToGenerateReport'));
      reportData.value = [];
      reportHeaders.value = [];
      searchFields.value = [];
      return;
    }
    const selectedIds = Array.isArray(selectedCampaignIds.value) === true ? selectedCampaignIds.value : [];
    if (selectedIds.length === 0) {
      toast.error(t('reports.campaignsReport.selectCampaignsFirst', 'Select at least one campaign.'));
      return;
    }
    const endDateInclusive = new Date(endDate.getTime());
    endDateInclusive.setHours(23, 59, 59, 999);
    const campaignReports = [];
    for (let index = 0; index < selectedIds.length; index = index + 1) {
      const campaignId = String(selectedIds[index]);
      const option = getCampaignOptionById(campaignId);
      const campaignName = option !== null && typeof option.name === 'string' ? option.name : campaignId;
      const response = await axios.get(`${globalState.apiEndpoint}/api/campaigns/${encodeURIComponent(campaignId)}/stats`, {
        headers: getAuthHeaders()
      });
      const logs = response.data !== undefined && response.data !== null && Array.isArray(response.data.logs) === true ? response.data.logs : [];
      const filteredLogs = [];
      for (let logIndex = 0; logIndex < logs.length; logIndex = logIndex + 1) {
        const log = logs[logIndex];
        if (isCampaignLogInDateRange(log !== undefined && log !== null ? log.createdat : '', startDate, endDateInclusive) !== true) {
          continue;
        }
        filteredLogs.push(log);
      }
      campaignReports.push({
        campaignId,
        campaignName,
        logs: filteredLogs
      });
    }
    const reportType = typeof selectedCampaignReport.value === 'string' && selectedCampaignReport.value.trim().length > 0 ? selectedCampaignReport.value : 'performanceSummary';
    const headers = buildCampaignReportHeaders(reportType);
    const rows = buildCampaignReportRows(reportType, campaignReports);
    reportHeaders.value = headers;
    reportData.value = rows;
    searchFields.value = headers.map(header => header.value);
    reportFieldVisibility.value = {};
    toast.success(t('reports.toasts.reportGeneratedSuccess'));
  } catch (error) {
    console.error('Error generating campaigns report:', error);
    handleAuthError(error);
    toast.error(t('reports.toasts.failedToGenerateReport'));
    reportData.value = [];
    reportHeaders.value = [];
    searchFields.value = [];
  } finally {
    isGeneratingReport.value = false;
  }
};

const generateReport = async () => {
  isGeneratingReport.value = true;
  try {
    clearReportRecordingUrls();
    conversationAnalysisData.value = null;

    let startDate = null;
    let endDate = null;
    if (selectedReportAllowsDateRange.value === true) {
      const startCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[0] : null;
      const endCandidate = Array.isArray(dateRange.value) === true && dateRange.value.length === 2 ? dateRange.value[1] : null;
      startDate = startCandidate !== null ? new Date(startCandidate) : null;
      endDate = endCandidate !== null ? new Date(endCandidate) : null;
      if (startDate === null || endDate === null || isNaN(startDate.getTime()) === true || isNaN(endDate.getTime()) === true) {
        toast.error(t('reports.toasts.failedToGenerateReport'));
        reportData.value = [];
        reportHeaders.value = [];
        searchFields.value = [];
        return;
      }
      if (startDate.getTime() > endDate.getTime()) {
        toast.error(t('reports.toasts.failedToGenerateReport'));
        reportData.value = [];
        reportHeaders.value = [];
        searchFields.value = [];
        return;
      }
    }
    const safeAgentIds = reportSourceMode.value === 'agents' ? normalizeIdListFlexible(selectedAgents.value, 50, 128, ['agentid', 'agentId', 'id']) : [];
    const safeEntityIds = reportSourceMode.value === 'entities' ? selectedReportEntityIds.value : [];
    const safeEntityId = safeEntityIds.length > 0 ? safeEntityIds[0] : '';
    const safeTagIds = selectedReportSupportsTags.value === true ? normalizeIdListFlexible(selectedTagIds.value, 200, 64, ['tagid', 'tagId', 'id']) : [];
    const collectionRaw = selectedTagCollectionId.value;
    let safeTagCollectionId = null;
    if (collectionRaw !== '' && collectionRaw !== null && collectionRaw !== undefined) {
      const parsedCollectionId = parseInt(collectionRaw, 10);
      if (isNaN(parsedCollectionId) === false) {
        safeTagCollectionId = parsedCollectionId;
      }
    }
    const safeReportType = normalizeIdValue(selectedReport.value, 64);
    if ((safeAgentIds.length < 1 && safeEntityIds.length === 0) || safeReportType === '') {
      toast.error(t('reports.toasts.failedToGenerateReport'));
      reportData.value = [];
      reportHeaders.value = [];
      searchFields.value = [];
      return;
    }
    if (selectedReportSupportsAnalysisFilter.value === true && selectedAnalysisFilterKey.value !== '' && selectedAnalysisFilterValue.value === '') {
      toast.error(t('reports.toasts.failedToGenerateReport'));
      return;
    }
    const requestAnalysisFilterKey = selectedReportSupportsAnalysisFilter.value === true ? selectedAnalysisFilterKey.value : '';
    const requestAnalysisFilterValue = selectedReportSupportsAnalysisFilter.value === true ? selectedAnalysisFilterValue.value : '';
    const communicationChannelType = selectedReport.value === 'chatSessions' ? selectedChannelType.value : '';
    const subAgentConversationFilter = selectedReport.value === 'chatSessions' ? selectedSubAgentConversationFilter.value : '';

    if (isDashboardReport.value === true) {
      await fetchDashboardSummary();
      reportData.value = [];
      reportHeaders.value = [];
      searchFields.value = [];
      return;
    }

    let response = null;
    if (isCustomReportSelected.value === true) {
      const safeCustomReportId = normalizeIdValue(selectedCustomReportId.value, 128);
      if (safeCustomReportId === '') {
        toast.error(t('reports.toasts.failedToGenerateReport'));
        reportData.value = [];
        reportHeaders.value = [];
        searchFields.value = [];
        return;
      }
      response = await axios.post(`${globalState.apiEndpoint}/api/customReports/${encodeURIComponent(safeCustomReportId)}/run`, {
        agentIds: safeAgentIds,
        entityId: safeEntityId,
        entityIds: safeEntityIds,
        startDate: startDate !== null ? startDate.toISOString() : null,
        endDate: endDate !== null ? endDate.toISOString() : null,
        analysisFilterKey: requestAnalysisFilterKey,
        analysisFilterValue: requestAnalysisFilterValue
      }, {
        headers: getAuthHeaders()
      });
    } else {
      const reportRequestBody = {
        agentIds: safeAgentIds,
        entityId: safeEntityId,
        entityIds: safeEntityIds,
        reportType: safeReportType,
        startDate: startDate !== null ? startDate.toISOString() : null,
        endDate: endDate !== null ? endDate.toISOString() : null,
        tagIds: safeTagIds,
        tagCollectionId: safeTagCollectionId,
        communicationChannelType: communicationChannelType,
        subAgentConversationFilter: subAgentConversationFilter,
        analysisFilterKey: requestAnalysisFilterKey,
        analysisFilterValue: requestAnalysisFilterValue
      };
      try {
        response = { data: await generateReportOverSocket(reportRequestBody) };
      } catch (socketError) {
        if (socketError !== null && socketError !== undefined && socketError.requestSent === true) {
          throw socketError;
        }
        response = await axios.post(`${globalState.apiEndpoint}/api/reports/generate`, reportRequestBody, {
          headers: getAuthHeaders(),
          timeout: 0
        });
      }
    }

    if (response.data !== undefined) {
      if (response.data !== null) {
        if (Array.isArray(response.data.data) === true) {
          if (Array.isArray(response.data.headers) === true) {
            const normalizedHeaders = normalizeReportHeadersForSource(response.data.headers);
            const normalizedRows = normalizeReportRowsForSource(response.data.data, normalizedHeaders);
            reportData.value = normalizedRows;
            reportHeaders.value = normalizedHeaders;
            searchFields.value = reportHeaders.value.map(header => header.value);
            if (safeReportType === 'chatSessions') {
              hydrateReportRecordingAudioUrls(normalizedRows).catch(() => {});
            }
            toast.success(t('reports.toasts.reportGeneratedSuccess'));
          } else {
            console.error('Unexpected report data format received:', response.data);
            reportData.value = [];
            reportHeaders.value = [];
            searchFields.value = [];
            toast.error(t('reports.toasts.failedToProcessReport'));
          }
        } else {
          console.error('Unexpected report data format received:', response.data);
          reportData.value = [];
          reportHeaders.value = [];
          searchFields.value = [];
          toast.error(t('reports.toasts.failedToProcessReport'));
        }
      } else {
        console.error('Unexpected report data format received:', response.data);
        reportData.value = [];
        reportHeaders.value = [];
        searchFields.value = [];
        toast.error(t('reports.toasts.failedToProcessReport'));
      }
    } else {
      console.error('Unexpected report data format received:', response.data);
      reportData.value = [];
      reportHeaders.value = [];
      searchFields.value = [];
      toast.error(t('reports.toasts.failedToProcessReport'));
    }

  } catch (error) {
    toast.error(t('reports.toasts.failedToGenerateReport'));
    console.error('Error generating report:', error);
    handleAuthError(error);
    reportData.value = [];
    reportHeaders.value = [];
    searchFields.value = [];
  } finally {
    isGeneratingReport.value = false;
  }
}


function canOpenConversationModalFromRow(item) {
  if (item === null || item === undefined || typeof item !== 'object') {
    return false;
  }
  const safeSessionId = normalizeIdValue(item.sessionid, 180);
  const safeConversationId = normalizeIdValue(item.conversationid, 180);
  return safeSessionId !== '' && safeConversationId !== '';
}

const loadSelectedConversationRecording = async (sessionId, recordingData, fallbackRecordingValue) => {
  if (shouldShowRecordingColumn.value !== true) {
    return;
  }
  const safeSessionId = normalizeIdValue(sessionId, 180);
  const hasRecordingBlob = typeof recordingData?.recordingBlobPath === 'string' && recordingData.recordingBlobPath.trim() !== '';
  const hasRecordingUrl = typeof recordingData?.recordingUrl === 'string' && recordingData.recordingUrl.trim() !== '';
  const hasFallbackRecording = typeof fallbackRecordingValue === 'string' && fallbackRecordingValue.trim() !== '';
  if (safeSessionId === '') {
    return;
  }
  if (hasRecordingBlob !== true && hasRecordingUrl !== true && hasFallbackRecording !== true) {
    return;
  }
  selectedConversationRecordingLoading.value = true;
  selectedConversationRecordingError.value = '';
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/reports/conversation/${encodeURIComponent(safeSessionId)}/recording`, {
      headers: getBlobAuthHeaders(),
      responseType: 'blob'
    });
    const contentType = typeof response.headers?.['content-type'] === 'string' && response.headers['content-type'].trim() !== '' ? response.headers['content-type'].trim() : 'audio/wav';
    const blob = new Blob([response.data], { type: contentType });
    if (selectedConversationRecordingUrl.value !== '') {
      try { URL.revokeObjectURL(selectedConversationRecordingUrl.value); } catch (error) {}
    }
    selectedConversationRecordingUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    selectedConversationRecordingError.value = 'Recording is not available for playback.';
  } finally {
    selectedConversationRecordingLoading.value = false;
  }
};

const openConversationModal = async (item) => {
  console.log('Attempting to open conversation modal for item:', item);
  if (item?.sessionid == null || item?.conversationid == null) {
    toast.error(t('reports.toasts.invalidSessionData'));
    return;
  }
  const safeSessionId = normalizeIdValue(item.sessionid, 180);
  const safeConversationId = normalizeIdValue(item.conversationid, 180);
  if (safeSessionId === '' || safeConversationId === '') {
    toast.error(t('reports.toasts.invalidSessionData'));
    return;
  }
  selectedSessionId.value = safeSessionId;
  selectedConversationAgentId.value = typeof item.agentid === 'string' && item.agentid.trim() !== '' ? item.agentid.trim() : null;
  selectedConversationOpen.value = item.conversationopen === true;
  selectedConversationChannelType.value = typeof item.communicationchanneltype === 'string' ? item.communicationchanneltype.trim().toLowerCase() : '';
  isConversationModalOpen.value = true;
  isLoadingConversation.value = true;
  conversationHistory.value = [];
  clearSelectedConversationRecording();
  conversationAnalysisData.value = null; 
  conversationTags.value = [];

  try {
    const isEntityConversation = reportSourceMode.value === 'entities';
    const conversationUrl = isEntityConversation === true
      ? `${globalState.apiEndpoint}/api/reports/entityConversation/${encodeURIComponent(safeConversationId)}`
      : `${globalState.apiEndpoint}/api/reports/conversation/${encodeURIComponent(safeSessionId)}`;
    const conversationResponse = await axios.get(conversationUrl, {
      headers: getAuthHeaders()
    });
    const conversationPayload = conversationResponse !== null && conversationResponse !== undefined && typeof conversationResponse.data === 'object' && conversationResponse.data !== null && Array.isArray(conversationResponse.data) !== true
      ? conversationResponse.data
      : {};
    if (conversationPayload.isAgentsToAgentsSession === true) {
      selectedConversationChannelType.value = 'agentstoagents';
    }
    const rawHistory = Array.isArray(conversationPayload.history) === true
      ? conversationPayload.history
      : (Array.isArray(conversationResponse.data) === true ? conversationResponse.data : []);
    if (shouldShowConversationTranscript.value === true) {
      conversationHistory.value = rawHistory.map((turn) => ({
        ...turn,
        toolExecutions: normalizeToolExecutionEntries(turn?.toolExecutions)
      }));
    }
    conversationAnalysisData.value = conversationPayload.analysis !== undefined ? conversationPayload.analysis : null;
    if (Array.isArray(conversationAnalysisData.value?.identifiedtags) === true && conversationAnalysisData.value.identifiedtags.length > 0) {
      const tagIds = normalizeIdList(conversationAnalysisData.value.identifiedtags, 200, 64);
      try {
        const tagsResponse = await axios.get(
          `${globalState.apiEndpoint}/api/tags/`,
          { params: { ids: tagIds.join(',') }, headers: getAuthHeaders() }
        );
        conversationTags.value = Array.isArray(tagsResponse.data) === true ? tagsResponse.data.map(tag => sanitizeTextValue(tag.tagName, 80)) : [];
      } catch (tagsError) {
        console.error('Failed to load tag names:', tagsError);
        toast.warning(t('reports.toasts.failedToLoadTagNames'));
      }
    } else {
      conversationTags.value = [];
    }
    if (isEntityConversation !== true) {
      await loadSelectedConversationRecording(safeSessionId, conversationPayload.recording, item.recordingurl);
    }

  } catch (error) {
    toast.error(t('reports.toasts.failedToLoadHistory'));
    console.error('Error loading conversation:', error);
    handleAuthError(error);
  } finally {
    isLoadingConversation.value = false;
  }
}

const isReportSessionClosing = (sessionId) => {
  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    return false;
  }
  return closingReportSessionIds.value.includes(sessionId.trim()) === true;
};

const closeConversationFromReport = async (sessionId, agentId) => {
  const safeSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
  const safeAgentId = typeof agentId === 'string' ? agentId.trim() : '';
  if (safeSessionId === '') {
    toast.error(t('reports.toasts.invalidSessionData'));
    return false;
  }
  if (isReportSessionClosing(safeSessionId) === true) {
    return false;
  }
  closingReportSessionIds.value = [...closingReportSessionIds.value, safeSessionId];
  try {
    await axios.post(
      `${globalState.apiEndpoint}/api/reports/closeConversation`,
      {
        sessionId: safeSessionId,
        agentId: safeAgentId === '' ? null : safeAgentId
      },
      { headers: getAuthHeaders() }
    );
    reportData.value = reportData.value.map((row) => {
      if (row.sessionid === safeSessionId) {
        return { ...row, conversationopen: false };
      }
      return row;
    });
    if (selectedSessionId.value === safeSessionId) {
      selectedConversationOpen.value = false;
    }
    toast.success('Conversation closed successfully.');
    return true;
  } catch (error) {
    handleAuthError(error);
    return false;
  } finally {
    closingReportSessionIds.value = closingReportSessionIds.value.filter(id => id !== safeSessionId);
  }
};

const closeSelectedConversationFromModal = async () => {
  const safeSessionId = typeof selectedSessionId.value === 'string' ? selectedSessionId.value.trim() : '';
  if (safeSessionId === '') {
    toast.error(t('reports.toasts.invalidSessionData'));
    return;
  }
  await closeConversationFromReport(safeSessionId, selectedConversationAgentId.value);
};

const closeConversationModal = () => {
  isConversationModalOpen.value = false;
  selectedSessionId.value = null;
  selectedConversationAgentId.value = null;
  selectedConversationOpen.value = false;
  selectedConversationChannelType.value = '';
  conversationHistory.value = [];
  clearSelectedConversationRecording();
  conversationAnalysisData.value = null; 
  conversationTags.value = [];
  isSourcesModalOpen.value = false;
  sourcesSessionId.value = '';
  sourcesMessageId.value = '';
  isToolExecutionsModalOpen.value = false;
  selectedToolExecutions.value = [];
};

const handleRowClick = (item) => {
  if (isConversationDrilldownReport.value !== true) {
    return;
  }
  if (canOpenConversationModalFromRow(item) !== true) {
    return;
  }
  openConversationModal(item);
};

const openSourcesModalForMessage = (message) => {
  const sessionIdValue = typeof selectedSessionId.value === 'string' ? selectedSessionId.value.trim() : '';
  const messageIdValue = message?.messageid !== undefined && message?.messageid !== null ? String(message.messageid).trim() : '';
  sourcesSessionId.value = sessionIdValue;
  sourcesMessageId.value = messageIdValue;
  isSourcesModalOpen.value = true;
};

const closeSourcesModal = () => {
  isSourcesModalOpen.value = false;
  sourcesSessionId.value = '';
  sourcesMessageId.value = '';
};

const openToolExecutionsModal = (message) => {
  selectedToolExecutions.value = getDisplayableToolExecutions(message);
  isToolExecutionsModalOpen.value = true;
};

const closeToolExecutionsModal = () => {
  isToolExecutionsModalOpen.value = false;
  selectedToolExecutions.value = [];
};

function getTextDirection(text) {
  const raw = typeof text === 'string' ? text : (text === null || text === undefined ? '' : String(text));
  if (raw.length < 1) {
    return 'ltr'; 
  }
  const firstChar = raw.charCodeAt(0);
  
  if ((firstChar >= 0x0590 && firstChar <= 0x05FF) || (firstChar >= 0x0600 && firstChar <= 0x06FF)) {
    return 'rtl';
  } else {
    return 'ltr';
  }
}


function escapeHtml(unsafe) {
  if (unsafe === null || unsafe === undefined) return '';
  const raw = typeof unsafe === 'string' ? unsafe : String(unsafe);
  return raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}


const exportToExcel = () => {
  if (reportData.value == undefined) {
    toast.info(t('reports.toasts.noDataToExport'));
    return;
  }

  try {
  
    const headers = visibleReportHeaders.value
      .filter(h => h.value !== 'options')
      .map(h => h.text);
    

    const dataToExport = reportData.value.map(item => {
      return visibleReportHeaders.value
        .filter(h => h.value !== 'options')
        .map(header => {
          let value = item[header.value];
  
          if (header.value === 'sessionstartdate' || header.value === 'createdat' || header.value === 'first_interaction' || header.value === 'last_interaction' || header.value === 'date') { 
            value = formatDateHardcoded(value);
          }
          if (Array.isArray(value) === true) {
            value = value.join(', ');
          }
 
          return value; 
        });
    });


    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataToExport]);
    

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ReportData');


    const reportName = sanitizeFileNameSegment(isDefinedAndNotEmpty(selectedReport.value) != false ? selectedReport.value : 'report', 40);
    const dateStr = new Date().toISOString().slice(0, 10);
    const safeDateStr = sanitizeFileNameSegment(dateStr, 16).replace(/[-_]/g, '');
    const fileNameBase = sanitizeFileNameSegment(`${reportName}${safeDateStr}`, fileNameMaxLength).replace(/[-_]/g, '');
    const fileName = `${fileNameBase !== '' ? fileNameBase : 'report'}.xlsx`;

   
    XLSX.writeFile(wb, fileName);
    toast.success(t('reports.toasts.exportExcelSuccess'));

  } catch (error) {
    console.error("Error exporting to Excel:", error);
    toast.error(t('reports.toasts.exportExcelError'));
  }
};


const exportConversationToPdf = async () => { 
  console.log("Attempting PDF export with html2canvas...");
  if (conversationHistory.value === undefined || conversationHistory.value.length === 0 || conversationContainerRef.value === undefined || conversationContainerRef.value === null) {
    toast.info(t('reports.toasts.noConversationDataToExport'));
    console.log("Export aborted: No conversation data or container.");
    return;
  }
  console.log("Conversation history length:", conversationHistory.value.length);
  const elementToCapture = conversationContainerRef.value;
  const originalBgColor = elementToCapture.style.backgroundColor; 
  elementToCapture.style.backgroundColor = '#FFFFFF'; 

  try {
    const canvas = await html2canvas(elementToCapture, {
        scale: 2, 
        useCORS: true, 
        logging: true, 
        scrollX: 0, 
        scrollY: -window.scrollY 
    });
    const imgData = canvas.toDataURL('image/png');
    
    const doc = new jsPDF();
    const imgProps = doc.getImageProperties(imgData);
    const pdfWidth = doc.internal.pageSize.getWidth() - 20; 
    const pdfHeight = doc.internal.pageSize.getHeight() - 20; 
    const imgWidth = imgProps.width;
    const imgHeight = imgProps.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

    const scaledWidth = imgWidth * ratio;
    const scaledHeight = imgHeight * ratio;

    
    let currentPosition = 10;
    const totalImageHeightInPdf = imgHeight * (pdfWidth / imgWidth); 
    let remainingImageHeight = imgHeight;
    const sourceCanvas = canvas;
    
    doc.setFont('Heebo-VariableFont_wght', 'normal');
    doc.setFontSize(16);
    const sessionIdForTitle = sanitizeTextValue(selectedSessionId.value, 180);
    doc.text(`Conversation: ${sessionIdForTitle}`, 10, currentPosition);
    currentPosition += 10; 
    
    let sourceY = 0;
    
    while (remainingImageHeight > 0) {
        const availableHeightOnPage = pdfHeight - currentPosition;
        const heightToDrawOnPage = Math.min(remainingImageHeight * (pdfWidth / imgWidth), availableHeightOnPage);
        const sourceHeight = heightToDrawOnPage / (pdfWidth / imgWidth);
        
        
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = imgWidth;
        tempCanvas.height = sourceHeight;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.drawImage(sourceCanvas, 0, sourceY, imgWidth, sourceHeight, 0, 0, imgWidth, sourceHeight);
        const pageImgData = tempCanvas.toDataURL('image/png');

        doc.addImage(pageImgData, 'PNG', 10, currentPosition, pdfWidth, heightToDrawOnPage);
        
        remainingImageHeight -= sourceHeight;
        sourceY += sourceHeight;
        
        if (remainingImageHeight > 0) {
            doc.addPage();
            currentPosition = 10; 
        } else {
             currentPosition += heightToDrawOnPage;
        }
    }


    console.log("PDF content added with html2canvas image.");

    const sessionId = sanitizeFileNameSegment(isDefinedAndNotEmpty(selectedSessionId.value) != false ? selectedSessionId.value : 'conversation', 60);
    const dateStr = new Date().toISOString().slice(0, 10);
    const safeDateStr = sanitizeFileNameSegment(dateStr, 16).replace(/[-_]/g, '');
    const fileNameBase = sanitizeFileNameSegment(`conversation${sessionId}${safeDateStr}`, fileNameMaxLength).replace(/[-_]/g, '');
    const fileName = `${fileNameBase !== '' ? fileNameBase : 'conversation'}.pdf`;
    console.log(`Attempting to download PDF as: ${fileName}`);

    doc.save(fileName);
    console.log("jsPDF doc.save() called.");

    toast.success(t('reports.toasts.exportPdfSuccess'));

  } catch (error) {
    console.error("Error exporting conversation to PDF with html2canvas:", error);
    toast.error(t('reports.toasts.exportPdfError'));
  } finally {
      elementToCapture.style.backgroundColor = originalBgColor; 
  }
};

const exportConversationToTxt = () => {
  console.log("Attempting TXT export...");
  if (conversationHistory.value === undefined || conversationHistory.value.length === 0) {
    toast.info(t('reports.toasts.noConversationDataToExport'));
    console.log("Export aborted: No conversation data.");
    return;
  }
  console.log("Conversation history length:", conversationHistory.value.length);

  try {
    const sessionIdTitle = sanitizeTextValue(selectedSessionId.value, 180);
    let textContent = `Conversation: ${sessionIdTitle}\n\n`;

    conversationHistory.value.forEach(turn => {
      if (turn.userquery != undefined) {
        const timestamp = formatDateTimeForViewer(turn.createdat);
        textContent += `User (${timestamp}):\n${extractConversationText(turn.userquery)}\n\n`;
      }
      if (turn.response != undefined) {
        const timestamp = formatDateTimeForViewer(turn.createdat); 
        const agentResponseText = extractConversationText(turn.response).replace(/<[^>]*>/g, ''); 
        textContent += `Agent (${timestamp}):\n${agentResponseText}\n\n`;
      }
    });

    const sessionId = sanitizeFileNameSegment(isDefinedAndNotEmpty(selectedSessionId.value) != false ? selectedSessionId.value : 'conversation', 60);
    const dateStr = new Date().toISOString().slice(0, 10);
    const safeDateStr = sanitizeFileNameSegment(dateStr, 16).replace(/[-_]/g, '');
    const fileNameBase = sanitizeFileNameSegment(`conversation${sessionId}${safeDateStr}`, fileNameMaxLength).replace(/[-_]/g, '');
    const fileName = `${fileNameBase !== '' ? fileNameBase : 'conversation'}.txt`;

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    toast.success(t('reports.toasts.exportTxtSuccess'));

  } catch (error) {
    console.error("Error exporting conversation to TXT:", error);
    toast.error(t('reports.toasts.exportTxtError'));
  }
};

function loadStyle(cssString) {

const style = document.createElement('style');


style.type = 'text/css';


if (style.styleSheet != null) {
  style.styleSheet.cssText = cssString;
} else {

  style.appendChild(document.createTextNode(cssString));
}


document.head.appendChild(style);
}

const getSentimentBadgeClass = (sentiment) => {
  if (sentiment === undefined || sentiment === null) return 'badge-ghost';
  const sentimentLower = sentiment.toLowerCase();
  if (sentimentLower.includes('very positive')) return 'badge-success';
  if (sentimentLower.includes('positive')) return 'badge-success badge-outline';
  if (sentimentLower.includes('very negative')) return 'badge-error';
  if (sentimentLower.includes('negative')) return 'badge-error badge-outline';
  if (sentimentLower.includes('mixed')) return 'badge-warning';
  return 'badge-neutral';
};


  
 

</script>

<style scoped>
.reports-container {
  max-width: 1600px;
  margin: 0 auto;
}

.enterprise-table {
  @apply w-full;
  font-family: "Inter", sans-serif;
  table-layout: fixed;
  max-width: 100%;
}

.enterprise-table :deep(th) {
  @apply bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium py-3 px-4 sticky top-0 z-10;
  white-space: pre-line;
  overflow-wrap: break-word;
  word-break: normal;
}

.enterprise-table :deep(td) {
  @apply py-3 px-4 border-t border-gray-200 dark:border-gray-700;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
}

.compact-id-cell {
  display: inline-block;
  max-width: 4.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  direction: ltr;
}

.latency-details {
  position: relative;
  display: inline-block;
  margin-top: 0.5rem;
  direction: ltr;
  text-align: left;
}

.latency-summary {
  list-style: none;
  cursor: pointer;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(255, 255, 255, 0.86);
  color: #374151;
  font-size: 11px;
  padding: 0.1rem 0.55rem;
}

.latency-summary::-webkit-details-marker {
  display: none;
}

.latency-popover {
  position: absolute;
  z-index: 40;
  left: 0;
  right: auto;
  top: 1.65rem;
  width: 310px;
  border-radius: 0.5rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18);
  padding: 0.65rem;
  direction: ltr;
  text-align: left;
}

.latency-stage {
  display: flex;
  align-items: center;
  margin-bottom: 0.42rem;
}

.latency-stage-label {
  width: 105px;
  font-size: 11px;
  color: #6b7280;
}

.latency-stage-bar-wrap {
  flex: 1;
  height: 10px;
  background: #eef2f7;
  border-radius: 999px;
  overflow: hidden;
  margin-left: 0.45rem;
  margin-right: 0.45rem;
}

.latency-stage-bar {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.latency-stage-value {
  width: 48px;
  text-align: right;
  font-size: 11px;
  color: #6b7280;
}

.latency-total {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  font-size: 12px;
  color: #4b5563;
}

.latency-audio-progress {
  margin-top: 0.35rem;
  font-size: 11px;
  color: #6b7280;
}

.latency-action-button {
  margin-left: 0.5rem;
}

.report-table-scroll-scorecard {
  overflow-x: auto;
}

.report-table-scroll-scorecard :deep(.vue3-easy-data-table),
.report-table-scroll-scorecard :deep(.vue3-easy-data-table__main) {
  max-width: none;
  overflow-x: visible;
}

.enterprise-scorecard-table {
  table-layout: auto;
  width: max-content;
  min-width: 100%;
}

.enterprise-scorecard-table :deep(th) {
  white-space: pre-line;
  overflow-wrap: normal;
  word-break: normal;
}

.enterprise-scorecard-table :deep(td) {
  white-space: nowrap;
  overflow-wrap: normal;
  word-break: keep-all;
}

.enterprise-scorecard-table :deep(td:nth-child(3)),
.enterprise-scorecard-table :deep(td:last-child) {
  white-space: normal;
  overflow-wrap: break-word;
  min-width: 12rem;
  max-width: 24rem;
}

.enterprise-scorecard-table :deep(td:nth-child(6)) {
  white-space: normal;
}

.enterprise-scorecard-table :deep(.report-scorecard-cell) {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  max-width: none;
  vertical-align: middle;
}

.enterprise-scorecard-table :deep(.report-scorecard-cell > span:first-child) {
  white-space: nowrap;
}

.enterprise-table :deep(tr:hover) {
  @apply bg-gray-50 dark:bg-gray-700;
}

.report-table-shell {
  width: 100%;
  flex: 1;
  overflow: hidden;
}

.report-table-scroll {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: auto;
}

.report-table-scroll :deep(.vue3-easy-data-table),
.report-table-scroll :deep(.vue3-easy-data-table__main),
.report-table-scroll :deep(.vue3-easy-data-table__body),
.report-table-scroll :deep(.vue3-easy-data-table__footer) {
  max-width: none;
  overflow-x: visible;
}

.report-table-shell-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 9500;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  max-height: 100dvh;
  background: #ffffff;
  padding: 1rem;
  overflow: hidden;
}

.dark .report-table-shell-fullscreen {
  background: #111827;
}

.report-table-fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding-bottom: 1rem;
}

.report-table-scroll-fullscreen {
  flex: 1 1 auto;
  min-height: 0;
  height: calc(100dvh - 5rem);
  display: flex;
  flex-direction: column;
}

.report-table-scroll-fullscreen :deep(.theme-only-wrapper),
.report-table-scroll-fullscreen :deep(.theme-modern),
.report-table-scroll-fullscreen :deep(.theme-modern-inverted) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.report-table-scroll-fullscreen :deep(.vue3-easy-data-table) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.report-table-scroll-fullscreen :deep(.vue3-easy-data-table__main) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

:global(body.report-table-fullscreen-active) {
  overflow: hidden;
}

.query-text, .ai-response {
  @apply max-w-md overflow-hidden text-ellipsis whitespace-normal;
  direction: rtl;
}

.loading-spinner {
  @apply w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin;
}

:root {
  --dp-primary-color: #4f46e5;
  --dp-font-family: "Inter", sans-serif;
}



.select-bordered {
  @apply border-gray-300 dark:border-gray-600;
}

.clickable-row {
  cursor: pointer;
}

.chat-header {
  box-shadow: none;
  background-color: transparent;
}
.chat {
  flex-direction: column;
  display: flex;
}

.chat-bubble {
  max-width: 80%; 
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.chat-start .chat-bubble {
  background: #cadff4; 
  color: #1e293b; 
  border-bottom-right-radius: 0.25rem;
}

.chat-end .chat-bubble {
  background: #2563eb;
  color: white; 
  border-bottom-left-radius: 0.25rem;
}

.chat-bubble :deep(p) { 
  margin: 0;
  line-height: 1.5;
}

.chat-bubble :deep(a) {
  color: inherit; 
  text-decoration: underline;
}
.chat-bubble::before{display:none;}
.chat-bubble :deep(pre), .chat-bubble :deep(code) {
  background-color: rgba(0,0,0,0.1);
  padding: 0.1em 0.3em;
  border-radius: 4px;
  font-family: monospace;
}

.search-fields-grid {
  margin: -0.5rem;
}
.search-fields-grid > * {
  margin: 0.5rem;
}

.report-source-switch-row {
  width: 100%;
}

.report-source-switch {
  max-width: 100%;
}

.report-source-switch-button {
  display: inline-flex;
  direction: rtl;
  align-items: center;
  border-radius: 0.45rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.report-source-switch-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.report-source-switch-icon {
  display: block;
}

.report-source-switch-button + .report-source-switch-button {
  margin-inline-start: 0.25rem;
}

.report-source-switch-button-active {
  background-color: #4f46e5;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.15);
}

.report-date-range-control {
  max-width: 16rem;
}

.report-date-range-control :deep(.dp__main),
.report-date-range-control :deep(.dp__input_wrap),
.report-date-range-control :deep(input) {
  width: 100%;
}

@media (max-width: 767px) {
  .report-date-range-control {
    min-width: 0;
  }
}

.table-buttons-row {
  margin: -0.5rem;
}
.table-buttons-row > * {
  margin: 0.5rem;
}

.report-field-picker {
  position: relative;
}

.report-field-picker-menu {
  position: absolute;
  z-index: 40;
  min-width: 16rem;
  max-height: 22rem;
  overflow-y: auto;
  margin-top: 0.5rem;
}

.report-field-picker-item {
  display: flex;
  align-items: center;
  padding: 0.35rem 0;
}

.report-field-picker-item > input {
  margin-right: 0;
  margin-inline-end: 0.45rem;
}

.report-result-with-explanation {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  vertical-align: middle;
}

.report-result-with-explanation > * {
  margin-inline-end: 0.25rem;
}

.report-result-explanation-tooltip :deep(.btn) {
  width: 1.55rem;
  height: 1.55rem;
  min-height: 1.55rem;
}

.report-result-explanation-tooltip :deep(svg) {
  width: 1rem;
  height: 1rem;
}

.report-result-explanation-tooltip :deep(.dropdown) {
  direction: ltr;
}

.report-result-explanation-tooltip :deep(.dropdown-content) {
  z-index: 60;
  left: 0;
  right: auto;
  direction: ltr;
  text-align: left;
  max-width: 24rem;
}

#conversation-analysis-details-div .report-result-explanation-tooltip :deep(.dropdown-content) {
  max-width: min(24rem, 100%);
}

.report-section-explanation-row {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #64748b;
  font-size: 0.75rem;
}

.report-section-explanation-row > * {
  margin-inline-end: 0.25rem;
}

.report-custom-analysis-result {
  align-items: flex-start;
}

.report-custom-analysis-result pre {
  margin-bottom: 0;
}

.usage-dashboard-grid {
  margin: -0.5rem;
}

.usage-dashboard-cell {
  padding: 0.5rem;
}

.usage-dashboard-card {
  height: 100%;
}

.usage-chart-summary {
  margin-left: 0;
  margin-inline-start: 1rem;
}

.usage-time-text {
  direction: ltr;
  unicode-bidi: isolate;
  display: inline-block;
}

.usage-legend-row {
  display: flex;
  flex-wrap: wrap;
  margin: -0.25rem;
}

.usage-legend-item {
  display: inline-flex;
  align-items: center;
  margin: 0.25rem;
}

.usage-legend-swatch {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 9999px;
  display: inline-block;
  margin-right: 0;
  margin-inline-end: 0.4rem;
}

.usage-legend-swatch-chat {
  background-color: #4f46e5;
}

.usage-legend-swatch-call {
  background-color: #10b981;
}

.usage-hour-chart {
  display: flex;
  align-items: flex-end;
  min-height: 17rem;
  direction: ltr;
  padding: 0 0.25rem 0.5rem 0.25rem;
  overflow-x: auto;
}

.usage-hour-chart-item {
  flex: 1 1 0%;
  min-width: 2.35rem;
  padding: 0 0.22rem;
}

.usage-hour-chart-value {
  text-align: center;
  font-size: 0.7rem;
  color: #6b7280;
  min-height: 1rem;
  margin-bottom: 0.35rem;
}

.usage-hour-chart-column-shell {
  height: 13.5rem;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.usage-hour-chart-column {
  width: 100%;
  min-height: 0.35rem;
  border-radius: 0.65rem 0.65rem 0 0;
  background-color: #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
}

.usage-hour-chart-stack {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
}

.usage-hour-chart-segment {
  width: 100%;
}

.usage-hour-chart-segment-chat {
  background-color: #4f46e5;
}

.usage-hour-chart-segment-call {
  background-color: #10b981;
}

.usage-hour-chart-label {
  text-align: center;
  font-size: 0.62rem;
  color: #6b7280;
  margin-top: 0.7rem;
  direction: ltr;
  unicode-bidi: isolate;
  white-space: nowrap;
  transform: rotate(-45deg);
  transform-origin: top center;
}

.usage-ranked-row {
  display: flex;
  align-items: center;
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
}

.usage-ranked-row:last-child {
  border-bottom: 0;
}

.usage-ranked-index {
  width: 1.9rem;
  height: 1.9rem;
  border-radius: 9999px;
  background-color: rgba(79, 70, 229, 0.12);
  color: #4338ca;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
  margin-right: 0;
  margin-inline-end: 0.75rem;
  flex-shrink: 0;
}

.usage-ranked-total {
  margin-left: 0;
  margin-inline-start: 0.75rem;
}

.usage-mix-bar-chat {
  background-color: #4f46e5;
}

.usage-mix-bar-call {
  background-color: #10b981;
}

.clicking-legend-swatch-unique {
  background-color: #4f46e5;
}

.clicking-legend-swatch-repeat {
  background-color: #10b981;
}

.clicking-hour-chart-segment-unique {
  background-color: #4f46e5;
}

.clicking-hour-chart-segment-repeat {
  background-color: #10b981;
}

.clicking-return-chart {
  direction: ltr;
  width: 100%;
}

.clicking-return-chart-svg {
  width: 100%;
  height: 16rem;
  display: block;
}

.clicking-return-chart-axis {
  stroke: #d1d5db;
  stroke-width: 0.7;
}

.clicking-return-chart-grid {
  stroke: #e5e7eb;
  stroke-width: 0.5;
  stroke-dasharray: 2 2;
}

.clicking-return-chart-line {
  fill: none;
  stroke: #10b981;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.clicking-return-chart-dot {
  fill: #10b981;
}

.clicking-return-chart-labels {
  display: flex;
  justify-content: space-between;
  direction: ltr;
  color: #6b7280;
  font-size: 0.7rem;
}

.clicking-behavior-bar-new {
  background-color: #4f46e5;
}

.clicking-behavior-bar-returning {
  background-color: #10b981;
}

.clicking-behavior-bar-repeat {
  background-color: #f59e0b;
}

.clicking-behavior-bar-sessions {
  background-color: #ec4899;
}

.report-type-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  border-radius: 0.6rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  min-height: 44px;
}

.report-type-dropdown {
  position: absolute;
  z-index: 50;
  width: 100%;
  margin-top: 0.4rem;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 18px 25px -8px rgba(15,23,42,0.18), 0 8px 12px -6px rgba(15,23,42,0.12);
  transform-origin: top;
  overflow: hidden;
  max-height: min(32rem, calc(100vh - 10rem));
  display: flex;
  flex-direction: column;
}

.report-type-dropdown-search {
  flex: 0 0 auto;
}

.report-type-dropdown-list {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.report-type-option {
  width: 100%;
  display: flex;
  align-items: flex-start;
  text-align: left;
  padding: 0.65rem 0.9rem;
  border-bottom: 1px solid #eef2f7;
  background: transparent;
}

.report-type-option:last-child {
  border-bottom: none;
}

.report-type-option:hover {
  background: #f3f6ff;
}

.report-type-option-skeleton {
  cursor: default;
}

.report-type-icon-skeleton {
  background: #e5e7eb;
}

.report-type-skeleton-line {
  width: 100%;
  border-radius: 9999px;
  background: #e5e7eb;
  animation: pulse 1.6s ease-in-out infinite;
}

.report-type-skeleton-line-title {
  height: 0.8rem;
}

.report-type-skeleton-line-description {
  height: 0.65rem;
  margin-top: 0.5rem;
  width: 75%;
}

.report-type-icon {
  margin-right: 0.6rem;
  margin-top: 0.2rem;
  color: #4b5563;
  background: #eef2ff;
  border-radius: 0.45rem;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.report-type-text {
  text-align: left;
}

:deep(.dark) .report-type-dropdown {
  border-color: #374151;
  background: #1f2937;
  box-shadow: 0 18px 25px -8px rgba(0,0,0,0.35), 0 8px 12px -6px rgba(0,0,0,0.2);
}

:deep(.dark) .report-type-option {
  border-bottom-color: #374151;
}

:deep(.dark) .report-type-option:hover {
  background: #111827;
}

:deep(.dark) .report-type-icon-skeleton,
:deep(.dark) .report-type-skeleton-line {
  background: #374151;
}

:dir(rtl) .report-type-icon {
  margin-right: 0;
  margin-left: 0.5rem;
}

:dir(rtl) .report-type-text {
  text-align: right;
}

.report-dropdown-enter-active,
.report-dropdown-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.report-dropdown-enter-from,
.report-dropdown-leave-to {
  opacity: 0;
  transform: scale(0.98) translateY(-6px);
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-shell .bg-base-300 {
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.16) 0%, rgba(148, 163, 184, 0.32) 50%, rgba(148, 163, 184, 0.16) 100%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s linear infinite;
}

.report-dropdown-enter-to,
.report-dropdown-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

@media (max-height: 820px) {
  .report-type-dropdown {
    max-height: calc(100vh - 8rem);
  }
}

</style> 